import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'util/Api'
import {
  AutoComplete,
  Button,
  Checkbox,
  DatePicker,
  Icon,
  Input,
  InputNumber,
  Modal,
  Spin,
  Tooltip,
  message
} from 'antd'
import { connect } from 'react-redux'
import moment from 'moment'
import AuditVoucherSearch from '../../components/AuditVoucherSearch'
import {
  getAssetsByAuditVoucher,
  fetchFilter,
  getPvByVoucher,
  getAssetsForReconciliation,
  saveReconciliationData,
  processReconciliationData,
  exportSheets
} from '../../appRedux/actions'
import { Link } from 'react-router-dom'
import AstRecord from '../Reconciliation/ast/AstRecord'
import PhotoThumb from '../Reconciliation/ast/PhotoThumb'
import PhotoGalleryModal from '../Reconciliation/ast/PhotoGalleryModal'
import PhotoCompare from './PhotoCompare'
import PvLinkPicker from './PvLinkPicker'
import JevCheck from './JevCheck'
import AiLinkPanel, { AiTag } from './AiLinkPanel'
import AssetThumb from './AssetThumb'
import DuplicateAutoModal from './DuplicateAutoModal'
import { planDuplicates } from './duplicateAuto'
import NewAssetDetails, { isReady, missingFor, REQUIRED, DETAIL_FIELDS, scanValue, readLastOwner as lastNewAssetOwner } from './NewAssetDetails'
import { buildAuditReport } from './auditReport'
import { buildReconSummary, buildAnnexure, astBridgeLines, pvBridgeLines } from './reconSummary'
import { unprintableToast } from '../../util/pdfText'
import LatestImage from '../Asset/latest-image'
import {
  ACT, AST_ACTIONS, PV_ACTIONS, statusMeta, recommended,
  locName, clientName, rkey, ownerOptions,
  AST_STATUS_ORDER, NO_STATUS, groupOf, metaForGroup
} from './helpers'
import './style.css'

const { Option } = AutoComplete
const LIST_CAP = 400
// The audit scope a voucher can carry; null = not set on the voucher
const NO_SCOPE = { location: null, client: null, assetType: null, equipmentSubType: null, assetModel: null }
const SCOPE_LABELS = { location: 'Location', client: 'Sub-location', assetType: 'Asset type', equipmentSubType: 'Sub-type', assetModel: 'Model' }

export const ReconciliationCockpit = (props) => {
  // ---- data for loading ----
  // Generate always loads the WHOLE voucher: there are no load filters
  // (Rishabh, 2026-10-17). A partial load made duplicate auto-resolve pick a keeper
  // from only the scans that happened to be loaded. The only thing sent is the
  // voucher's own audit scope (location / sub-location / type / sub-type / model
  // stored on the voucher), because the server narrows the register by it - without
  // it a scoped audit would list the whole register as missing. Shown read-only.
  // Narrowing the rows on screen is done with the table's own filters.
  const [voucher, setVoucher] = useState(null)
  const [voucherScope, setVoucherScope] = useState(NO_SCOPE)
  const [locations, setLocations] = useState([])
  const [clients, setClients] = useState([])

  const [astData, setAstData] = useState([])
  const [pvData, setPvData] = useState([])
  const [allAssets, setAllAssets] = useState([])
  // Per-PV-date "assets moved after this date" maps (keyed by the PV date), used to
  // match LINK candidates by where an asset was on the PV date, not where it is now.
  const [movedByPvDate, setMovedByPvDate] = useState({})

  // ---- cockpit ui state ----
  const [sourceFilter, setSourceFilter] = useState('AST') // AST | PV | ASSETS
  // Stage filter, driven by the three counters in the results toolbar.
  const [statusFilter, setStatusFilter] = useState('all') // all | review | reviewed | processed
  // Second-level AST filter: one reconcileStatus code, or null for "all statuses".
  const [recStatusFilter, setRecStatusFilter] = useState(null)
  // PV tab: filter by the "Best match" column's category (LINKED / rule key / NO_CANDIDATE).
  const [bestMatchFilter, setBestMatchFilter] = useState(null)
  const [query, setQuery] = useState('')
  const [currentKey, setCurrentKey] = useState(null)
  const [selected, setSelected] = useState(new Set())

  const [linkRecord, setLinkRecord] = useState(null)
  const [showLink, setShowLink] = useState(false)
  const [linkTab, setLinkTab] = useState(null) // pre-selected group tab in the link picker
  const [galleryRecord, setGalleryRecord] = useState(null)
  const [showGallery, setShowGallery] = useState(false)
  const [astView, setAstView] = useState(null)
  // Record whose photo history is open for comparison (AST table only).
  const [cmpRecord, setCmpRecord] = useState(null)

  // ---------- initial loads ----------
  useEffect(() => { props.fetchFilter('Reconciliation') }, [])

  useEffect(() => {
    if (props.authUser && props.authUser.locations) {
      setLocations([...props.authUser.locations].sort((a, b) => a.name.localeCompare(b.name)))
    }
    if (props.authUser && props.authUser.clients) {
      setClients([...props.authUser.clients].sort((a, b) => a.name.localeCompare(b.name)))
    }
  }, [props.authUser])

  useEffect(() => {
    const ra = props.reconciliationAssets || {}
    // Stamp a unique _uid on every record so React keys never collide, even when
    // the backend hands back records that share an _id (see rkey in helpers).
    const astRows = (ra.astAssets || []).map((r, i) => ({ ...r, _uid: `AST#${i}` }))
    const pvRows = (ra.pvAssets || []).map((r, i) => ({ ...r, _uid: `PV#${i}` }))
    setAstData(astRows)
    setPvData(pvRows)
    setAllAssets(ra.allAssets || [])
    setMovedByPvDate(ra.movedByPvDate || {})
    setSelected(new Set())
    setCurrentKey(null)
    setBestMatchFilter(null)
    // Rehydrate saved Assets-tab decisions (SOLD / IGNORE) so a reloaded register
    // asset shows as saved rather than reverting to "Missing".
    const assetDec = {}
    ;(ra.allAssets || []).forEach(a => {
      const s = a && a.savedAssetDecision
      if (s) assetDec[String(a._id)] = {
        saveId: s.saveId, actionTaken: s.actionTaken, isAlreadySaved: true,
        isProcessed: s.isProcessed, processedOn: s.processedOn, processedRemark: s.processedRemark,
      }
    })
    setAssetDecisions(assetDec)
    // Rehydrate the new-asset form state from what was saved, so a reloaded record
    // shows its saved details in the modal and a re-save merges edits onto the full
    // set (rather than only the field just changed).
    const hydrated = {}
    astRows.concat(pvRows).forEach(r => {
      const nd = r.actionTaken && r.actionTaken.name === 'NEW_ASSET' && r.actionTaken.newAssetDetails
      if (nd) hydrated[r._uid] = { fields: { ...nd } }
    })
    setAssetDetails(hydrated)
  }, [props.reconciliationAssets])

  // ---------- AI decisions for the loaded voucher ----------
  // One GET per voucher load (Generate, or the auto re-check after Process) returns the
  // latest AI link-mode decision per PV item, for the row's AI tag. Keys match the
  // reconciliation save: record _id + temp code. The voucher is taken from the PV rows
  // themselves (auditVoucher), not from the voucher picker. A failed load is shown,
  // never treated as "no AI decisions".
  const [aiDecisions, setAiDecisions] = useState({})      // PV key -> latest AI link-mode decision record
  const pdVoucherRef = useRef(null)  // voucher the loaded state belongs to
  const pdGenRef = useRef(0)         // bumps on every reload; stale GETs are ignored
  const pdKey = (recordId, tempCode) => `${recordId}|${tempCode || ''}`
  const pdKeyOf = (r) => pdKey(r._id, r.tempAssetCode)
  const idOfRef = (v) => (v && v._id) ? String(v._id) : (v ? String(v) : null)
  const errMsg = (err, fallbackText) =>
    (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || fallbackText
  // Keep whichever document is newer, so a GET that lands late cannot replace a fresh one.
  const newerDoc = (a, b) => (!a ? b : !b ? a : (new Date(b.createdAt) > new Date(a.createdAt) ? b : a))

  useEffect(() => {
    const pvAssets = (props.reconciliationAssets && props.reconciliationAssets.pvAssets) || []
    const vouchers = [...new Set(pvAssets.map(r => idOfRef(r.auditVoucher)))]
    const vId = vouchers.length === 1 ? vouchers[0] : null
    const gen = ++pdGenRef.current
    if (vId !== pdVoucherRef.current) {
      // Different voucher: nothing from the previous one may show here.
      setAiDecisions({})
      Object.values(jevTokens.current).forEach(t => { t.cancelled = true })
      jevGenRef.current += 1
      jevTokens.current = {}; jevChecksRef.current = {}; setJevChecks({}); setJevSearch({})
      // Runs tied to the previous voucher stop and their panels clear (never left "running").
      if (aiRunToken.current) aiRunToken.current.cancelled = true
      if (backlogToken.current) backlogToken.current.cancelled = true
      setAiRun(null); setBacklog(null)
    }
    pdVoucherRef.current = vId
    if (!pvAssets.length) return
    if (!vId) {
      message.warning(`AI tags not loaded: ${vouchers.length > 1 ? 'PV rows span more than one voucher' : 'PV rows carry no audit voucher'}`)
      return
    }
    axios.get(`/pv-photo-description/voucher/${vId}`)
      .then(res => {
        if (gen !== pdGenRef.current) return
        const body = res.data || {}
        setAiDecisions(prev => {
          const next = { ...prev }
          ;(body.aiDecisions || []).forEach(d => { const k = pdKey(d.assetRecordId, d.tempAssetCode); next[k] = newerDoc(next[k], d) })
          return next
        })
      })
      .catch(err => { if (gen === pdGenRef.current) message.warning(`AI tags not loaded: ${errMsg(err, 'request failed')}`) })
  }, [props.reconciliationAssets]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- AI search over the link options ----------
  // Used by AI link mode (and Continue / Search again in the picker): asks the AI (via the
  // backend and the AI service) whether the item is the same physical item as each link
  // option IN PRIORITY ORDER - rule groups most specific first, assets in the order the
  // picker lists them - one option at a time, and STOPS at the first option whose
  // probability is >= JEV_MATCH_THRESHOLD.
  // It also stops on Stop, on closing the picker, on leaving the page, and when a call
  // fails (shown with the reason; Continue re-walks the CURRENT options from the top,
  // answers already given this session cost nothing, and resumes at the first new one).
  // After a match, options with the same model / sub-type / place-on-PV-date are
  // dry-run (request built, Jev NOT called): if any would send the identical request,
  // Jev cannot tell them apart and the match is shown as ambiguous.
  // See docs/reconciliation-cockpit-pv-jev-check.md.
  const JEV_MATCH_THRESHOLD = 0.5   // Rishabh, 2026-10-09: "till it gets to 50%"
  const JEV_CALL_CAP = 20           // Rishabh, 2026-10-09: at most 20 NEW Jev calls per PV item per run
  const [jevChecks, setJevChecks] = useState({}) // pair key -> { running } | { doc } | { error }
  const jevChecksRef = useRef({})                // same, readable inside the async loop
  const jevGenRef = useRef(0)                    // bumps on voucher reset: older loops stop writing
  // PV key -> { status: running|found|exhausted|stopped|error, order, pos, matchId, identical, error, startedAt }
  const [jevSearch, setJevSearch] = useState({})
  const jevTokens = useRef({})                   // PV key -> live run token { cancelled, gen }
  const jevKeyOf = (r, a) => `${pdKeyOf(r)}|${a._id}`
  const setJevCheck = (k, v, gen) => {
    if (gen !== jevGenRef.current) return
    jevChecksRef.current = { ...jevChecksRef.current, [k]: v }
    setJevChecks(jevChecksRef.current)
  }
  // Leaving the page stops every running search (no Jev calls after unmount).
  useEffect(() => () => {
    Object.values(jevTokens.current).forEach(t => { t.cancelled = true })
    if (aiRunToken.current) aiRunToken.current.cancelled = true
    if (backlogToken.current) backlogToken.current.cancelled = true
  }, [])

  const jevPost = (r, a, extra) => axios.post(`/pv-photo-description/jev-compare/${idOfRef(r.auditVoucher)}`, {
    pvId: idOfRef(r.pvId), assetRecordId: String(r._id), tempAssetCode: r.tempAssetCode || '', assetId: String(a._id), ...extra
  })

  // One Jev check; resolves { doc } or { error }. Reuses this session's OK answer.
  const jevCheckOne = (r, a, gen) => {
    const k = jevKeyOf(r, a)
    const have = jevChecksRef.current[k]
    if (have && have.doc && have.doc.status === 'OK') return Promise.resolve({ ...have, session: true })
    if (!idOfRef(r.auditVoucher)) { const v = { error: 'This PV row carries no audit voucher' }; setJevCheck(k, v, gen); return Promise.resolve(v) }
    setJevCheck(k, { running: Date.now() }, gen)
    return jevPost(r, a)
      .then(res => {
        const d = res.data || {}
        const v = (d.status === 'OK' && typeof d.sameProbability === 'number')
          ? { doc: d } : { doc: d, error: d.error || 'The AI gave no probability' }
        setJevCheck(k, v, gen); return v
      })
      .catch(err => { const v = { error: errMsg(err, 'AI check failed') }; setJevCheck(k, v, gen); return v })
  }

  // Same model / sub-type / place on the PV date as the matched option: the only options
  // that could produce an identical request (the photo text decides the rest).
  const sameMeta = (a, b) => ['assetModel', 'equipmentSubType'].every(f => idOfRef(a[f]) === idOfRef(b[f]))
    && idOfRef(a._onDateLocation != null ? a._onDateLocation : a.location) === idOfRef(b._onDateLocation != null ? b._onDateLocation : b.location)
    && idOfRef(a._onDateClient != null ? a._onDateClient : a.client) === idOfRef(b._onDateClient != null ? b._onDateClient : b.client)

  // Resolves { status, pos, matchIndex, jevCalls, optionsChecked } so AI link mode can act on it.
  // `skipIdentical` (AI mode): the decision rests on the Jev score alone, so the identical-option
  // dry-run is not done. A run stops after JEV_CALL_CAP NEW Jev calls (answers reused from this
  // session or from the server's stored identical request do not count) -> status 'capped'.
  const jevSearchRun = async (r, order, fromPos, opts = {}) => {
    const pk = pdKeyOf(r)
    if (jevTokens.current[pk]) jevTokens.current[pk].cancelled = true
    const gen = jevGenRef.current
    const token = { cancelled: false, gen }
    jevTokens.current[pk] = token
    // Only the live run of the current voucher may write this PV's search state.
    const live = () => jevTokens.current[pk] === token && gen === jevGenRef.current
    const put = (patch) => { if (live()) setJevSearch(prev => ({ ...prev, [pk]: { ...(prev[pk] || {}), ...patch } })) }
    let jevCalls = 0
    const end = (patch) => { put({ ...patch, jevCalls }); return { ...patch, jevCalls, optionsChecked: patch.checked } }
    put({ status: 'running', order, pos: fromPos, matchId: null, identical: null, error: null, startedAt: Date.now(), jevCalls: 0, cap: JEV_CALL_CAP })
    for (let i = fromPos; i < order.length; i++) {
      if (token.cancelled) return end({ status: 'stopped', pos: i, checked: i })
      put({ pos: i, jevCalls })
      const res = await jevCheckOne(r, order[i].asset, gen)
      if (!res.session && res.doc && res.doc.fromCache === false) jevCalls += 1
      // Evaluate the answer even if Stop was pressed while it was on its way: a match
      // that arrived is a match, never thrown away.
      const hit = !res.error && res.doc.sameProbability >= JEV_MATCH_THRESHOLD
      if (hit) {
        const out = end({ status: 'found', pos: i, matchId: String(order[i].asset._id), matchIndex: i, checked: i + 1,
          identical: opts.skipIdentical ? null : { checking: true } })
        if (!opts.skipIdentical) await jevCheckIdentical(r, order, i, res.doc.requestHash, token, put)
        return { ...out, doc: res.doc }
      }
      if (res.error) return end({ status: 'error', pos: i, error: res.error, checked: i })
      if (token.cancelled) return end({ status: 'stopped', pos: i + 1, checked: i + 1 })
      if (jevCalls >= JEV_CALL_CAP && i + 1 < order.length) return end({ status: 'capped', pos: i + 1, checked: i + 1 })
    }
    return end({ status: 'exhausted', pos: order.length, checked: order.length })
  }

  // Dry-run the remaining same-looking options (no Jev call) and record which would send
  // a byte-identical request to the match's.
  const jevCheckIdentical = async (r, order, matchPos, matchHash, token, put) => {
    const m = order[matchPos].asset
    const rest = order.slice(matchPos + 1).filter(o => sameMeta(o.asset, m))
    const same = []
    for (const o of rest) {
      if (token.cancelled) { put({ identical: { codes: same, partial: true } }); return }
      try {
        const res = await jevPost(r, o.asset, { dryRun: true })
        if (res.data && res.data.requestHash === matchHash) same.push(o.asset.assetCode)
      } catch (err) {
        put({ identical: { codes: same, error: errMsg(err, 'check failed'), partial: true } }); return
      }
    }
    put({ identical: { codes: same, checked: rest.length } })
  }

  const jevSearchStop = (r) => { const t = r && jevTokens.current[pdKeyOf(r)]; if (t) t.cancelled = true }

  // ---------- AI link mode ----------
  // "AI link" runs the Jev search on every PV item in the current filter that has no
  // decision yet, one item at a time, and SETS its action (pending save, like a
  // person's choice) - Rishabh, 2026-10-09:
  //   first option with Jev probability >= JEV_MATCH_THRESHOLD  -> LINK_ASSET
  //     (also when the asset's model differs: isPvOverwrite, like "Link and overwrite";
  //      also when other options look identical - the decision rests on the score alone)
  //   no option reaches it within JEV_CALL_CAP new Jev calls, all options checked,
  //   or no option at all                                        -> NEW_ASSET
  // Items already decided or processed are left alone. An asset linked earlier in the
  // run is never offered to a later item (one asset, one PV). Each decision is also
  // recorded server-side (pvaidecisions) with its reason and score, and the row is
  // tagged "AI" while its action still equals the AI's. A failed item is listed and
  // left undecided; 3 failures in a row halt the run (service likely down).
  // See docs/reconciliation-cockpit-ai-link-mode.md.
  const AI_HALT_AFTER_ERRORS = 3
  const [aiRun, setAiRun] = useState(null)
  const aiRunToken = useRef(null)
  const pvDataRef = useRef([])
  pvDataRef.current = pvData
  const assetDetailsRef = useRef({})     // latest assetDetails (set after its declaration below)
  const pvCandidatesRef = useRef(null)   // latest pvCandidates (fresh exclusions each render)

  // defaults = { ownedBy, rate } entered at start: applied to items marked NEW_ASSET, only
  // to the required fields they are missing (scan values / edits are kept).
  // `keys` are PV identity keys (record _id + temp code), not row positions, so a reload of the
  // same voucher cannot redirect a decision to another row.
  const runAiLink = async (keys, defaults) => {
    if (!defaults || !defaults.ownedBy || typeof defaults.rate !== 'number' || defaults.rate < 0) {
      message.error('AI link needs a default owner and invoice value')
      return
    }
    if (aiRunToken.current) aiRunToken.current.cancelled = true
    const token = { cancelled: false }
    aiRunToken.current = token
    const gen = jevGenRef.current
    const runId = `ai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    const st = { status: 'running', runId, total: keys.length, done: 0, linked: 0, newCount: 0, skipped: 0, errors: 0,
      items: [], startedAt: Date.now(), current: null }
    const upd = (patch) => { Object.assign(st, patch); if (gen === jevGenRef.current) setAiRun({ ...st, items: st.items.slice() }) }
    upd({})
    let failedInARow = 0
    const claimed = new Set()
    const rowOf = (key) => {
      const d = pvDataRef.current.find(x => pdKeyOf(x) === key)
      return d ? { ...d, source: 'PV' } : null
    }
    for (const key of keys) {
      if (token.cancelled || gen !== jevGenRef.current) { upd({ status: 'stopped', current: null }); return }
      const rec = rowOf(key)
      const uid = rec ? rec._uid : key
      if (!rec || rec.isProcessed || isDecided(rec)) {
        st.items.push({ uid, code: rec ? codeOf(rec) : key, outcome: 'skipped', note: rec ? 'decided or processed meanwhile' : 'row no longer loaded' })
        upd({ done: st.done + 1, skipped: st.skipped + 1 }); continue
      }
      upd({ current: { uid, code: codeOf(rec) } })
      const cands = pvCandidatesRef.current(rec)
      const order = []
      cands.groups.forEach(g => g.assets.forEach(a => {
        if (!claimed.has(String(a._id))) order.push({ asset: a, groupKey: g.key, groupName: g.name })
      }))
      let dec
      if (!order.length) {
        dec = { action: 'NEW_ASSET', reason: 'NO_OPTIONS', jevCalls: 0, optionsChecked: 0 }
      } else {
        const res = await jevSearchRun(rec, order, 0, { skipIdentical: true })
        if (res.status === 'stopped' || token.cancelled) {
          if (res.status !== 'found') { upd({ status: 'stopped', current: null }); return }
        }
        if (res.status === 'found') {
          dec = { action: 'LINK_ASSET', reason: 'MATCH', asset: order[res.matchIndex].asset, doc: res.doc,
            jevCalls: res.jevCalls, optionsChecked: res.optionsChecked }
        } else if (res.status === 'exhausted' || res.status === 'capped') {
          dec = { action: 'NEW_ASSET', reason: res.status === 'exhausted' ? 'ALL_CHECKED_BELOW' : 'CAP_REACHED_BELOW',
            jevCalls: res.jevCalls, optionsChecked: res.optionsChecked }
        } else {
          failedInARow += 1
          st.items.push({ uid, code: codeOf(rec), outcome: 'error', note: res.error || res.status, optionsTotal: order.length })
          upd({ done: st.done + 1, errors: st.errors + 1 })
          if (failedInARow >= AI_HALT_AFTER_ERRORS) {
            upd({ status: 'halted', current: null, haltReason: `${AI_HALT_AFTER_ERRORS} items in a row failed — last: ${res.error || res.status}` })
            return
          }
          continue
        }
      }
      failedInARow = 0
      if (gen !== jevGenRef.current) { upd({ status: 'stopped', current: null }); return }

      // Re-check just before applying: the search can take a while, and the person may have decided
      // this row, or linked the chosen asset elsewhere, in the meantime. Never overwrite that.
      const now = rowOf(key)
      let skipWhy = null
      if (!now) skipWhy = 'row no longer loaded'
      else if (now.isProcessed || isDecided(now)) skipWhy = 'decided by a person during the run'
      else if (dec.action === 'LINK_ASSET') {
        const still = pvCandidatesRef.current(now).groups.some(g => g.assets.some(a => String(a._id) === String(dec.asset._id)))
        if (!still) skipWhy = `${dec.asset.assetCode} was taken by another decision during the run`
      }
      if (skipWhy) {
        st.items.push({ uid, code: codeOf(rec), outcome: 'skipped', note: skipWhy })
        upd({ done: st.done + 1, skipped: st.skipped + 1 }); continue
      }
      const applyUid = now._uid

      // Apply exactly as the manual buttons do (pending save).
      const pvModel = rec.assetModel && (rec.assetModel._id || rec.assetModel)
      let overwrite = false
      if (dec.action === 'LINK_ASSET') {
        const a = dec.asset
        const am = a.assetModel && (a.assetModel._id || a.assetModel)
        overwrite = !!(pvModel && am && String(am) !== String(pvModel))
        setPvAction(applyUid, { name: 'LINK_ASSET', refRecordId: a._id, refRecordSubId: a.assetCode, isPvOverwrite: overwrite }, a)
        claimed.add(String(a._id))
      } else {
        setPvAction(applyUid, { name: 'NEW_ASSET' })
        const missing = missingFor(assetDetailsRef.current, now)
        const fill = {}
        if (missing.includes('ownedBy')) fill.ownedBy = defaults.ownedBy
        if (missing.includes('rate')) fill.rate = defaults.rate
        if (Object.keys(fill).length) applyDetails([now], fill, 'group:AI link default')
        dec.defaultsApplied = Object.keys(fill)
      }

      // Audit trail (server validates the comparison for a link).
      let auditError = null
      try {
        const saved = await axios.post(`/pv-photo-description/ai-decision/${idOfRef(rec.auditVoucher)}`, {
          runId, pvId: idOfRef(rec.pvId), assetRecordId: String(rec._id), tempAssetCode: rec.tempAssetCode || '',
          action: dec.action, reason: dec.reason,
          assetId: dec.asset ? String(dec.asset._id) : undefined,
          comparisonId: dec.doc ? String(dec.doc._id) : undefined, isPvOverwrite: overwrite,
          threshold: JEV_MATCH_THRESHOLD, jevCallCap: JEV_CALL_CAP, jevCalls: dec.jevCalls,
          optionsChecked: dec.optionsChecked, optionsTotal: order.length,
          defaultOwner: dec.action === 'NEW_ASSET' ? defaults.ownedBy : undefined,
          defaultRate: dec.action === 'NEW_ASSET' ? defaults.rate : undefined,
          defaultsApplied: dec.action === 'NEW_ASSET' ? dec.defaultsApplied : undefined
        })
        if (gen === jevGenRef.current) setAiDecisions(prev => ({ ...prev, [pdKeyOf(rec)]: saved.data }))
      } catch (err) {
        auditError = errMsg(err, 'AI decision record not saved')
      }
      st.items.push({ uid, code: codeOf(rec), outcome: dec.action === 'LINK_ASSET' ? 'linked' : 'new', reason: dec.reason,
        assetCode: dec.asset && dec.asset.assetCode, score: dec.doc && dec.doc.sameProbability, overwrite,
        jevCalls: dec.jevCalls, optionsChecked: dec.optionsChecked, optionsTotal: order.length, auditError,
        defaultsApplied: dec.defaultsApplied })
      upd(dec.action === 'LINK_ASSET' ? { done: st.done + 1, linked: st.linked + 1 } : { done: st.done + 1, newCount: st.newCount + 1 })
    }
    upd({ status: 'done', current: null })
  }
  const stopAiLink = () => {
    const t = aiRunToken.current
    if (t) t.cancelled = true
    if (aiRun && aiRun.current) jevSearchStop(pvDataRef.current.find(d => d._uid === aiRun.current.uid))
  }
  // ---------- One-time photo description backlog ----------
  // "Prepare photos" asks the backend to describe, ahead of time, every photo AI link needs:
  // all PV photos of this voucher, then the latest photo set of every register asset of the
  // organisation. One batch per call, sequential, resumable; already-described photos are
  // skipped by the backend. Stop / leaving the page stops it. Descriptions are stored per
  // photo in AssetPro's DB (the AI service decides the model and prompt).
  const [backlog, setBacklog] = useState(null)
  const backlogToken = useRef(null)
  const runBacklog = async () => {
    const vId = pdVoucherRef.current
    if (!vId) { message.error('Load a voucher with PV items first'); return }
    if (backlogToken.current) backlogToken.current.cancelled = true
    const token = { cancelled: false }
    backlogToken.current = token
    const st = { status: 'running', phase: 'PV', startedAt: Date.now(), byPhase: {} }
    const upd = (patch) => { Object.assign(st, patch); setBacklog({ ...st, byPhase: { ...st.byPhase } }) }
    upd({})
    for (const phase of ['PV', 'REGISTER']) {
      let cursor = null
      const acc = { total: null, processed: 0, photos: 0, described: 0, already: 0, failed: 0, notDescribed: 0 }
      st.byPhase[phase] = acc
      upd({ phase })
      for (;;) {
        if (token.cancelled) { upd({ status: 'stopped' }); return }
        let res
        try {
          res = (await axios.post(`/pv-photo-description/describe-backlog/${vId}`, { phase, cursor })).data
        } catch (err) {
          upd({ status: 'error', error: errMsg(err, 'request failed') }); return
        }
        acc.total = res.total
        ;['processed', 'photos', 'described', 'already', 'failed', 'notDescribed'].forEach(k => { acc[k] += res[k] || 0 })
        cursor = res.cursor
        upd({})
        if (res.done) break
      }
    }
    upd({ status: 'done' })
  }
  const stopBacklog = () => { if (backlogToken.current) backlogToken.current.cancelled = true }

  // The AI tag on a row: only while the row's action is still the one the AI set.
  const aiTagOf = (r) => {
    const ai = aiDecisions[pdKeyOf(r)]
    if (!ai || !isDecided(r) || r.actionTaken.name !== ai.action) return null
    if (ai.action === 'LINK_ASSET' && String(r.actionTaken.refRecordId) !== String(ai.asset)) return null
    return ai
  }
  // Search again: forget this PV's session answers; the server still reuses identical
  // requests without asking the AI again.
  const jevSearchRestart = (r, order) => {
    const pk = pdKeyOf(r) + '|'
    jevChecksRef.current = Object.keys(jevChecksRef.current)
      .reduce((acc, k) => { if (!k.startsWith(pk)) acc[k] = jevChecksRef.current[k]; return acc }, {})
    setJevChecks(jevChecksRef.current)
    jevSearchRun(r, order, 0)
  }

  // ---------- sync: after SAVE (AST) ----------
  useEffect(() => {
    const saved = props.reconciliationSavedAstAssets || []
    if (!saved.length) return
    setAstData(prev => {
      const next = [...prev]
      saved.forEach(d => {
        const ix = next.findIndex(doc => doc._id.toString() === (d.astAsset && d.astAsset.assetRecordId && d.astAsset.assetRecordId.toString() || ''))
        // The server's copy is now authoritative, so any pending clear is settled.
        if (ix >= 0) next[ix] = { ...next[ix], saveId: d._id, isAlreadySaved: true, actionTaken: d.actionTaken, clearPending: false, dirty: false }
      })
      return next
    })
  }, [props.reconciliationSavedAstAssets])

  // ---------- sync: after SAVE (PV) ----------
  useEffect(() => {
    const saved = props.reconciliationSavedPvAssets || []
    if (!saved.length) return
    setPvData(prev => {
      const next = [...prev]
      saved.forEach(d => {
        // Older PV details share the PV's _id, so the temp code is part of the match.
        const ix = next.findIndex(doc => doc._id.toString() === (d.pvAsset && d.pvAsset.assetRecordId && d.pvAsset.assetRecordId.toString() || '')
          && (doc.tempAssetCode || '') === (d.pvAsset.tempCode || ''))
        // The server's copy is now authoritative, so any pending clear is settled.
        if (ix >= 0) next[ix] = { ...next[ix], saveId: d._id, isAlreadySaved: true, actionTaken: d.actionTaken, clearPending: false, dirty: false }
      })
      return next
    })
    // Record whether each saved link / new-asset decision was taken by the AI or a person.
    // The server derives the source from the saved record and the AI decision log - only
    // the saved ids are sent. A failure is shown (the save itself has succeeded).
    const ids = saved.filter(d => d && d._id && d.actionTaken && ['LINK_ASSET', 'NEW_ASSET'].includes(d.actionTaken.name)).map(d => String(d._id))
    const vId = saved.length ? idOfRef(saved[0].auditVoucher) : null
    if (ids.length && !vId) message.warning('Saved, but who took the decisions (AI / manual) was not recorded: the saved records carry no voucher')
    if (ids.length && vId) {
      axios.post(`/pv-photo-description/decision-source/${vId}`, { reconciliationIds: ids })
        .catch(err => message.warning(`Saved, but who took the decisions (AI / manual) was not recorded: ${errMsg(err, 'request failed')}`))
    }
  }, [props.reconciliationSavedPvAssets]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- sync: after PROCESS (AST) ----------
  useEffect(() => {
    const processed = props.reconciliationProcessedAstAssets || []
    if (!processed.length) return
    setAstData(prev => {
      const next = [...prev]
      processed.forEach(d => {
        const ix = next.findIndex(doc => doc._id.toString() === (d.astAsset && d.astAsset.assetRecordId && d.astAsset.assetRecordId.toString() || ''))
        if (ix >= 0) next[ix] = {
          // d is the row that was sent - its _id is the row id, not the saved record id.
          ...next[ix], saveId: d.saveId || next[ix].saveId, isAlreadySaved: true, actionTaken: d.actionTaken, clearPending: false, dirty: false,
          isProcessed: d.isProcessed, processedOn: d.processedOn && moment(d.processedOn).format('YYYY-MM-DD'), processedRemark: d.processedRemark || ''
        }
      })
      return next
    })
  }, [props.reconciliationProcessedAstAssets])

  // ---------- sync: after PROCESS (PV) ----------
  useEffect(() => {
    const processed = props.reconciliationProcessedPvAssets || []
    if (!processed.length) return
    setPvData(prev => {
      const next = [...prev]
      processed.forEach(d => {
        // Older PV details share the PV's _id, so the temp code is part of the match.
        const ix = next.findIndex(doc => doc._id.toString() === (d.pvAsset && d.pvAsset.assetRecordId && d.pvAsset.assetRecordId.toString() || '')
          && (doc.tempAssetCode || '') === (d.pvAsset.tempCode || ''))
        if (ix >= 0) next[ix] = {
          // d is the row that was sent - its _id is the row id, not the saved record id.
          ...next[ix], saveId: d.saveId || next[ix].saveId, isAlreadySaved: true, actionTaken: d.actionTaken, clearPending: false, dirty: false,
          isProcessed: d.isProcessed, processedOn: d.processedOn && moment(d.processedOn).format('YYYY-MM-DD'), processedRemark: d.processedRemark || ''
        }
      })
      return next
    })
  }, [props.reconciliationProcessedPvAssets])

  // ---------- sync: after SAVE (ASSET) ----------
  // Register-asset rows are view-derived, so we don't hold them in local state -
  // decisions are merged back into the queue by _id via assetDecisions.
  useEffect(() => {
    const saved = props.reconciliationSavedAssetAssets || []
    if (!saved.length) return
    setAssetDecisions(prev => {
      const next = { ...prev }
      saved.forEach(d => {
        const id = d.assetAsset && d.assetAsset.assetRecordId && d.assetAsset.assetRecordId.toString()
        // The server now holds exactly this decision: no longer unsaved.
        if (id) next[id] = { ...(next[id] || {}), saveId: d._id, isAlreadySaved: true, actionTaken: d.actionTaken, dirty: false }
      })
      return next
    })
  }, [props.reconciliationSavedAssetAssets])

  // ---------- sync: after PROCESS (ASSET) ----------
  useEffect(() => {
    const processed = props.reconciliationProcessedAssetAssets || []
    if (!processed.length) return
    setAssetDecisions(prev => {
      const next = { ...prev }
      processed.forEach(d => {
        const id = d.assetAsset && d.assetAsset.assetRecordId && d.assetAsset.assetRecordId.toString()
        if (id) next[id] = {
          // d is the row that was sent - its _id is the asset id, not the saved record id.
          ...(next[id] || {}), saveId: d.saveId || (next[id] || {}).saveId, isAlreadySaved: true, actionTaken: d.actionTaken, dirty: false,
          isProcessed: d.isProcessed, processedOn: d.processedOn && moment(d.processedOn).format('YYYY-MM-DD'), processedRemark: d.processedRemark || ''
        }
      })
      return next
    })
  }, [props.reconciliationProcessedAssetAssets])

  // ---------- derived ----------
  const queue = useMemo(() => {
    const a = (astData || []).map(r => ({ ...r, source: 'AST' }))
    const p = (pvData || []).map(r => ({ ...r, source: 'PV' }))
    return a.concat(p)
  }, [astData, pvData])

  // Id of a populated-or-raw reference field.
  const assetIdOf = (v) => (v && v._id) ? String(v._id) : (v ? String(v) : 'NA')

  // ---------- audit scope ----------
  // Which locations / sub-locations this audit actually covered. An asset outside
  // the scope is NOT missing - the audit never looked for it.
  //
  // Scope is DECLARED, never inferred from where scans happened: on AAV-1, 33 of
  // 87 scanned locations account for 0% of their register assets and only 3 clear
  // 90%, so a scan proves someone was there, not that the place was worked
  // through. Coverage is shown beside each location as evidence for the choice.
  //
  // NOT PERSISTED YET. Scope belongs to the voucher and decides how many assets
  // are reported missing (7,911 vs 2,391 on AAV-1), so it is an audit record, not
  // a view setting. Until the backend stores it, it lives here and resets on
  // reload - deliberately NOT in localStorage, which would give two people
  // different missing counts for the same audit.
  const [excludedLocs, setExcludedLocs] = useState(new Set())
  const [excludedSubs, setExcludedSubs] = useState(new Set()) // `${locationId}|${clientId}`
  const [scopeOpen, setScopeOpen] = useState(false)
  const [scopeExpanded, setScopeExpanded] = useState(new Set())
  const scopeSet = excludedLocs.size > 0 || excludedSubs.size > 0

  // Decisions made directly on register (Assets-tab) rows, keyed by asset id.
  // The Assets list is a huge derived memo, so decisions live here as an overlay
  // and are merged into each row rather than held on 6k+ objects.
  const [assetDecisions, setAssetDecisions] = useState({})

  // Where a register asset sat ON THE AUDIT DATE (Rishabh, 2026-10-24). Scope, the
  // SOLD exclusion and the location summary all use this place, so the audit is
  // judged against the register as it stood when it was audited and does not shift
  // as Process later transfers or sells assets. The backend reconstructs it from
  // transfers after the audit date and sends it as locationOnDate / clientOnDate on
  // every asset whenever the voucher has an audit date (asOfDate). With no audit
  // date (no scans at all) there is no earlier register to go back to, so today's
  // place is the register place - the report then says "no scans recorded".
  const placeOf = (a) => a.asOfDate
    ? { loc: assetIdOf(a.locationOnDate), cli: assetIdOf(a.clientOnDate) }
    : { loc: assetIdOf(a.location), cli: assetIdOf(a.client) }
  const subKey = (a) => { const p = placeOf(a); return `${p.loc}|${p.cli}` }
  const outOfScope = (a) =>
    excludedLocs.has(placeOf(a).loc) || excludedSubs.has(subKey(a))

  // Index AST/PV decisions by the asset they target. The previous version scanned
  // every AST and PV record for every asset - ~41M comparisons on this voucher.
  // Register asset ids a processed NEW_ASSET created: the backend-resolved
  // createdAssetId (old records) plus every id in actionTaken.asset / .assets.
  const createdIdsOf = (d) => {
    const at = d.actionTaken || {}
    const ids = new Set()
    if (d.createdAssetId) ids.add(String(d.createdAssetId))
    if (at.asset) ids.add(String(at.asset))
      ; (at.assets || []).forEach(x => { if (x) ids.add(String(x)) })
    return [...ids]
  }
  const decisionByAsset = useMemo(() => {
    const ast = new Map(), pv = new Map()
      ; (astData || []).forEach(d => {
        if (d.matchedAsset && d.matchedAsset._id) ast.set(String(d.matchedAsset._id), d)
      })
      // A link is only real once processed - mirror the NEW_ASSET rule below so the
      // target asset shows "Linked from PV" only after Process runs, not while the
      // decision is merely saved/reviewed.
      ; (pvData || []).forEach(d => {
        if (d.actionTaken && d.actionTaken.name === 'LINK_ASSET' && d.actionTaken.refRecordId && d.isProcessed)
          pv.set(String(d.actionTaken.refRecordId), d)
      })
      // A processed "create new asset" now has a real register asset - key it by
      // the created asset id (backend-resolved, incl. old records via tempAssetCode)
      // so that register row is recognised as created-from-PV.
      // A PV of qty > 1 creates several assets (actionTaken.assets) - key every one.
      // Applied AFTER the links, so it wins: on AAV-1, 23 PV items were linked to an
      // asset another PV item had created in the same audit. That asset was not in the
      // register on the audit date, so it is "new", never "recovered" - before, the
      // record that happened to come last decided it (17 read as recovered).
      ; (pvData || []).forEach(d => {
        if (d.actionTaken && d.actionTaken.name === 'NEW_ASSET' && d.isProcessed)
          createdIdsOf(d).forEach(id => pv.set(id, d))
      })
    // Assets created by Process from an AST scan (a repeat scan of a code that was a
    // second physical item). They carry a new code no scan matches, so without this
    // they would read as Missing or "bought after the audit date".
    const astCreated = new Map()
      ; (astData || []).forEach(d => {
        if (d.actionTaken && d.actionTaken.name === 'NEW_ASSET' && d.isProcessed)
          createdIdsOf(d).forEach(id => astCreated.set(id, d))
      })
    return { ast, pv, astCreated }
  }, [astData, pvData])

  // ---------- audit cut-off (purchase date) ----------
  // The moment the last asset was captured in this audit - the latest AST scan
  // time or PV date. The backend computes it once per voucher and puts it on every
  // register asset as asOfDate (with asOfSource / asOfRef naming what set it).
  //
  // An asset PURCHASED after that day could not have been there to find, so it is
  // outside this audit's purview: if nothing in the audit accounted for it, it is
  // not "Missing" and is left out of the Assets list altogether (counted and named
  // in a banner and in Export, never silently). Compared by calendar day, so an
  // asset bought on the cut-off day itself stays in.
  //
  // Not applied to an asset the audit DID account for (found / linked / created
  // from a PV) - the audit saw it, whatever its paperwork says - nor to one that
  // already carries a decision, which would otherwise vanish half-done. An asset
  // with no purchase date cannot be tested and stays listed.
  const auditCutoff = useMemo(() => {
    const a = (allAssets || []).find(x => x && x.asOfDate)
    return a ? { date: a.asOfDate, source: a.asOfSource, ref: a.asOfRef } : null
  }, [allAssets])
  const boughtAfterCutoff = (a) =>
    !!(auditCutoff && a.invoiceDate && moment(a.invoiceDate).isAfter(moment(auditCutoff.date), 'day'))

  // When each location | sub-location pair was last audited: the latest AST scan or
  // PV item captured AT that place (where it was found). Used to tell a missing asset
  // from one bought after its own place had been audited (Rishabh, 2026-10-24).
  const lastScanByPlace = useMemo(() => {
    const m = new Map()
    const note = (r) => {
      if (!r || !r.time) return
      const t = new Date(r.time)
      if (isNaN(t.getTime())) return
      const k = `${assetIdOf(r.location)}|${assetIdOf(r.client)}`
      if (!m.has(k) || t > m.get(k)) m.set(k, t)
    }
      ; (astData || []).forEach(note)
      ; (pvData || []).forEach(note)
    return m
  }, [astData, pvData]) // eslint-disable-line react-hooks/exhaustive-deps

  // An unaccounted register asset bought after the last scan of the place it sat in on
  // the audit date (calendar day; bought the same day stays Missing - it could have been
  // there). The audit cannot have found it, so it is not Missing. Strict: no purchase
  // date, or a place with no scan at all, cannot be tested and stays Missing.
  const boughtAfterPlaceAudit = (a) => {
    if (!a.invoiceDate) return null
    const last = lastScanByPlace.get(subKey(a))
    return (last && moment(a.invoiceDate).isAfter(moment(last), 'day')) ? last : null
  }
  const hasAssetDecision = (r) => !!(r.isProcessed || r.saveId || (r.actionTaken && r.actionTaken.name))

  // Master-asset list for the Assets tab, each register asset carrying its status
  // in THIS audit. PV records set to "create new asset" are appended as rows with
  // no asset code - they do not exist in the register until Process runs, and
  // hiding them would lose the answer to "how many new assets will this create?".
  const assetPartition = useMemo(() => {
    const soldLoc = (locations || []).find(l => l.name === 'SOLD')
    const postAudit = []
    // In the SOLD location on the audit date - not part of the register that day.
    // Kept (not just dropped) so the report can show them in its register bridge.
    const soldOnDate = []
    const rows = (allAssets || [])
      .filter(a => {
        if (soldLoc && placeOf(a).loc === String(soldLoc._id)) { soldOnDate.push(a); return false }
        return true
      })
      .map((a, i) => {
        const id = String(a._id)
        const rec = { ...a, source: 'ASSET', _uid: `ASSET#${i}` }
        const ast = decisionByAsset.ast.get(id)
        const pv = decisionByAsset.pv.get(id)
        const astNew = decisionByAsset.astCreated.get(id)
        const pvNew = pv && pv.actionTaken && pv.actionTaken.name === 'NEW_ASSET'
        if (ast) {
          rec.astRef = ast
          rec.origStatus = ast.reconcileStatus
          rec.recStatus = ast.actionTaken && ast.actionTaken.name
          rec.recRemark = ast.processedRemark
          rec.assetStatus = 'found'
        } else if (pv && (pvNew || !astNew)) {
          rec.pvRef = pv
          rec.recStatus = pv.actionTaken && pv.actionTaken.name
          rec.recRemark = pv.processedRemark
          // Carry the PV's processed state onto this register row so a linked /
          // created-from-PV asset shows and counts as Processed (both are keyed here
          // only once the PV record is processed).
          rec.isProcessed = pv.isProcessed
          rec.processedOn = pv.processedOn
          rec.processedRemark = pv.processedRemark
          // Created from a PV (NEW_ASSET) vs an existing asset linked from a PV.
          rec.assetStatus = (pv.actionTaken && pv.actionTaken.name === 'NEW_ASSET') ? 'newasset' : 'pvlink'
        } else if (astNew) {
          // Created by Process from an AST scan - new to the register, like a PV one.
          rec.astCreatedRef = astNew
          rec.recStatus = 'NEW_ASSET'
          rec.recRemark = astNew.processedRemark
          rec.isProcessed = astNew.isProcessed
          rec.processedOn = astNew.processedOn
          rec.processedRemark = astNew.processedRemark
          rec.assetStatus = 'newasset'
        } else if (outOfScope(a)) {
          rec.assetStatus = 'oos'
        } else {
          rec.assetStatus = 'missing'
        }
        // Bought after its own place was last audited - but on or before the audit date,
        // so it is in the register on that date (bought after the audit date as a whole
        // is left out below, as before). An asset that already carries a decision keeps
        // it, the same exception as the audit-date rule.
        if (rec.assetStatus === 'missing' && !boughtAfterCutoff(rec) && !hasAssetDecision({ ...rec, ...(assetDecisions[id] || {}) })) {
          const placeAudited = boughtAfterPlaceAudit(a)
          if (placeAudited) { rec.assetStatus = 'boughtafter'; rec.placeAuditedOn = placeAudited }
        }
        // Overlay any decision made directly on this register asset.
        const dec = assetDecisions[id]
        if (dec) Object.assign(rec, dec)
        return rec
      })
      .filter(rec => {
        const unaccounted = rec.assetStatus === 'missing' || rec.assetStatus === 'oos'
        if (unaccounted && boughtAfterCutoff(rec) && !hasAssetDecision(rec)) { postAudit.push(rec); return false }
        return true
      })

    const created = (pvData || [])
      // Placeholder rows for new assets not yet created. Once processed, the real
      // register asset exists (recognised above via decisionByAsset), so drop the
      // placeholder to avoid a duplicate empty-code row.
      .filter(d => d.actionTaken && d.actionTaken.name === 'NEW_ASSET'
        && !(d.isProcessed && (d.createdAssetId || d.actionTaken.asset)))
      .map((d, i) => ({
        _id: `new-${d._uid}`, _uid: `NEWASSET#${i}`, source: 'ASSET', assetStatus: 'newasset', isPlaceholder: true,
        assetCode: null, pvRef: d, location: d.location, client: d.client,
        assetModel: d.assetModel, equipmentSubType: d.equipmentSubType, assetType: d.assetType,
        photos: d.photos, isProcessed: d.isProcessed
      }))

    return { rows: rows.concat(created), postAudit, soldOnDate }
  }, [allAssets, locations, decisionByAsset, pvData, excludedLocs, excludedSubs, assetDecisions, auditCutoff, lastScanByPlace]) // eslint-disable-line react-hooks/exhaustive-deps
  const assetQueue = assetPartition.rows
  // Left out of the list: purchased after the audit's last capture (see above).
  const postAuditAssets = assetPartition.postAudit

  const ASSET_STATUS = {
    found: { label: 'Found in audit', cls: 'found' },
    pvlink: { label: 'Linked from PV', cls: 'pvlink' },
    // PV items set to "create" and repeat AST scans processed as a new asset.
    newasset: { label: 'New asset from audit', cls: 'newasset' },
    missing: { label: 'Missing', cls: 'miss' },
    // Not found, but bought after its location / sub-location was last audited.
    boughtafter: { label: 'Bought after its place was audited', cls: 'boughtafter' },
    oos: { label: 'Out of scope', cls: 'oos' }
  }
  const ASSET_ORDER = ['found', 'pvlink', 'newasset', 'missing', 'boughtafter', 'oos']

  const assetBreakdown = useMemo(() => {
    const c = {}
    assetQueue.forEach(r => { c[r.assetStatus] = (c[r.assetStatus] || 0) + 1 })
    return ASSET_ORDER.filter(k => c[k]).map(k => ({ key: k, count: c[k] }))
  }, [assetQueue]) // eslint-disable-line react-hooks/exhaustive-deps

  // Coverage per location AND per sub-location, the evidence shown in the scope
  // picker. Both lists come from the asset register itself, so they only ever
  // offer places assets are actually recorded in - no separate master to drift.
  // Places are as on the audit date (placeOf), the same basis scope is applied on.
  const locationCoverage = useMemo(() => {
    const per = new Map()
      ; (allAssets || []).forEach(a => {
        const { loc, cli } = placeOf(a)
        if (!per.has(loc)) per.set(loc, { loc, total: 0, accounted: 0, subs: new Map() })
        const e = per.get(loc)
        e.total += 1
        if (!e.subs.has(cli)) e.subs.set(cli, { client: cli, total: 0, accounted: 0 })
        const sub = e.subs.get(cli)
        sub.total += 1
        const id = String(a._id)
        if (decisionByAsset.ast.has(id) || decisionByAsset.pv.has(id)) { e.accounted += 1; sub.accounted += 1 }
      })
    const pct = (acc, tot) => (tot ? Math.round((acc / tot) * 100) : 0)
    return [...per.values()]
      .map(e => ({
        ...e,
        name: locName(e.loc, locations) || '(unnamed location)',
        pct: pct(e.accounted, e.total),
        subs: [...e.subs.values()]
          .map(sv => ({
            ...sv, name: clientName(sv.client, clients) || '(no sub-location)',
            pct: pct(sv.accounted, sv.total)
          }))
          .sort((a, b) => b.total - a.total)
      }))
      .sort((a, b) => b.pct - a.pct || b.total - a.total)
  }, [allAssets, decisionByAsset, locations, clients]) // eslint-disable-line react-hooks/exhaustive-deps

  // Decisions on Missing / Out of scope are gated until PV linking is PROCESSED:
  // any unprocessed PV could still claim one of these assets, and disposing of it
  // first would double-claim it.
  const pvOutstanding = useMemo(
    () => (pvData || []).filter(d => !d.isProcessed).length, [pvData])
  const assetsGated = pvOutstanding > 0

  // ---- register-asset (Assets tab) decisions ----
  const setAssetAction = (r, name) => {
    if (assetsGated || r.isProcessed) return
    const id = String(r._id)
    setAssetDecisions(prev => {
      const cur = prev[id] || {}
      // Toggle off when the same action is clicked again.
      if (cur.actionTaken && cur.actionTaken.name === name) return clearedDecision(prev, id, cur)
      return { ...prev, [id]: { ...cur, actionTaken: { name }, dirty: true } }
    })
  }
  const clearedDecision = (prev, id, cur) => {
    // A server-held decision (has saveId) must go up as an explicit clear; a purely
    // local one just disappears.
    if (cur.saveId) return { ...prev, [id]: { ...cur, actionTaken: null, dirty: true } }
    const n = { ...prev }; delete n[id]; return n
  }
  const clearAssetAction = (r) => {
    if (r.isProcessed) return
    const id = String(r._id)
    setAssetDecisions(prev => clearedDecision(prev, id, prev[id] || {}))
  }
  // Bulk versions for the Assets-tab filter bar. Same rules as one row at a time:
  // only Missing rows, never a processed one, nothing while PV is unprocessed.
  const bulkAssetTargets = (rows) => assetsGated ? []
    : rows.filter(r => r.assetStatus === 'missing' && r._id && !r.isProcessed)
  const setAssetActionBulk = (rows, name) => {
    const targets = bulkAssetTargets(rows)
    setAssetDecisions(prev => {
      const next = { ...prev }
      targets.forEach(r => {
        const id = String(r._id)
        next[id] = { ...(next[id] || {}), actionTaken: { name }, dirty: true }
      })
      return next
    })
    return targets.length
  }
  const clearAssetActionBulk = (rows) => {
    const targets = bulkAssetTargets(rows).filter(r => r.actionTaken && r.actionTaken.name)
    setAssetDecisions(prev => targets.reduce((acc, r) => {
      const id = String(r._id)
      return clearedDecision(acc, id, acc[id] || {})
    }, { ...prev }))
    return targets.length
  }

  // Register-asset rows to send. Shape mirrors what save-action reads for an ASSET
  // record: the register asset's own id/code/place, the UI status, and the action.
  const assetRowPayload = (r) => ({
    _id: r._id,
    assetCode: r.assetCode,
    location: r.location,
    client: r.client,
    reconcileStatus: (ASSET_STATUS[r.assetStatus] && ASSET_STATUS[r.assetStatus].label) || 'NA',
    actionTaken: (r.actionTaken && r.actionTaken.name) ? r.actionTaken : null,
    saveId: r.saveId,
  })
  // decided (SOLD/IGNORE) or a pending clear (server holds it but it was cleared)
  const assetSaveRows = () => assetQueue.filter(r =>
    r.assetStatus === 'missing' && r._id && !r.isProcessed &&
    ((r.actionTaken && r.actionTaken.name) || (r.saveId && r.actionTaken === null)))

  const isAssets = sourceFilter === 'ASSETS'

  const isDecided = (r) => !!(r.actionTaken && r.actionTaken.name)

  // Clearing a decision that was already SAVED is itself an action that has to be
  // saved - the server still holds the old one until then. Such a record keeps a
  // clearPending marker and stays in Reviewed as "pending save"; it only returns
  // to To review once the clear has actually round-tripped to the server.
  // Clearing a decision that was never saved has nothing to persist and goes
  // straight back to To review.
  const hasPendingClear = (r) => !!(r.clearPending && r.saveId && !r.isProcessed)

  // "The server does NOT hold what the UI is showing." Three ways that happens:
  // a decision that was never saved, a decision changed since it was saved
  // (dirty), or a saved decision that has been cleared.
  const isUnsaved = (r) => !r.isProcessed && (
    hasPendingClear(r) || (isDecided(r) && (!r.saveId || !!r.dirty))
  )

  // Processing is asynchronous on the server: a submitted record comes back
  // isProcessed:false carrying processedRemark 'Processing...', and the outcome
  // only lands on a later reload. A record in that state is no longer the user's
  // to act on, and a record whose processing FAILED is emphatically theirs again.
  // Both are therefore stages of their own rather than being left to sit in
  // "Reviewed / saved", where they were previously indistinguishable from work
  // that had never been submitted at all.
  const PROCESSING_REMARK = 'Processing...'
  const isInFlight = (r) => !r.isProcessed && r.processedRemark === PROCESSING_REMARK
  const didFail = (r) => !r.isProcessed && !!r.processedRemark && r.processedRemark !== PROCESSING_REMARK

  // Stage predicates. Declared here, above passesFilter, because the filtered list
  // is computed further down in the component body and would otherwise hit these
  // consts before initialisation.
  // A pending clear is still "reviewed" work in flight, so it does NOT count as
  // To review until the clear has been saved.
  const awaitingReview = (r) => !isDecided(r) && !hasPendingClear(r)
  const inReview = (r) => (isDecided(r) || hasPendingClear(r)) && !r.isProcessed
    && !isInFlight(r) && !didFail(r)
  const codeOf = (r) => r.source === 'PV' ? r.tempAssetCode : r.assetCode
  const nameOf = (r) => r.source === 'AST'
    ? (r.matchedAsset && (r.matchedAsset.altName || r.matchedAsset.assetCode)) || '—'
    : (r.assetModel && r.assetModel.name) || '—'

  // model / subtype / type — AST reads off its matched master asset; PV and the
  // master asset itself read off their own populated fields.
  const typeLine = (r) => {
    let model, sub, type
    if (r.source === 'AST') {
      const m = r.matchedAsset || {}
      model = m.assetModel && m.assetModel.name
      sub = m.equipmentSubType && m.equipmentSubType.name
      type = m.equipmentSubType && m.equipmentSubType.assetType && m.equipmentSubType.assetType.name
    } else if (r.source === 'ASSET') {
      model = r.assetModel && r.assetModel.name
      sub = r.equipmentSubType && r.equipmentSubType.name
      type = r.equipmentSubType && r.equipmentSubType.assetType && r.equipmentSubType.assetType.name
    } else {
      model = r.assetModel && r.assetModel.name
      sub = r.equipmentSubType && r.equipmentSubType.name
      type = r.assetType && r.assetType.name
    }
    return [model, sub, type].filter(Boolean).join(' / ')
  }

  // location / sub-location (the scanned place on the record)
  const placeLine = (r) => [locName(r.location, locations), clientName(r.client, clients)].filter(Boolean).join(' / ')

  // asset identity (alt name · owner code) — AST reads it off the matched master
  // asset, the Assets tab off the master itself. PV items have no master identity.
  const identLine = (r) => {
    const src = r.source === 'AST' ? (r.matchedAsset || {}) : r.source === 'ASSET' ? r : {}
    return [src.altName, src.ownerCode].filter(Boolean).join(' · ')
  }

  // For an Assets-tab row, which reconciliation record touched it (so a PV-linked
  // master asset is identifiable in the asset list, not just the PV list).
  const refLine = (r) => {
    if (r.source !== 'ASSET') return ''
    if (r.pvRef) return `Linked from PV · ${r.pvRef.tempAssetCode || r.pvRef.pvNo || ''}`.trim()
    if (r.astRef) return `From AST · ${r.astRef.astNo || r.astRef.assetCode || ''}`.trim()
    return ''
  }

  // AST actions per row. An asset the register already marks SOLD cannot be
  // transferred - there is nothing to move - so it offers only UNSOLD (reverse the
  // sale) and IGNORE. Every other row offers the standard set.
  // A DUPLICATE_RECORD is not always a duplicate. The same code scanned twice is
  // one explanation; the other is a wrong code entry - a different physical asset
  // recorded against a code that already exists. That second case is really an
  // unidentified item, so it gets the PV remedies: link it to the asset it
  // actually is, or create a new one for it. IGNORE stays the recommendation
  // because a genuine re-scan is the common case.
  //
  // SOLD is deliberately not offered here: disposing of an asset whose identity is
  // in doubt is the one irreversible mistake this row can make. The underlying
  // asset can still be marked sold from its own, non-duplicate row.
  const astActionsFor = (r) => {
    if (r.reconcileStatus === 'SOLD') return ['UNSOLD', 'IGNORE']
    if (r.reconcileStatus === 'DUPLICATE_RECORD') return ['LINK_ASSET', 'NEW_ASSET', 'ATA_ARN', 'IGNORE']
    return AST_ACTIONS
  }

  // Stage filter, driven by the To review / Reviewed / Processed counters. Split
  // out of passesFilter so the AST status breakdown counts against the same subset
  // the list is showing, and therefore always sums to the selected stage's total.
  const passesWorkflow = (r) => {
    if (statusFilter === 'all') return true
    if (statusFilter === 'review') return awaitingReview(r)      // To review counter
    if (statusFilter === 'reviewed') return inReview(r)          // Reviewed counter
    if (statusFilter === 'processed') return !!r.isProcessed     // Processed counter
    if (statusFilter === 'processing') return isInFlight(r)      // Processing counter
    if (statusFilter === 'failed') return didFail(r)             // Failed counter
    return true
  }

  // Bucket an AST record for the breakdown. groupOf folds MISMATCHED_LOCATION and
  // NOT_FOUND into LOCATION_OFF (location + sub-location are one pair). Records
  // with no status at all land in NO_STATUS rather than being dropped or merged
  // into another bucket.
  const recStatusOf = (r) => r.reconcileStatus ? groupOf(r.reconcileStatus) : NO_STATUS

  const passesFilter = (r) => {
    if (query) {
      const q = query.toLowerCase()
      const hay = `${codeOf(r) || ''} ${nameOf(r) || ''} ${typeLine(r) || ''} ${placeLine(r) || ''} ${r.astNo || r.pvNo || ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    if (r.source === 'ASSET') return true // assets: search only, no status chips
    if (!passesWorkflow(r)) return false
    // AST only: PV records carry no reconcileStatus, so the breakdown never applies.
    if (recStatusFilter && r.source === 'AST' && recStatusOf(r) !== recStatusFilter) return false
    // PV only: the "Best match" column's category (see bestMatchKeyOf).
    if (bestMatchFilter && r.source === 'PV' && bestMatchKeyOf(r) !== bestMatchFilter) return false
    return true
  }

  // Per-reconcileStatus breakdown for the AST tab. Counts respect the selected
  // stage so they sum to it, but ignore the search box. Statuses with a zero count
  // are omitted; unknown codes from the backend are appended rather than dropped.
  const astBreakdown = useMemo(() => {
    if (sourceFilter !== 'AST') return []
    const counts = new Map()
    queue.forEach(r => {
      if (r.source !== 'AST' || !passesWorkflow(r)) return
      const k = recStatusOf(r)
      counts.set(k, (counts.get(k) || 0) + 1)
    })
    const known = AST_STATUS_ORDER.filter(k => counts.has(k))
    const extra = [...counts.keys()].filter(k => !AST_STATUS_ORDER.includes(k))
    return known.concat(extra).map(k => ({ key: k, count: counts.get(k) }))
  }, [queue, sourceFilter, statusFilter])

  // ---------- PV link candidate pool ----------
  // A PV is an item found with no asset code, so its link target must be an asset
  // this audit has NOT otherwise accounted for. Four exclusions, all read from
  // local state so they hold for decisions that are only PENDING SAVE:
  //   1. matched by an AST scan  - it was physically found; a PV cannot also be it
  //   2. already linked by another PV
  //   3. sitting at the SOLD location
  //   4. created by a processed NEW_ASSET of this voucher - it IS the item another
  //      scan / PV recorded, so it is taken. On AAV-1, 23 PV items were linked to
  //      such an asset (Rishabh, 2026-10-27); the backend refuses these links too.
  // The counts are kept so the UI can say what was withheld and why - the pool is
  // narrowed, never silently.

  const linkExclusionsRef = useRef(null)
  const linkExclusions = useMemo(() => {
    const foundInAst = new Set()
      ; (astData || []).forEach(d => {
        if (d.matchedAsset && d.matchedAsset._id) foundInAst.add(String(d.matchedAsset._id))
      })
    // assetId -> the _uid holding it. Duplicate AST rows can link too, so both
    // slices are collected: one asset, one claimant, whoever claimed it.
    const linkedByPv = new Map()
    const claim = (d) => {
      if (d.actionTaken && d.actionTaken.name === 'LINK_ASSET' && d.actionTaken.refRecordId)
        linkedByPv.set(String(d.actionTaken.refRecordId), d._uid)
    }
      ; (pvData || []).forEach(claim)
      ; (astData || []).forEach(claim)
    // Created ids exist only once Process has run the NEW_ASSET (createdIdsOf).
    const createdByAudit = new Set()
    const collectCreated = (d) => {
      if (d.actionTaken && d.actionTaken.name === 'NEW_ASSET') createdIdsOf(d).forEach(id => createdByAudit.add(id))
    }
      ; (pvData || []).forEach(collectCreated)
      ; (astData || []).forEach(collectCreated)
    const soldLoc = (locations || []).find(l => l.name === 'SOLD')
    const soldLocId = soldLoc ? String(soldLoc._id) : null
    // Same content -> same object. Most actions (Ignore, Transfer, New asset...)
    // change astData/pvData without touching what is excluded; keeping the object
    // stable stops the ~11k-asset candidate pool and its indexes from being rebuilt
    // on every such click (it took 3-6 s on AAV-1).
    const sig = [...foundInAst].sort().join(',') + '#' +
      [...linkedByPv].map(([k, v]) => `${k}=${v}`).sort().join(',') + '#' +
      [...createdByAudit].sort().join(',') + '#' + soldLocId
    const prev = linkExclusionsRef.current
    if (prev && prev.sig === sig) return prev
    const next = { foundInAst, linkedByPv, createdByAudit, soldLocId, sig }
    linkExclusionsRef.current = next
    return next
  }, [astData, pvData, locations])

  // Why an asset is not offered, or null when it is. `forUid` keeps a PV's OWN
  // current link visible to itself, so re-opening the picker still shows it -
  // except to an asset this audit created, which no PV may link to.
  const excludedReason = (a, forUid) => {
    const id = String(a._id)
    const { foundInAst, linkedByPv, createdByAudit, soldLocId } = linkExclusions
    if (soldLocId && String(a.location) === soldLocId) return 'sold'
    if (foundInAst.has(id)) return 'ast'
    if (createdByAudit.has(id)) return 'created'
    const holder = linkedByPv.get(id)
    if (holder && holder !== forUid) return 'pv'
    return null
  }

  const selectableAssets = useMemo(
    () => (allAssets || []).filter(a => !excludedReason(a, null)),
    [allAssets, linkExclusions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Candidate rules, most specific first. An asset falls into the FIRST rule it
  // satisfies, so counts never double-count.
  const LINK_RULES = [
    { key: 'mlc', label: 'Model + Location + Sub-location', short: 'Model+Loc+Sub', props: ['assetModel', 'location', 'client'] },
    { key: 'ml', label: 'Model + Location', short: 'Model+Loc', props: ['assetModel', 'location'] },
    { key: 'slc', label: 'Sub-type + Location + Sub-location', short: 'Subtype+Loc+Sub', props: ['equipmentSubType', 'location', 'client'] },
    { key: 'sl', label: 'Sub-type + Location', short: 'Subtype+Loc', props: ['equipmentSubType', 'location'] },
    { key: 'm', label: 'Model, any location', short: 'Model only', props: ['assetModel'] },
    { key: 's', label: 'Sub-type, any location', short: 'Subtype only', props: ['equipmentSubType'] }
  ]
  // An AST record carries no equipment of its own - its model and sub-type come
  // from the asset its scanned code points at. A duplicate row matching for a link
  // therefore reasons about the equipment the code implies, at the place it was
  // actually scanned.
  const linkSubject = (r) => (r && r.source === 'AST')
    ? {
      ...r,
      assetModel: r.matchedAsset && r.matchedAsset.assetModel,
      equipmentSubType: r.matchedAsset && r.matchedAsset.equipmentSubType
    }
    : r

  const ruleKeyOf = (rec, props) => props.map(pr => assetIdOf(rec[pr])).join('|')

  // On-date matching: a LINK candidate is matched by where it was on the PV
  // record's date, not where it is now. movedByPvDate[asOfKey] holds the assets
  // that moved AFTER that date (their pre-move place); others keep current.
  const movedMapFor = (rec) => (rec && rec.asOfKey && movedByPvDate[rec.asOfKey]) || {}
  const onDatePlace = (a, movedMap) => {
    const m = movedMap && movedMap[assetIdOf(a)]
    return { location: m ? m.location : a.location, client: m ? m.client : a.client }
  }
  const ruleKeyOfAsset = (a, props, movedMap) => props.map(pr => {
    if (pr === 'location' || pr === 'client') {
      const m = movedMap && movedMap[assetIdOf(a)]
      return assetIdOf(m ? m[pr] : a[pr])
    }
    return assetIdOf(a[pr])
  }).join('|')

  // Counts per rule over the selectable pool. Built once per distinct PV date so
  // best-match reflects on-date locations without rescanning ~11k assets per row.
  const linkIndex = useMemo(() => {
    const idx = {}
    LINK_RULES.forEach(r => { idx[r.key] = new Map() })
      ; (selectableAssets || []).forEach(a => {
        LINK_RULES.forEach(r => {
          const k = ruleKeyOf(a, r.props)
          idx[r.key].set(k, (idx[r.key].get(k) || 0) + 1)
        })
      })
    return idx
  }, [selectableAssets]) // eslint-disable-line react-hooks/exhaustive-deps

  // Per PV date, only the DIFFERENCE from linkIndex: an asset that moved after
  // that date is counted at its on-date place instead of its current one, so its
  // current key loses 1 and its on-date key gains 1. Assets that did not move
  // count the same on every date. This gives exactly the counts a full rebuild per
  // date would, without rescanning the whole pool once per date (351 dates x
  // ~14,800 assets x 6 rules on AAV-1 - several seconds on every action).
  const linkIndexByDate = useMemo(() => {
    const byId = new Map((selectableAssets || []).map(a => [assetIdOf(a), a]))
    const out = {}
    Object.keys(movedByPvDate || {}).forEach(key => {
      const movedMap = movedByPvDate[key] || {}
      const delta = {}
      LINK_RULES.forEach(r => { delta[r.key] = new Map() })
      Object.keys(movedMap).forEach(id => {
        const a = byId.get(String(id))
        if (!a) return // not in the selectable pool on any date
        LINK_RULES.forEach(r => {
          const before = ruleKeyOf(a, r.props)
          const after = ruleKeyOfAsset(a, r.props, movedMap)
          if (before === after) return
          const d = delta[r.key]
          d.set(before, (d.get(before) || 0) - 1)
          d.set(after, (d.get(after) || 0) + 1)
        })
      })
      out[key] = delta
    })
    return out
  }, [selectableAssets, movedByPvDate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Candidate count for one rule key on a record's PV date: the current-location
  // count plus that date's difference (none when the record has no date / no moves).
  const countFor = (rec, ruleKey, k) => {
    const base = linkIndex[ruleKey].get(k) || 0
    const delta = rec && rec.asOfKey && linkIndexByDate[rec.asOfKey]
    return base + ((delta && delta[ruleKey].get(k)) || 0)
  }

  // The most specific rule that yields at least one candidate. Because the rules
  // are nested (mlc is a subset of ml is a subset of m), the first non-empty one
  // is also the first bucket under "first rule wins", so its raw count is exact.
  const bestMatchFor = (rec) => {
    const r = linkSubject(rec)
    for (const rule of LINK_RULES) {
      const n = countFor(rec, rule.key, ruleKeyOf(r, rule.props))
      if (n > 0) return { key: rule.key, label: rule.label, short: rule.short, count: n }
    }
    return null
  }

  // Full candidate set for one PV record: the assets it may be linked to, bucketed
  // by rule (first rule wins, so nothing is counted twice), plus a breakdown of
  // what was withheld and why. The picker renders both - the pool is narrowed but
  // never silently, so an auditor can see why an asset they expected is absent.
  const pvCandidates = (rec) => {
    if (!rec) return { groups: [], total: 0, excluded: { ast: 0, pv: 0, created: 0, sold: 0, total: 0 } }
    const r = linkSubject(rec)
    const movedMap = movedMapFor(rec)
    const excluded = { ast: 0, pv: 0, created: 0, sold: 0, total: 0 }
    let pool = []
      ; (allAssets || []).forEach(a => {
        const why = excludedReason(a, r._uid)
        if (!why) { pool.push(a); return }
        // Only count an exclusion if the asset could ever have been a candidate for
        // this record - otherwise the number is meaningless noise.
        const isCandidate = LINK_RULES.some(rule => ruleKeyOfAsset(a, rule.props, movedMap) === ruleKeyOf(r, rule.props))
        if (isCandidate) { excluded[why] += 1; excluded.total += 1 }
      })

    const groups = LINK_RULES.map(rule => {
      const key = ruleKeyOf(r, rule.props)
      // Match on where the asset was on the PV date; carry both places for display.
      const assets = pool.filter(a => ruleKeyOfAsset(a, rule.props, movedMap) === key)
        .map(a => { const p = onDatePlace(a, movedMap); return { ...a, _onDateLocation: p.location, _onDateClient: p.client } })
      const ids = new Set(assets.map(a => String(a._id)))
      pool = pool.filter(a => !ids.has(String(a._id)))
      return { key: rule.key, name: rule.label, assets, count: assets.length }
    }).filter(g => g.count > 0)

    return { groups, total: groups.reduce((n, g) => n + g.count, 0), excluded }
  }
  pvCandidatesRef.current = pvCandidates

  // ---------- PV "Best match" filter ----------
  // The category the PV table's "Best match" column shows for a record, so the
  // filter and the column can never disagree: LINKED when the chosen action is
  // LINK_ASSET (saved or pending save), else the most specific rule with a
  // candidate (a LINK_RULES key), else NO_CANDIDATE.
  const BM_LINKED = 'LINKED'
  const BM_NEW = 'NEW_ASSET'      // decided as New asset (Rishabh, 2026-10-09): no match data shown
  const BM_NONE = 'NO_CANDIDATE'
  const bestMatchKeyOf = (r) => {
    if (isDecided(r) && r.actionTaken.name === 'LINK_ASSET') return BM_LINKED
    if (isDecided(r) && r.actionTaken.name === 'NEW_ASSET') return BM_NEW
    const bm = bestMatchFor(r)
    return bm ? bm.key : BM_NONE
  }
  const bestMatchMeta = (key) => {
    if (key === BM_LINKED) return { label: 'Linked', why: 'The action chosen for these PV items is Link to asset.' }
    if (key === BM_NEW) return { label: 'New asset', why: 'The action chosen for these PV items is New asset.' }
    if (key === BM_NONE) return { label: 'No candidate', why: 'No asset still available to link shares the model or sub-type.' }
    const rule = LINK_RULES.find(x => x.key === key)
    return { label: rule.short, why: 'Best match: ' + rule.label }
  }

  // Per-category counts for the PV tab. Like astBreakdown: respects the selected
  // stage, ignores the search box, omits zero counts, ordered Linked -> most
  // specific rule -> No candidate.
  const pvBreakdown = useMemo(() => {
    if (sourceFilter !== 'PV') return []
    const counts = new Map()
    queue.forEach(r => {
      if (r.source !== 'PV' || !passesWorkflow(r)) return
      const k = bestMatchKeyOf(r)
      counts.set(k, (counts.get(k) || 0) + 1)
    })
    // The active category stays visible at 0, so a filter that empties (e.g. after
    // linking its last item) is never applied without a pressed chip to clear it.
    const order = [BM_LINKED, BM_NEW, ...LINK_RULES.map(x => x.key), BM_NONE]
    return order.filter(k => counts.has(k) || k === bestMatchFilter).map(k => ({ key: k, count: counts.get(k) || 0 }))
  }, [queue, sourceFilter, statusFilter, linkIndex, linkIndexByDate, bestMatchFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // The list shows ONLY the active tab's records (no combined view).
  const baseList = isAssets ? assetQueue : queue.filter(r => r.source === sourceFilter)
  const filtered = baseList.filter(passesFilter)
  const current = (isAssets ? assetQueue : queue).find(r => rkey(r) === currentKey) || null

  // Candidates for the open link picker, computed once per render (the picker and
  // the Jev search share it), flattened in priority order for the search.
  const linkCands = pvCandidates(showLink ? linkRecord : null)
  const linkOrder = []
  linkCands.groups.forEach(g => g.assets.forEach(a => linkOrder.push({ asset: a, groupKey: g.key, groupName: g.name })))
  const linkSearch = (linkRecord && jevSearch[pdKeyOf(linkRecord)]) || null

  // Opening the picker does NOT run the AI (Rishabh, 2026-10-09): the AI search runs
  // only in AI link mode. If this item was searched in this session, the picker shows
  // that result (status line, AI % on the checked cards, the match highlighted).

  // ---------- stats ----------
  const total = queue.length
  const decided = queue.filter(isDecided).length
  const pct = total ? Math.round((decided / total) * 100) : 0

  // Review pipeline. Once an action is chosen a record is "reviewed", then moves
  // pending save -> saved -> processed.
  //
  // saveId is the ONLY trustworthy "exists on the server" marker: it is assigned
  // solely from save/process responses. isAlreadySaved used to be set locally the
  // moment an action was picked or bulk-applied, which overstated what was
  // actually saved - and let process() submit records the server had never seen.
  // It is now written only from server responses, and both the counters and
  // process() key off saveId.
  // To review, split by source. queue holds only AST + PV records, so the two
  // sub-counts sum to the tile's headline number.
  const toReview = queue.filter(awaitingReview).length
  const toReviewAst = queue.filter(r => r.source === 'AST' && awaitingReview(r)).length
  const toReviewPv = queue.filter(r => r.source === 'PV' && awaitingReview(r)).length

  const processed = queue.filter(r => r.isProcessed).length
  const processedAst = queue.filter(r => r.source === 'AST' && r.isProcessed).length
  const processedPv = queue.filter(r => r.source === 'PV' && r.isProcessed).length
  const reviewed = queue.filter(inReview).length
  // "Saved" means the server holds exactly what the UI shows - so a record only
  // counts as saved when it has a saveId AND nothing has changed since.
  const reviewedPending = queue.filter(r => inReview(r) && isUnsaved(r)).length
  const reviewedSaved = reviewed - reviewedPending

  // Submitted and still running on the server.
  const processing = queue.filter(isInFlight).length
  const processingAst = queue.filter(r => r.source === 'AST' && isInFlight(r)).length
  const processingPv = queue.filter(r => r.source === 'PV' && isInFlight(r)).length

  // Submitted, came back, and did NOT succeed. These carry the server's reason in
  // processedRemark and need the user to act again, so they get their own counter
  // instead of being buried among saved decisions.
  const failedN = queue.filter(didFail).length
  const failedAst = queue.filter(r => r.source === 'AST' && didFail(r)).length
  const failedPv = queue.filter(r => r.source === 'PV' && didFail(r)).length

  // The counters double as the queue's stage filter, replacing the old chip
  // row. Clicking the active one clears it back to showing everything.
  const toggleStage = (stage) => {
    setStatusFilter(prev => (prev === stage ? 'all' : stage))
    setCurrentKey(null)
  }

  // ---------- unsaved-work warning ----------
  // A decision lives only in component state until Save writes it to the server.
  // The user is WARNED, never blocked: a standing banner while anything is
  // pending, plus the browser's own dialog on tab close/reload. In-app navigation
  // is deliberately NOT blocked - a router <Prompt> intercepts every history
  // change, which made ordinary in-app actions pop a confirm.
  //
  // The one confirmation kept is Generate, because that specific click reloads
  // the voucher and would definitely destroy pending decisions.
  // Assets-tab decisions changed here and not yet saved (incl. a cleared saved one).
  const assetUnsaved = assetQueue.filter(r => r.assetStatus === 'missing' && !r.isProcessed && r.dirty).length
  const unsavedTotal = reviewedPending + assetUnsaved
  const hasUnsaved = unsavedTotal > 0
  const unsavedMsg = (n) => `${n} reviewed record${n === 1 ? '' : 's'} ${n === 1 ? 'has' : 'have'} not been saved yet. Leaving now discards ${n === 1 ? 'it' : 'them'}. Save first?`

  useEffect(() => {
    if (!hasUnsaved) return
    const warn = (e) => {
      // Browsers ignore custom text now, but returnValue must be set to trigger
      // the native "leave site?" dialog.
      e.preventDefault()
      e.returnValue = unsavedMsg(unsavedTotal)
      return e.returnValue
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsaved, unsavedTotal])

  // ---------- background processing ----------
  // Process is fire-and-forget on the server: records come back isProcessed:false
  // with processedRemark 'Processing...', and the outcome only appears on a later
  // reload. Without something watching, the user is told "it is running" once and
  // never told it finished.
  //
  // A batch is tracked by saveId - the server-assigned action id. _uid is a
  // load-order index and _id is not unique for PV, so neither survives a reload;
  // saveId does, and every record that can be processed has one.
  //
  // NOTE: this rechecks by re-running the full reconcile call, which is the only
  // thing the API offers. A lightweight GET .../process-status/{voucher} returning
  // {inFlight, done, failed} would replace `recheck` and the interval below and
  // leave the rest of this untouched.
  // PROCESSING_REMARK, isInFlight and didFail are declared with the other stage
  // predicates above, because the counters depend on them.
  const RECHECK_MS = 60000

  // Details for assets that will be CREATED from a PV item. Keyed by record _uid:
  // { fields: {...}, prov: { field: 'group:Foot light' | 'individual' } }.
  // Provenance is kept so a group apply can warn before overwriting a value that
  // was set on one asset deliberately.
  const [assetDetails, setAssetDetails] = useState({})
  assetDetailsRef.current = assetDetails
  const [detailsFor, setDetailsFor] = useState(null)   // records the modal is open on

  const applyDetails = (targets, fields, prov) => {
    setAssetDetails(prev => {
      const next = { ...prev }
      targets.forEach(r => {
        const cur = next[r._uid] || { fields: {}, prov: {} }
        const f = { ...cur.fields }, pv = { ...cur.prov }
        Object.keys(fields).forEach(k => {
          const v = fields[k]
          // An explicitly cleared field is REMOVED rather than stored as null, so
          // "not set" and "set to nothing" cannot drift apart.
          if (v === null || v === '') { delete f[k]; delete pv[k] }
          else { f[k] = v; pv[k] = prov }
        })
        next[r._uid] = { fields: f, prov: pv }
      })
      return next
    })
    // The decision itself changes, so it needs saving again. AST duplicates made
    // new assets use the same form, so their rows are marked too.
    setPvData(prev => prev.map(d => targets.some(t => t._uid === d._uid) ? { ...d, dirty: true } : d))
    setAstData(prev => prev.map(d => targets.some(t => t._uid === d._uid) ? { ...d, dirty: true } : d))
  }

  // A record that will CREATE an asset needs details; anything else does not.
  const needsDetails = (r) => !!(r.actionTaken && r.actionTaken.name === 'NEW_ASSET' && !r.isProcessed)
  const detailsReady = (r) => isReady(assetDetails, r)
  const detailsMissing = (r) => missingFor(assetDetails, r)

  // Every new-asset record still short a required field. Save is allowed - a
  // half-filled decision is worth keeping - but Process is not, because that is
  // the step that actually creates the asset.
  const newAssetsIncomplete = queue.filter(r => needsDetails(r) && !detailsReady(r)).length

  const [batch, setBatch] = useState(null)   // { ids: Set<saveId>, at, notified }
  const [lastChecked, setLastChecked] = useState(null)
  const [checking, setChecking] = useState(false)

  // The Process run in this tab: { parts, sent, current, failures: [{ label, error }], finished }.
  // runningRef is the synchronous guard against a second click before state updates.
  const [run, setRun] = useState(null)
  const runningRef = useRef(false)
  const runActive = !!(run && !run.finished)

  // Leaving mid-run stops the parts not yet sent (the server finishes the one it has).
  useEffect(() => {
    if (!runActive) return
    const warn = (e) => { e.preventDefault(); e.returnValue = 'Processing is still sending records to the server.'; return e.returnValue }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [runActive])

  const batchStats = useMemo(() => {
    if (!batch || !batch.ids.size) return null
    const rows = queue.filter(r => r.saveId && batch.ids.has(String(r.saveId)))
    return {
      total: batch.ids.size,
      running: rows.filter(isInFlight).length,
      done: rows.filter(r => r.isProcessed).length,
      failed: rows.filter(didFail).length,
      // Records we submitted but can no longer see (filtered out, or beyond the
      // reload). Reported rather than quietly folded into "done".
      unaccounted: batch.ids.size - rows.length
    }
  }, [batch, queue]) // eslint-disable-line react-hooks/exhaustive-deps

  // Every reconcile response is a fresh look at the batch.
  useEffect(() => {
    if (props.reconciliationAssets) { setLastChecked(Date.now()); setChecking(false) }
  }, [props.reconciliationAssets])

  // The save/process thunks catch their own errors and put the message in
  // common.error, dispatching no success action - so a rejected Process call
  // otherwise leaves the cockpit showing nothing at all. Surface it against the
  // batch, which is the only place the user is looking after clicking Process.
  useEffect(() => {
    if (!props.commonError || !batch || batch.err) return
    const text = typeof props.commonError === 'string'
      ? props.commonError
      : (props.commonError.message || 'The server rejected the request.')
    setBatch(b => (b ? { ...b, err: text, notified: true } : b))
    message.error({ content: `Processing was not accepted — ${text}`, duration: 10 })
  }, [props.commonError]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recheck on a timer while anything is still running - but NEVER while there
  // are unsaved decisions, because the reload would discard them, and not while
  // this tab is still sending parts (a reload would drop their Processing marks).
  useEffect(() => {
    if (!batchStats || batchStats.running === 0 || hasUnsaved || !voucher || runActive) return
    const id = setInterval(() => { setChecking(true); runFetch() }, RECHECK_MS)
    return () => clearInterval(id)
  }, [batchStats && batchStats.running, hasUnsaved, voucher, runActive]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tell the user once, when the batch actually lands.
  useEffect(() => {
    if (!batch || batch.notified || batch.err || !batchStats || batchStats.running > 0 || runActive) return
    const { done, failed, unaccounted } = batchStats
    const line = `${done} processed${failed ? `, ${failed} failed` : ''}${unaccounted ? `, ${unaccounted} not visible in the current filter` : ''}.`
    if (failed) message.warning({ content: `Processing finished — ${line}`, duration: 8 })
    else message.success({ content: `Processing finished — ${line}`, duration: 6 })
    // Reaches the user even if they moved to another tab.
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const n = new Notification('Reconciliation processing finished', { body: line })
        n.onclick = () => { window.focus(); n.close() }
      }
    } catch (e) { /* notifications are a convenience; never block on them */ }
    setBatch(b => (b ? { ...b, notified: true } : b))
  }, [batchStats, batch, runActive])

  const ringC = 2 * Math.PI * 19
  const autoN = astData.filter(r => r.reconcileStatus === 'FULLY_MATCHED' && !r.isProcessed && !isDecided(r)).length

  // ---------- mutations ----------
  // `dirty` marks a decision the user has changed locally since the last server
  // round-trip. Without it, changing the action on a record that already carries a
  // saveId would be counted as "saved" even though the new action has not been
  // sent anywhere. Cleared on every save/process sync.
  const setAstAction = (uid, actionTaken) =>
    setAstData(prev => prev.map(d => d._uid === uid ? { ...d, actionTaken, dirty: true } : d))

  const setPvAction = (uid, actionTaken, actionRecord = null) =>
    setPvData(prev => prev.map(d => d._uid === uid ? { ...d, actionTaken, actionRecord, dirty: true } : d))

  const clearAction = (r) => {
    // A processed record has already executed against master data — clearing it
    // locally would only create an inconsistent state, so it's not allowed.
    if (r.isProcessed) { message.info('This record is already processed — its action is final.'); return }
    const clearOne = (d, isPv) => {
      const c = { ...d }
      delete c.actionTaken
      if (isPv) c.actionRecord = null
      if (c.saveId) {
        // Saved on the server: the clear is a change that must itself be saved.
        c.clearPending = true
      } else {
        c.clearPending = false
        c.isAlreadySaved = false
      }
      return c
    }
    if (r.source === 'AST') {
      setAstData(prev => prev.map(d => d._uid === r._uid ? clearOne(d, false) : d))
    } else {
      setPvData(prev => prev.map(d => d._uid === r._uid ? clearOne(d, true) : d))
    }
  }

  const advanceAfter = (actedKey) => {
    // Only in the "To review" stage does acting on a record remove it from the
    // list, so that is the only stage where we need to jump to the next one.
    if (statusFilter !== 'review') return
    const idx = filtered.findIndex(r => rkey(r) === actedKey)
    for (let i = idx + 1; i < filtered.length; i++) {
      if (!isDecided(filtered[i]) && rkey(filtered[i]) !== actedKey) { setCurrentKey(rkey(filtered[i])); return }
    }
  }

  // askOwner is gone: owner is one field among many now, collected by
  // NewAssetDetails along with classification, condition and commercials.

  // pick an action on the *current* record
  const pickAction = (r, name) => {
    if (!r || r.isProcessed || r.source === 'ASSET') return
    const key = rkey(r)
    if (r.source === 'AST') {
      // A duplicate that turns out to be a wrong code entry takes the same two
      // remedies a PV item does, through the same picker.
      if (name === 'LINK_ASSET') { setLinkRecord(r); setLinkTab(null); setShowLink(true); return }
      if (name === 'NEW_ASSET') {
        // A duplicate becoming its own asset needs the same details as a PV item.
        setAstAction(r._uid, { name: 'NEW_ASSET' })
        setDetailsFor([r])
        return
      }
      const refRecordId = ['RECONCILED', 'ATA_ARN', 'SOLD', 'UNSOLD'].includes(name) ? (r.matchedAsset && r.matchedAsset._id) : null
      setAstAction(r._uid, { name, refRecordId })
      message.success(`${ACT[name].verb ? 'You ' + ACT[name].verb : 'Set'} ${codeOf(r)}`)
      advanceAfter(key)
    } else {
      if (name === 'LINK_ASSET') { setLinkRecord(r); setLinkTab(null); setShowLink(true); return }
      if (name === 'NEW_ASSET') {
        setPvAction(r._uid, { name: 'NEW_ASSET' })
        setDetailsFor([r])
        return
      }
      setPvAction(r._uid, { name })
      message.success(`${ACT[name].verb ? 'You ' + ACT[name].verb : 'Set'} ${codeOf(r)}`)
      advanceAfter(key)
    }
  }

  // callback from the reused PV link modal (preserves candidate ordering)
  const handleRecordAction = (asset, willOverwrite, action) => {
    if (!asset || !action || !linkRecord) return
    const at = {
      name: 'LINK_ASSET', refRecordId: asset._id, refRecordSubId: asset.assetCode, isPvOverwrite: willOverwrite
    }
    // The picker now serves duplicate AST rows as well as PV items, so the
    // decision has to land in the slice the record came from.
    if (linkRecord.source === 'AST') setAstAction(linkRecord._uid, at)
    else setPvAction(linkRecord._uid, at, asset)
    message.success(`${codeOf(linkRecord)} linked to ${asset.assetCode}`)
  }
  // Closing the picker stops a running Jev search (it can be continued on reopen).
  const hideLink = () => { jevSearchStop(linkRecord); setShowLink(false); setLinkRecord(null) }

  // ---------- selection + bulk ----------
  const toggleSel = (r) => {
    const k = rkey(r)
    setSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n })
  }
  const selectAllFiltered = () => setSelected(new Set(filtered.filter(r => !r.isProcessed).map(rkey)))
  const clearSel = () => setSelected(new Set())

  const selectedRecords = queue.filter(r => selected.has(rkey(r)) && !r.isProcessed)
  const hasAst = selectedRecords.some(r => r.source === 'AST')
  const hasPv = selectedRecords.some(r => r.source === 'PV')
  // Bulk-applicable actions. LINK_ASSET is never bulk (needs a specific asset).
  // AST no longer has NEW_ASSET; PV can bulk NEW_ASSET / RECONCILED / IGNORE.
  const AST_BULK = ['ATA_ARN', 'SOLD', 'UNSOLD', 'IGNORE']
  const PV_BULK = ['NEW_ASSET', 'IGNORE']
  const bulkOptions = (hasAst && hasPv) ? ['IGNORE'] : hasPv ? PV_BULK : hasAst ? AST_BULK : []
  const clearableN = selectedRecords.filter(isDecided).length

  // Mirrors astActionsFor for bulk: SOLD only applies to a not-yet-sold row,
  // UNSOLD only to a currently-sold row, and a register-sold asset cannot be
  // transferred. Ineligible rows are skipped and reported in the toast.
  const astBulkEligible = (d, name) => {
    if (name === 'UNSOLD') return d.reconcileStatus === 'SOLD'
    if (name === 'SOLD') return d.reconcileStatus !== 'SOLD'
    if (name === 'ATA_ARN') return d.reconcileStatus !== 'SOLD'
    return true
  }

  const applyBulk = (name, owner) => {
    const applied =
      astData.filter(d => selected.has(d._uid) && !d.isProcessed && AST_BULK.includes(name) && astBulkEligible(d, name)).length +
      pvData.filter(d => selected.has(d._uid) && !d.isProcessed && PV_BULK.includes(name)).length

    setAstData(prev => prev.map(d => {
      if (!selected.has(d._uid) || d.isProcessed || !AST_BULK.includes(name) || !astBulkEligible(d, name)) return d
      const refRecordId = ['RECONCILED', 'ATA_ARN', 'SOLD', 'UNSOLD'].includes(name) ? (d.matchedAsset && d.matchedAsset._id) : null
      return { ...d, actionTaken: { name, refRecordId }, dirty: true }
    }))
    setPvData(prev => prev.map(d => {
      if (!selected.has(d._uid) || d.isProcessed || !PV_BULK.includes(name)) return d
      const at = name === 'NEW_ASSET' ? { name, newAssetOwner: owner } : { name }
      // NOT isAlreadySaved - nothing has been sent yet. saveId, set from the
      // save response, is the only marker that the server holds this.
      return { ...d, actionTaken: at, actionRecord: null, dirty: true }
    }))
    const skipped = selectedRecords.length - applied
    message.success(`${ACT[name].label} applied to ${applied} record${applied === 1 ? '' : 's'}${skipped > 0 ? ` — ${skipped} skipped (not applicable)` : ''}.`)
    clearSel()
  }

  // Bulk clear of DECISIONS - distinct from clearSel(), which only unchecks the
  // boxes. Mirrors the per-record Clear exactly, so a record returns to
  // "To review". selectedRecords already excludes processed records, whose action
  // has executed against master data and cannot be taken back here.
  const bulkClear = () => {
    const targets = selectedRecords.filter(isDecided)
    if (!targets.length) return message.info('Nothing to clear — no decisions in the selection.')
    const uids = new Set(targets.map(r => r._uid))
    // Same rule as the per-record Clear: a saved decision becomes a pending clear
    // that still has to be saved; an unsaved one just disappears.
    const clearRow = (d, isPv) => {
      const c = { ...d }
      delete c.actionTaken
      if (isPv) c.actionRecord = null
      if (c.saveId) { c.clearPending = true } else { c.clearPending = false; c.isAlreadySaved = false }
      return c
    }
    setAstData(prev => prev.map(d => uids.has(d._uid) ? clearRow(d, false) : d))
    setPvData(prev => prev.map(d => uids.has(d._uid) ? clearRow(d, true) : d))
    const needSaving = targets.filter(r => r.saveId).length
    const skipped = selectedRecords.length - targets.length
    message.success(`Cleared ${targets.length} decision${targets.length === 1 ? '' : 's'}${needSaving ? ` — ${needSaving} already saved, hit Save to apply the clear` : ''}${skipped > 0 ? ` — ${skipped} had none` : ''}.`)
    clearSel()
  }

  const bulkPick = (name) => {
    if (!selectedRecords.length) return
    if (name === 'LINK_ASSET') return // never bulk
    if (name === 'NEW_ASSET' && hasPv) {
      // Set the action first so the rows show as decided, then open the details
      // workspace on them. Owner is one of the fields it collects, so the old
      // owner-only prompt is gone.
      const pv = selectedRecords.filter(r => r.source === 'PV')
      applyBulk(name)
      setDetailsFor(pv)
      return
    }
    applyBulk(name)
  }

  // ---------- save / process / export ----------
  // The whole voucher, narrowed only by the voucher's own audit scope; no status filter
  const runFetch = () => {
    if (!voucher) return
    const s = voucherScope
    props.getAssetsByAuditVoucher(voucher, s.location, s.client, null, s.assetType, s.equipmentSubType, s.assetModel)
  }

  // Take the audit scope stored on the chosen voucher. The voucher document carries
  // these as ids (or populated refs); take the id of whichever shape arrives. Every
  // field is set on each selection - to the voucher's value, or null when it has
  // none - so switching vouchers never keeps the previous one's scope.
  const idOf = (v) => (v && (v._id || v)) || null
  const selectVoucher = (v, doc) => {
    if (!v) return
    if (!doc) {
      // Its scope cannot be read, and loading it without the scope would list the
      // whole register as missing for a scoped audit - so it is not selected.
      setVoucher(null)
      setVoucherScope(NO_SCOPE)
      return message.error('Could not read this audit voucher. Search for it again and re-select it.')
    }
    const scope = {}
    const names = {}
    Object.keys(NO_SCOPE).forEach(k => {
      scope[k] = idOf(doc[k])
      names[k] = doc[k] && doc[k].name ? doc[k].name : null
    })
    setVoucher(v)
    // voucherNo rides along for the report masthead and Export (the search results
    // it was picked from are not kept, so the id used to print there instead).
    setVoucherScope({ ...scope, names, voucherNo: doc.voucherNo || null })
  }

  // Read-only scope line under the voucher box. Location / sub-location names come
  // from the user's lists; others only when the voucher arrived populated.
  const scopeChips = Object.keys(NO_SCOPE).filter(k => voucherScope[k]).map(k => {
    const id = voucherScope[k]
    const name = (voucherScope.names && voucherScope.names[k])
      || (k === 'location' ? locName(id, locations) : k === 'client' ? clientName(id, clients) : null)
    return { key: k, label: SCOPE_LABELS[k], name: name || 'set on the voucher (name not loaded)' }
  })

  // The New asset form's 15-minute list cache is kept per organisation + user
  const mastersCacheKey = (() => {
    const u = props.authUser || {}
    const org = u.organisation && (u.organisation._id || u.organisation)
    return org && u._id ? `${org}|${u._id}` : null
  })()

  // Deselect the voucher (the X on the AAV box)
  const handleVoucherClear = () => {
    setVoucher(null)
    setVoucherScope(NO_SCOPE)
  }

  // Generate replaces astData/pvData wholesale, which would drop any decision
  // that has not been saved. Confirm before discarding.
  const fetchAssets = () => {
    if (!voucher) return
    if (!hasUnsaved) return runFetch()
    Modal.confirm({
      title: `Discard ${unsavedTotal} unsaved decision${unsavedTotal === 1 ? '' : 's'}?`,
      content: 'Re-running Generate reloads the voucher from the server and these decisions will be lost. Cancel and hit Save to keep them.',
      okText: 'Discard and reload',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: runFetch
    })
  }

  // Rows to send to save-action: anything decided, plus any pending clear. A
  // pending clear goes up as the same record with actionTaken nulled - clearing is
  // an action like any other, and the server needs to be told, otherwise it keeps
  // the decision it was given earlier.
  // The details collected for a NEW_ASSET ride along on the action, so the server
  // creates the asset classified and priced rather than as an unclassified shell.
  // Only fields that were actually set are sent - an absent key means "not set",
  // never "set to nothing".
  const withDetails = (d) => {
    if (!needsDetails(d)) return d
    const held = assetDetails[d._uid]
    const fields = (held && held.fields) || {}
    // Seed a base from EVERY detail the scan already knows (classification,
    // condition, custodian, utilisation, serial/existing/parent codes, qty,
    // countable...), so a NEW_ASSET saved without ever opening the details form
    // still stores the PV's own values in actionTaken.newAssetDetails - exactly
    // as "set on this asset" does. Anything picked in the form overrides the base.
    const base = {}
    DETAIL_FIELDS.forEach(f => {
      const v = scanValue(d, f)
      if (v !== undefined && v !== null && v !== '') base[f] = v
    })
    const merged = { ...base, ...fields }
    if (!Object.keys(merged).length) return d
    return {
      ...d,
      actionTaken: {
        ...d.actionTaken,
        // newAssetOwner is kept as its own key because that is what the server
        // already reads; the rest arrive together under newAssetDetails.
        newAssetOwner: merged.ownedBy || (d.actionTaken && d.actionTaken.newAssetOwner) || undefined,
        newAssetDetails: merged
      }
    }
  }

  const savePayloadFor = (rows) => rows
    .filter(d => !d.isProcessed && (isDecided(d) || hasPendingClear(d)))
    .map(d => hasPendingClear(d) ? { ...d, actionTaken: null } : withDetails(d))

  const save = () => {
    const astPayload = savePayloadFor(astData)
    const pvPayload = savePayloadFor(pvData)
    const assetPayload = assetSaveRows().map(assetRowPayload)
    const n = astPayload.length + pvPayload.length + assetPayload.length

    const clears = astData.concat(pvData).filter(hasPendingClear).length
      + assetPayload.filter(d => d.actionTaken === null).length
    if (!n) return message.info('No pending decisions to save.')
    Modal.confirm({
      title: `Save ${n} decision${n === 1 ? '' : 's'}?`,
      content: (
        <div>
          {astPayload.length} AST · {pvPayload.length} PV · {assetPayload.length} Asset
          {clears > 0 && <div style={{ marginTop: 6 }}>Includes {clears} cleared decision{clears === 1 ? '' : 's'}, which will be removed on the server.</div>}
        </div>
      ),
      onOk: () => {
        message.info(`Saving ${n} decision${n === 1 ? '' : 's'}…`)
        if (astPayload.length) props.saveReconciliationData(astPayload, voucher, 'ast')
        if (pvPayload.length) props.saveReconciliationData(pvPayload, voucher, 'pv')
        if (assetPayload.length) props.saveReconciliationData(assetPayload, voucher, 'asset')
      }
    })
  }

  // Only a record the SERVER holds can be processed, and saveId is the only proof
  // of that. This used to trust isAlreadySaved for AST, which the UI sets locally
  // the moment an action is picked - so a bulk action followed straight by Process
  // submitted records the server had never been told about, and the whole batch
  // failed. PV was already guarded on saveId; AST now is too.
  const readyToProcess = (d) => !!(d.actionTaken && d.actionTaken.name && d.saveId && !d.isProcessed && !isInFlight(d))

  // "Mark missing" is processed by raising an Asset Condition Voucher with the
  // org's reserved "Missing" condition. The server does not require a photo or geo
  // location for these vouchers, whatever the condition's flags (nobody can photograph
  // or locate an asset that was not found), so the only thing that can stop all of them
  // is the condition not being
  // set up at all - checked here before anything is sent. One GET, only when the
  // batch contains a MISSING decision.
  const checkMissingCondition = async () => {
    try {
      const res = await axios.get('/asset-condition-voucher/reserved-conditions')
      const list = Array.isArray(res.data) ? res.data : []
      const m = list.find(c => String((c && c.name) || '').trim().toLowerCase() === 'missing')
      if (!m) return { block: "The reserved condition 'Missing' is not set up for this organisation, so no Asset Condition Voucher can be raised." }
      return {}
    } catch (e) {
      return { warn: 'Could not read the asset conditions to check this in advance. The server still checks every record and reports any failure in its remark.' }
    }
  }

  // Sends the records in parts of PROCESS_PART_SIZE, one request at a time: AST,
  // then PV, then Assets (the server processes an Assets decision only once every PV
  // decision is processed). One request for a whole source used to run for minutes,
  // past the ~60 s proxy timeout, while the server carried on - which invited a
  // second click. The server now also refuses to act twice on a record, so an
  // overlapping run from another tab is skipped there, not duplicated.
  //
  // Every AST/PV record of the run shows as Processing until its part's response
  // lands. A part that fails stays Processing - the server may still have done it -
  // and is listed in the status bar; the usual re-check then reloads the truth.
  const PROCESS_PART_SIZE = 25
  const runProcess = async (groups) => {
    if (runningRef.current) return
    runningRef.current = true
    const parts = []
    groups.forEach(([src, rows]) => {
      for (let i = 0; i < rows.length; i += PROCESS_PART_SIZE) {
        const slice = rows.slice(i, i + PROCESS_PART_SIZE)
        parts.push({ src, rows: slice, label: `${src} ${i + 1}–${i + slice.length} of ${rows.length}` })
      }
    })
    const queued = new Set(groups.filter(([src]) => src !== 'ASSET')
      .reduce((all, [, rows]) => all.concat(rows), []).map(d => String(d.saveId)))
    const markQueued = d => (d.saveId && queued.has(String(d.saveId)) && !d.isProcessed) ? { ...d, processedRemark: PROCESSING_REMARK } : d
    setAstData(prev => prev.map(markQueued))
    setPvData(prev => prev.map(markQueued))
    // Assets-tab rows live in the assetDecisions overlay, keyed by asset id.
    const queuedAssets = groups.filter(([src]) => src === 'ASSET').reduce((all, [, rows]) => all.concat(rows), []).map(d => String(d._id))
    if (queuedAssets.length) {
      setAssetDecisions(prev => {
        const next = { ...prev }
        queuedAssets.forEach(id => { if (next[id] && !next[id].isProcessed) next[id] = { ...next[id], processedRemark: PROCESSING_REMARK } })
        return next
      })
    }

    const failures = []
    setRun({ parts: parts.length, sent: 0, current: parts.length ? parts[0].label : null, failures: [], finished: false })
    try {
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        setRun(r => ({ ...r, current: part.label }))
        // Copies: the thunk stamps source/voucher onto each row it sends.
        const res = await props.processReconciliationData(part.rows.map(d => ({ ...d })), voucher, part.src, { quiet: true })
        if (!res || !res.ok) failures.push({ label: part.label, error: (res && res.error) || 'no response' })
        setRun(r => ({ ...r, sent: i + 1, failures: [...failures] }))
      }
    } finally {
      runningRef.current = false
      setRun(r => ({ ...r, current: null, finished: true }))
    }
    if (failures.length) {
      message.error({
        content: `${failures.length} of ${parts.length} part${parts.length === 1 ? '' : 's'} failed to process — see the status bar. Their records may still have been processed; the page re-checks.`,
        duration: 10
      })
    }
  }

  // ---------- Process: save pending changes first ----------
  // The server processes the decision it HOLDS (the saved record), never the one on
  // screen. So when there are unsaved changes - a decision never saved, a saved one
  // changed since (dirty) or cleared - Process saves exactly those first, waits for the
  // server's answer, and then processes what the server holds. If a save fails nothing
  // is processed. Rows that are mid-Process are left alone (the server skips them).
  const pendingSaveRows = () => ({
    ast: astData.filter(d => isUnsaved(d) && !isInFlight(d)),
    pv: pvData.filter(d => isUnsaved(d) && !isInFlight(d)),
    asset: assetQueue.filter(r => r.assetStatus === 'missing' && r._id && !r.isProcessed && r.dirty && !isInFlight(r))
  })
  // How a saved record finds its row - the same matching as the after-SAVE sync effects.
  const SAVED_KEY = {
    ast: { row: d => String(d._id), saved: s => (s.astAsset && s.astAsset.assetRecordId) ? String(s.astAsset.assetRecordId) : null },
    // Older PV details share the PV's _id, so the temp code is part of the key.
    pv: {
      row: d => `${d._id}|${d.tempAssetCode || ''}`,
      saved: s => (s.pvAsset && s.pvAsset.assetRecordId) ? `${s.pvAsset.assetRecordId}|${s.pvAsset.tempCode || ''}` : null
    },
    asset: { row: r => String(r._id), saved: s => (s.assetAsset && s.assetAsset.assetRecordId) ? String(s.assetAsset.assetRecordId) : null }
  }
  // Rows with the server's answer to their save applied (what the sync effects do to state).
  const applySaved = (rows, saved, src) => {
    const byKey = new Map((saved || []).map(s => [SAVED_KEY[src].saved(s), s]))
    return rows.map(r => {
      const s = byKey.get(SAVED_KEY[src].row(r))
      return s ? { ...r, saveId: s._id, isAlreadySaved: true, actionTaken: s.actionTaken, clearPending: false, dirty: false } : r
    })
  }
  // Decided rows Process will act on once pending changes are saved.
  const willProcess = (d) => isDecided(d) && !d.isProcessed && !isInFlight(d)
  const willProcessAsset = (r) => r.assetStatus === 'missing' && r._id && willProcess(r)

  const process = async () => {
    if (runningRef.current) return message.info('Processing is already running in this tab — wait for it to finish.')
    const pending = pendingSaveRows()
    const toSave = pending.ast.length + pending.pv.length + pending.asset.length
    const astCount = astData.filter(willProcess).length
    const pvCount = pvData.filter(willProcess).length
    const assetEst = assetQueue.filter(willProcessAsset)
    const n = astCount + pvCount + assetEst.length

    // Processing a NEW_ASSET creates a register entry. One short of a required
    // field would arrive owner-less or unpriced, so it is stopped here rather
    // than created and corrected later.
    //
    // Checked across every pending new-asset decision, not just the saved ones:
    // missing details are what the user has to fix FIRST, and reporting "nothing
    // has been saved yet" instead would send them down the wrong path.
    const short = astData.concat(pvData).filter(d => needsDetails(d) && !detailsReady(d))
    if (short.length) {
      const byField = {}
      short.forEach(d => detailsMissing(d).forEach(f => { byField[f] = (byField[f] || 0) + 1 }))
      const LBL = { ownedBy: 'an owner', rate: 'an invoice value' }
      return Modal.warning({
        title: `${short.length} new asset${short.length === 1 ? '' : 's'} missing required details`,
        content: (
          <div>
            {REQUIRED.filter(f => byField[f]).map(f => (
              <div key={f}>{byField[f]} need {LBL[f] || f}</div>
            ))}
            <div style={{ marginTop: 8 }}>
              Select them in the AST or PV tab and choose <b>New asset</b> to fill the details, then Process.
            </div>
          </div>
        ),
        okText: 'Got it'
      })
    }

    if (!n) {
      if (toSave) return message.info(`Nothing to process. Your ${toSave} unsaved change${toSave === 1 ? ' only clears a decision' : 's only clear decisions'} — use Save.`)
      return message.info('No saved-but-unprocessed decisions to process.')
    }
    const missingN = assetEst.filter(r => r.actionTaken && r.actionTaken.name === 'MISSING').length
    let acvWarn = null
    if (missingN) {
      const chk = await checkMissingCondition()
      if (chk.block) return Modal.warning({ title: 'Asset Condition Vouchers cannot be raised', content: chk.block, okText: 'Got it' })
      acvWarn = chk.warn || null
    }
    Modal.confirm({
      title: toSave
        ? `Save ${toSave} change${toSave === 1 ? '' : 's'} and process ${n} decision${n === 1 ? '' : 's'}?`
        : `Process ${n} decision${n === 1 ? '' : 's'}?`,
      content: (
        <div>
          This creates transfers / sale orders / new assets for {astCount} AST · {pvCount} PV · {assetEst.length} Asset.
          {missingN > 0 && (
            <div style={{ marginTop: 8 }}>
              <b className="tnum">{missingN}</b> asset{missingN === 1 ? '' : 's'} marked missing: one Asset Condition Voucher each,
              condition <b>Missing</b>, remark naming this audit voucher.
              {acvWarn && <div style={{ marginTop: 4, color: '#d46b08' }}>{acvWarn}</div>}
            </div>
          )}
          <br />
          The outcome — the <b>ATA/ARN, SI or PO number</b>, or an <b>error</b> — appears in each record's <b>Remark</b>.
          They move to <b>Processing</b> while the server works, then to <b>Processed</b>.
          {toSave > 0 && (
            <div style={{ marginTop: 10 }}>
              <b className="tnum">{toSave}</b> unsaved change{toSave === 1 ? '' : 's'} ({pending.ast.length} AST · {pending.pv.length} PV · {pending.asset.length} Asset)
              {' '}will be <b>saved first</b>, and Process then carries out the saved decisions. If the save fails, nothing is processed.
            </div>
          )}
        </div>
      ),
      okText: toSave ? 'Save and process' : 'Process now',
      onOk: () => {
        if (runningRef.current) return
        // Ask for notification permission on this click - a user gesture - rather
        // than unprompted on load (and before the save, which ends the gesture).
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission()
        } catch (e) { /* optional */ }
        saveThenProcess(pending)
      }
    })
  }

  const saveThenProcess = async (pending) => {
    if (runningRef.current) return
    // Hold the run lock while saving, so a second click cannot start another run;
    // runProcess takes the lock over below.
    runningRef.current = true
    const saved = { ast: [], pv: [], asset: [] }
    const toSave = pending.ast.length + pending.pv.length + pending.asset.length
    try {
      const sends = [
        ['ast', savePayloadFor(pending.ast)],
        ['pv', savePayloadFor(pending.pv)],
        ['asset', pending.asset.map(assetRowPayload)]
      ].filter(([, payload]) => payload.length)
      if (sends.length) message.info(`Saving ${toSave} change${toSave === 1 ? '' : 's'} before processing…`)
      for (const [src, payload] of sends) {
        const res = await props.saveReconciliationData(payload, voucher, src)
        if (!res || !res.ok) {
          Modal.error({
            title: 'Save failed — nothing was processed',
            content: `${src.toUpperCase()}: ${(res && res.error) || 'no response'}. Check the decisions and click Process again.`
          })
          return
        }
        saved[src] = Array.isArray(res.data) ? res.data : []
      }
    } finally {
      runningRef.current = false
    }

    // What the server now holds: rows not saved just now are unchanged; a pending row the
    // server did not save (processed or being processed meanwhile) stays dirty and is
    // left out, so its unsaved decision is never processed as the old saved one.
    const astPayload = applySaved(astData, saved.ast, 'ast').filter(d => readyToProcess(d) && !d.dirty)
    const pvPayload = applySaved(pvData, saved.pv, 'pv').filter(d => readyToProcess(d) && !d.dirty)
    const assetPayload = applySaved(assetQueue, saved.asset, 'asset')
      .filter(r => willProcessAsset(r) && r.saveId && !r.dirty).map(assetRowPayload)
    const n = astPayload.length + pvPayload.length + assetPayload.length

    const notSaved = toSave - (saved.ast.length + saved.pv.length + saved.asset.length)
    if (notSaved > 0) {
      message.warning({
        content: `${notSaved} change${notSaved === 1 ? ' was' : 's were'} not saved by the server (already processed or being processed) and ${notSaved === 1 ? 'is' : 'are'} not processed. Re-run Generate to see the current state.`,
        duration: 10
      })
    }
    if (!n) return message.info('Saved. No saved-but-unprocessed decisions to process.')

    message.info({
      content: `Processing ${n} record${n === 1 ? '' : 's'}… you'll be told here when it finishes.`,
      duration: 4
    })
    // Remember the batch by saveId so its progress can be reported.
    //
    // Guard the id itself: String(undefined) is the truthy string "undefined",
    // so an unsaved record used to poison the set with a bogus id that matched
    // nothing - the batch then read as 0 running and the UI reported nothing.
    const ids = new Set(astPayload.concat(pvPayload).concat(assetPayload).filter(d => d.saveId).map(d => String(d.saveId)))
    setBatch({ ids, at: Date.now(), notified: false, submitted: n, err: null })
    runProcess([['AST', astPayload], ['PV', pvPayload], ['ASSET', assetPayload]].filter(([, rows]) => rows.length))
  }

  const autoReconcile = () => {
    if (!autoN) return
    setAstData(prev => prev.map(d => (d.reconcileStatus === 'FULLY_MATCHED' && !d.isProcessed && !(d.actionTaken && d.actionTaken.name))
      ? { ...d, actionTaken: { name: 'RECONCILED', refRecordId: d.matchedAsset && d.matchedAsset._id }, dirty: true } : d))
    message.success(`Auto-reconciled ${autoN} matched record${autoN === 1 ? '' : 's'}.`)
  }

  // ---------- auto-resolve duplicate scans ----------
  // Rule and reasoning: duplicateAuto.js / docs/reconciliation-cockpit-duplicate-auto.md.
  // Opening shows a preview; applying only STAGES decisions like a manual pick
  // would (dirty, unsaved). Save and Process stay the user's.
  const hasAnyDecision = (r) => !!(r.isProcessed || isDecided(r) || hasPendingClear(r) || isInFlight(r) || didFail(r))
  const dupAuto = useMemo(() => planDuplicates(astData, hasAnyDecision), [astData]) // eslint-disable-line react-hooks/exhaustive-deps
  const [dupPlan, setDupPlan] = useState(null)
  const openDupAuto = () => setDupPlan(planDuplicates(astData, hasAnyDecision))
  const applyDupPlan = () => {
    if (!dupPlan) return
    const byUid = new Map()
    dupPlan.groups.forEach(g => g.rows.forEach(x => byUid.set(x.r._uid, x)))
    setAstData(prev => prev.map(d => {
      const x = byUid.get(d._uid)
      // Re-checked on the live row: anything decided since the preview opened is left alone.
      if (!x || hasAnyDecision(d)) return d
      const at = x.action === 'NEW_ASSET' ? { name: 'NEW_ASSET' } : { name: x.action, refRecordId: x.refRecordId || null }
      return { ...d, actionTaken: at, dirty: true }
    }))
    // Owner and invoice value copied from the coded asset, marked as such in the
    // details form so nobody mistakes them for values someone typed.
    setAssetDetails(prev => {
      const next = { ...prev }
      byUid.forEach(x => {
        if (x.action !== 'NEW_ASSET' || !x.details || !Object.keys(x.details).length) return
        const cur = next[x.r._uid] || { fields: {}, prov: {} }
        const prov = { ...(cur.prov || {}) }
        Object.keys(x.details).forEach(k => { prov[k] = `group:duplicate auto-resolve, copied from ${x.r.assetCode}` })
        next[x.r._uid] = { fields: { ...(cur.fields || {}), ...x.details }, prov }
      })
      return next
    })
    setDupPlan(null)
    message.success(`Decisions set on the duplicate scans — check them in the AST tab, then Save and Process.`)
  }

  // The report's two tallying lines, status values and location breakdown - one
  // computation shared by the PDF and the Excel summary (see reconSummary.js).
  const reportSummary = () => buildReconSummary({
    allAssets, partition: assetPartition, astData, pvData, decisionByAsset, placeOf,
    locName: (id) => locName(id, locations),
    clientName: (id) => clientName(id, clients),
    actLabel: (k) => (ACT[k] ? ACT[k].label : k),
    statusLabelOf: (d) => statusMeta({ ...d, source: 'AST' }).label,
    statusOrder: (key) => AST_STATUS_ORDER.indexOf(groupOf(key)),
    createdIdsOf
  })

  // Management report: the audit's outcome on a few pages, for submission.
  // Builds on the next tick so the "building" message paints first: with the annexure
  // (every scan and PV item) the PDF takes several seconds and blocks the page.
  const downloadReport = () => {
    if (!allAssets || !allAssets.length) return message.info('Load a voucher first.')
    const hide = message.loading('Building the report with the annexure of every scan and PV item…', 0)
    setTimeout(() => {
      try { buildReport() } catch (err) { console.error(err); message.error(`Report not generated: ${err.message}`) } finally { hide() }
    }, 60)
  }
  const buildReport = () => {
    const label = (props.auditVoucherSearchData || []).find(v => String(v._id) === String(voucher))
    // Asset codes by exact id, from the register as loaded (it includes every asset the
    // audit created), for the annexure's "register asset" column.
    const codeById = new Map((allAssets || []).map(a => [String(a._id), a.assetCode]))
    const annexure = buildAnnexure({
      astData, pvData, assetQueue, placeOf, createdIdsOf,
      codeOfAsset: (id) => (id && codeById.get(String(id))) || null,
      locName: (id) => locName(id, locations),
      clientName: (id) => clientName(id, clients)
    })
    const res = buildAuditReport({
      voucherLabel: voucherScope.voucherNo || (label && label.voucherNo) || voucher,
      orgLabel: (props.authUser && props.authUser.organisation && props.authUser.organisation.name) || '',
      astData, pvData, assetQueue,
      summary: reportSummary(),
      locationCoverage, excludedLocs, excludedSubs,
      locName: (id) => locName(id, locations),
      clientName: (id) => clientName(id, clients),
      placeOf,
      auditCutoff, postAuditCount: postAuditAssets.length,
      annexure
    })
    message.success(`Report generated — ${res.pages} pages: ${res.missing.toLocaleString('en-IN')} missing assets and ` +
      `${res.annexed.toLocaleString('en-IN')} records in the annexure.`)
    if (res.unprintable > 0) message.warning(unprintableToast(res.unprintable), 8)
  }

  // Export mirrors what the tables show, not the raw payload: the grouped status,
  // the action label, the decision state, each register asset's status in this
  // audit, and the SCOPE that produced the missing count - without which the
  // numbers in the workbook cannot be reproduced.
  //
  // It reads component state, so every sheet is complete whichever tab is open.
  const exportData = () => {
    if (!allAssets || !allAssets.length) return message.info('Load a voucher first.')

    const stateLabel = (r) => STATE_LABEL[rowStateOf(r)]
    const yn = (v) => (v ? 'Yes' : 'No')
    const actLabel = (r) => (isDecided(r) && ACT[r.actionTaken.name]) ? ACT[r.actionTaken.name].label : ''

    const ast = (astData || []).map((r, i) => {
      const m = r.matchedAsset || {}
      const meta = statusMeta({ ...r, source: 'AST' })
      const rec = recommended({ ...r, source: 'AST' })
      return {
        Sno: i + 1,
        Asset: r.assetCode || '',
        'Owner code': m.ownerCode || '',
        AST: r.astNo || '',
        Model: (m.assetModel && m.assetModel.name) || '',
        'Sub type': (m.equipmentSubType && m.equipmentSubType.name) || '',
        'Asset type': (m.equipmentSubType && m.equipmentSubType.assetType && m.equipmentSubType.assetType.name) || '',
        'Location found in audit': locName(r.location, locations) || '',
        'Sub-location found in audit': clientName(r.client, clients) || '',
        'Location in register': m._id ? (locName(m.location, locations) || '') : 'not in register',
        'Sub-location in register': m._id ? (clientName(m.client, clients) || '') : '',
        Status: meta.label,
        'Status code': r.reconcileStatus || '',
        'Suggested action': rec && ACT[rec] ? ACT[rec].label : '',
        Action: actLabel(r),
        'Decision state': stateLabel(r),
        'Clear pending save': yn(hasPendingClear(r)),
        'Saved on server': yn(!!r.saveId),
        Processed: yn(r.isProcessed),
        'Processed on': r.processedOn || '',
        Remark: r.processedRemark || ''
      }
    })

    const pv = (pvData || []).map((r, i) => {
      const bm = bestMatchFor(r)
      const linked = r.actionTaken && r.actionTaken.name === 'LINK_ASSET' ? r.actionTaken : null
      return {
        Sno: i + 1,
        'Temp code': r.tempAssetCode || '',
        PV: r.pvNo || '',
        Model: (r.assetModel && r.assetModel.name) || '',
        'Sub type': (r.equipmentSubType && r.equipmentSubType.name) || '',
        'Asset type': (r.assetType && r.assetType.name) || '',
        'Location found in audit': locName(r.location, locations) || '',
        'Sub-location found in audit': clientName(r.client, clients) || '',
        'Best match rule': bm ? bm.label : 'no candidate',
        'Best match count': bm ? bm.count : 0,
        Action: actLabel(r),
        'Linked asset': linked ? (linked.refRecordSubId || '') : '',
        'Overwrites asset model': linked ? yn(linked.isPvOverwrite) : '',
        'New asset owner': (r.actionTaken && r.actionTaken.newAssetOwner) || '',
        'Decision state': stateLabel(r),
        'Clear pending save': yn(hasPendingClear(r)),
        'Saved on server': yn(!!r.saveId),
        Processed: yn(r.isProcessed),
        'Processed on': r.processedOn || '',
        Remark: r.processedRemark || ''
      }
    })

    const assets = assetQueue.map((r, i) => {
      const st = ASSET_STATUS[r.assetStatus] || {}
      const astRef = r.astRef, pvRef = r.pvRef
      return {
        Sno: i + 1,
        Asset: r.assetCode || '(not created yet)',
        'Owner code': r.ownerCode || '',
        Model: (r.assetModel && r.assetModel.name) || '',
        'Sub type': (r.equipmentSubType && r.equipmentSubType.name) || '',
        'Asset type': (r.equipmentSubType && r.equipmentSubType.assetType && r.equipmentSubType.assetType.name) || '',
        Location: locName(r.location, locations) || '',
        'Sub-location': clientName(r.client, clients) || '',
        // The place the report's location summary groups by.
        'Location on audit date': r.isPlaceholder ? '' : (locName(placeOf(r).loc, locations) || ''),
        'Sub-location on audit date': r.isPlaceholder ? '' : (clientName(placeOf(r).cli, clients) || ''),
        'Purchase date': r.invoiceDate ? moment(r.invoiceDate).format('YYYY-MM-DD') : '',
        'Invoice value': r.invoiceValue != null ? Number(r.invoiceValue) : '',
        'Last located': r.lastLocated ? moment(r.lastLocated).format('YYYY-MM-DD') : 'never located',
        'Status in this audit': (outcomeOf(r) || st).label || '',
        'Place last audited': r.placeAuditedOn ? moment(r.placeAuditedOn).format('YYYY-MM-DD') : '',
        'Accounted by': astRef ? (astRef.astNo || 'AST') : pvRef ? (pvRef.tempAssetCode || 'PV')
          : r.astCreatedRef ? `${r.astCreatedRef.astNo || 'AST'} (created)` : '',
        'AST reconcile status': astRef ? (statusMeta({ ...astRef, source: 'AST' }).label) : '',
        'Action taken': r.recStatus && ACT[r.recStatus] ? ACT[r.recStatus].label : '',
        // A decision taken on the register asset itself (Missing rows only).
        'Assets-tab decision': (r.actionTaken && r.actionTaken.name && ACT[r.actionTaken.name]) ? ACT[r.actionTaken.name].label : '',
        'Decision state': r.assetStatus === 'missing' ? ASSET_DECISION[assetDecisionOf(r)].label : '',
        Voucher: voucherOf(r) || '',
        Processed: yn(r.isProcessed),
        Remark: r.recRemark || r.processedRemark || ''
      }
    })

    // Left out of the Assets list because they were bought after the audit's last
    // capture - exported so the exclusion can be checked, not just trusted.
    const afterAudit = postAuditAssets.map((r, i) => ({
      Sno: i + 1,
      Asset: r.assetCode || '',
      'Owner code': r.ownerCode || '',
      Model: (r.assetModel && r.assetModel.name) || '',
      Location: locName(r.location, locations) || '',
      'Sub-location': clientName(r.client, clients) || '',
      'Purchase date': r.invoiceDate ? moment(r.invoiceDate).format('YYYY-MM-DD') : '',
      'Invoice value': r.invoiceValue != null ? Number(r.invoiceValue) : '',
      'Audit cut-off': auditCutoff ? moment(auditCutoff.date).format('YYYY-MM-DD') : '',
      'Would have been': (ASSET_STATUS[r.assetStatus] || {}).label || ''
    }))

    // The missing count is only meaningful alongside the scope that produced it.
    const scope = []
    locationCoverage.forEach(l => {
      const locOut = excludedLocs.has(l.loc)
      scope.push({
        Location: l.name, 'Sub-location': '(whole location)',
        'In scope': yn(!locOut),
        'Register assets': l.total, 'Accounted in audit': l.accounted, 'Coverage %': l.pct
      })
      l.subs.forEach(sv => scope.push({
        Location: l.name, 'Sub-location': sv.name,
        'In scope': yn(!locOut && !excludedSubs.has(`${l.loc}|${sv.client}`)),
        'Register assets': sv.total, 'Accounted in audit': sv.accounted, 'Coverage %': sv.pct
      }))
    })

    // Same figures as the PDF (reconSummary.js): line 1 with its bridge, line 2 by
    // action, how line 2 reaches line 1, and the reconcile status with value.
    const rs = reportSummary()
    const reg = rs.register
    const valued = (b) => (b.valued ? b.value : (b.count ? 'no invoice on record' : ''))
    const priced = (b) => `${b.valued} / ${b.count}`
    const summary = [
      { Metric: 'Audit voucher', Value: voucherScope.voucherNo ? `${voucherScope.voucherNo} (${voucher})` : (voucher || '') },
      { Metric: 'Audit date (last scan)', Value: auditCutoff ? moment(auditCutoff.date).format('YYYY-MM-DD') : 'not available — no scans recorded' },
      { Metric: '1. Register assets loaded (today)', Value: reg.loaded },
      { Metric: '1. Less: in SOLD location on the audit date', Value: reg.soldOnDate },
      { Metric: '1. Less: new assets created by this audit', Value: reg.createdByAudit },
      { Metric: '1. Less: bought after the audit date, not seen', Value: reg.afterAuditDate },
      ...(reg.otherStatus ? [{ Metric: '1. Less: unrecognised status', Value: reg.otherStatus }] : []),
      { Metric: '1. REGISTER ON THE AUDIT DATE', Value: reg.total },
      { Metric: '1. = Found in audit', Value: reg.parts.found.count },
      { Metric: '1. + Recovered via PV', Value: reg.parts.pvlink.count },
      { Metric: '1. + Missing', Value: reg.parts.missing.count },
      { Metric: '1. + Bought after its place was audited', Value: reg.parts.boughtafter.count },
      { Metric: '1. + Out of scope', Value: reg.parts.oos.count },
      ...(reg.check !== 0 ? [{ Metric: '1. WARNING — line 1 does not add up by', Value: reg.check }] : []),
      // The PDF's opening lines (Rishabh, 2026-10-27).
      { Metric: 'A. TOTAL ASSETS AS ON DATE (today, without SOLD)', Value: rs.today.total },
      { Metric: 'A. = Register on the audit date (without marked unsold)', Value: rs.today.onAuditDate },
      { Metric: 'A. + Marked unsold during the audit', Value: rs.today.unsold },
      { Metric: 'A. + New assets added during the audit', Value: rs.today.created },
      { Metric: 'A. + Bought after the audit date', Value: rs.today.afterAuditDate },
      ...(rs.today.otherStatus ? [{ Metric: 'A. + Unrecognised status', Value: rs.today.otherStatus }] : []),
      ...(rs.today.check !== 0 ? [{ Metric: 'A. WARNING — does not add up by', Value: rs.today.check }] : []),
      { Metric: 'B. ASSETS AS ON THE LAST AUDIT DATE (except sold, incl. new)', Value: rs.closing.total },
      ...[['found', '= Found in audit'], ['pvlink', '+ Recovered via PV'], ['missing', '+ Missing'],
        ['boughtafter', '+ Bought after location audit'], ['oos', '+ Out of scope'],
        ['newasset', '+ New assets added during the audit'], ['unsold', '+ Marked unsold (sale reversed)']]
        .map(([k, l]) => ({ Metric: `B. ${l}`, Value: rs.closing.parts[k].count })),
      ...(rs.closing.check !== 0 ? [{ Metric: 'B. WARNING — does not add up by', Value: rs.closing.check }] : []),
      { Metric: '2. RECORDS PHYSICALLY AUDITED', Value: rs.audit.ast + rs.audit.pv },
      { Metric: '2. = AST scans', Value: rs.audit.ast },
      { Metric: '2. + PV items', Value: rs.audit.pv },
      ...rs.actionRows.map(a => ({
        Metric: `2. Action — ${a.label} (AST / PV / processed / assets / priced)`,
        Value: `${a.ast} / ${a.pv} / ${a.astProcessed + a.pvProcessed} / ${a.assets} / ${a.valued}${a.valued ? ` — ${a.value}` : ''}`
      })),
      ...astBridgeLines(rs.astBridge).map(l => ({ Metric: `2. AST scans — ${l[0]}`, Value: l[1] })),
      ...pvBridgeLines(rs.pvBridge).map(l => ({ Metric: `2. PV items — ${l[0]}`, Value: l[1] })),
      { Metric: 'New assets created in the register (PV / AST)', Value: `${rs.newAssets.created} (${rs.newAssets.fromPv} / ${rs.newAssets.fromAst})` },
      { Metric: 'New assets set but not processed yet', Value: rs.newAssets.pending },
      ...['found', 'pvlink', 'missing', 'boughtafter', 'oos', 'newasset'].map(k => ({
        Metric: `Invoice value — ${ASSET_STATUS[k].label} (priced / assets: ${priced(rs.outcome[k])})`, Value: valued(rs.outcome[k])
      })),
      ...rs.statusRows.map(r => ({
        Metric: `Reconcile status — ${r.label} (scans / register assets / priced)`,
        Value: `${r.scans} / ${r.assets} / ${r.valued}${r.valued ? ` — ${r.value}` : ''}`
      })),
      { Metric: 'Missing — invoice value on record', Value: hasCommercials ? missingValue.total : 'invoiceValue not returned by the reconcile endpoint' },
      { Metric: 'Missing — priced / total', Value: `${missingValue.priced} / ${missingValue.count}` },
      { Metric: 'Audit cut-off (last asset captured)', Value: auditCutoff ? `${moment(auditCutoff.date).format('YYYY-MM-DD')}${auditCutoff.ref ? ` (${auditCutoff.ref})` : ''}` : 'not available — no capture date on this voucher' },
      { Metric: 'Left out — purchased after the cut-off', Value: postAuditAssets.length },
      { Metric: 'Locations excluded from scope', Value: excludedLocs.size },
      { Metric: 'Sub-locations excluded from scope', Value: excludedSubs.size },
      { Metric: 'Exported at', Value: moment().format('YYYY-MM-DD HH:mm') }
    ]

    // Location -> sub-location on the audit date, the PDF's location table.
    const cov = (t) => { const sc = t.assets - t.oos - t.boughtafter; return sc > 0 ? Math.round(((t.found + t.pvlink) / sc) * 100) : '' }
    const locRow = (level, loc, sub, t) => ({
      Level: level, Location: loc, 'Sub-location': sub, Assets: t.assets, Found: t.found, 'Via PV': t.pvlink,
      Missing: t.missing, 'Bought after its place was audited': t.boughtafter, 'Out of scope': t.oos, 'Coverage %': cov(t)
    })
    const locationSummary = []
    rs.locations.rows.forEach(L => {
      locationSummary.push(locRow('Location', L.name, '(all)', L.totals))
      L.subs.forEach(sv => locationSummary.push(locRow('Sub-location', L.name, sv.name, sv.totals)))
    })
    locationSummary.push(locRow('Total', 'Register on the audit date + marked unsold', '', rs.locations.total))

    exportSheets([
      { name: 'summary', rows: summary },
      { name: 'location_summary', rows: locationSummary },
      { name: 'ast', rows: ast },
      { name: 'pv', rows: pv },
      { name: 'assets', rows: assets },
      { name: 'after_audit_date', rows: afterAudit },
      { name: 'scope', rows: scope }
    ], 'reconciliation')
    message.success(`Exported ${ast.length} AST · ${pv.length} PV · ${assets.length} register assets.`)
  }

  // ---------- keyboard ----------
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const idx = filtered.findIndex(r => rkey(r) === currentKey)
      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') {
        e.preventDefault(); const n = filtered[Math.min(idx + 1, filtered.length - 1)] || filtered[0]; if (n) setCurrentKey(rkey(n))
      } else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') {
        e.preventDefault(); const n = filtered[Math.max(idx - 1, 0)]; if (n) setCurrentKey(rkey(n))
      } else if (/^[1-9]$/.test(e.key) && current && current.source !== 'ASSET') {
        // Positional shortcut: number key maps to the Nth visible action button.
        const acts = current.source === 'PV' ? PV_ACTIONS : astActionsFor(current)
        const name = acts[parseInt(e.key, 10) - 1]
        if (name) { e.preventDefault(); pickAction(current, name) }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // The Assets tab (master list) is always available once a voucher is loaded,
  // even when it has no AST/PV scans.
  const hasData = queue.length > 0 || assetQueue.length > 0

  // ---------- render helpers ----------
  // remark lifecycle: 'pending' (queued, running), 'fail' (errored), 'ok' (done)
  const remarkState = (r) => {
    if (!r.processedRemark) return null
    if (r.processedRemark === 'Processing...') return 'pending'
    if (!r.isProcessed) return 'fail'
    return 'ok'
  }

  const renderQueueItem = (r) => {
    const meta = statusMeta(r)
    const k = rkey(r)
    const done = isDecided(r)
    const tl = typeLine(r)
    const pl = placeLine(r)
    const idn = identLine(r)
    const rf = refLine(r)
    const rs = remarkState(r)
    const rowState = r.isProcessed ? 'processed' : rs === 'fail' ? 'failed' : done ? 'decided' : ''
    return (
      <div key={k} className={'item' + (k === currentKey ? ' active' : '') + (rowState ? ' ' + rowState : '')} onClick={() => setCurrentKey(k)}>
        {r.source === 'ASSET'
          ? <span className="chk" />
          : <span className="chk" onClick={(e) => { e.stopPropagation(); toggleSel(r) }}>
            <Checkbox checked={selected.has(k)} disabled={r.isProcessed} />
          </span>}
        <span className={'src ' + r.source}>{r.source}</span>
        <div className="body">
          <div className="line1">
            <span className="code">{codeOf(r)}</span>
            <span className={'pill ' + (done ? 'done' : meta.cls)}>{done ? ACT[r.actionTaken.name].label : meta.label}</span>
            {/* A cleared-but-saved record looks undecided, yet the server still holds
                the old decision until Save runs. Say so on the row. */}
            {hasPendingClear(r) && <span className="pill clr" title="The saved decision was cleared — hit Save to remove it on the server">Clear pending save</span>}
            {r.isProcessed
              ? <span className="pill proc"><Icon type="check-circle" theme="filled" /> Processed</span>
              : rs === 'fail'
                ? <span className="pill err"><Icon type="exclamation-circle" theme="filled" /> Failed</span>
                : (done && (isUnsaved(r)
                  ? <span className="tick pend" title="Decision not saved yet"><Icon type="clock-circle" theme="filled" /></span>
                  : <span className="tick" title="Saved to the server"><Icon type="check-circle" theme="filled" /></span>))}
          </div>
          {idn
            ? <Tooltip title={idn} placement="topLeft"><div className="asset ident">{idn}</div></Tooltip>
            : null}
          {tl
            ? <Tooltip title={tl} placement="topLeft"><div className="asset">{tl}</div></Tooltip>
            : <div className="asset" />}
          {pl
            ? <Tooltip title={pl} placement="topLeft"><div className="asset place">{pl}</div></Tooltip>
            : null}
          {rf
            ? <Tooltip title={rf} placement="topLeft"><div className="asset ref"><Icon type="link" /> {rf}</div></Tooltip>
            : null}
          {/* After a PV decision is processed, show the asset it produced: the code
              a NEW_ASSET created, or the code a LINK_ASSET linked to. */}
          {r.createdAssetCode
            ? <Tooltip title={`Created asset · ${r.createdAssetCode}`} placement="topLeft"><div className="asset ref"><Icon type="plus-circle" /> Created · {r.createdAssetCode}</div></Tooltip>
            : r.linkedAssetCode
              ? <Tooltip title={`Linked asset · ${r.linkedAssetCode}`} placement="topLeft"><div className="asset ref"><Icon type="link" /> Linked · {r.linkedAssetCode}</div></Tooltip>
              : null}
          {rs
            ? <Tooltip title={r.processedRemark} placement="topLeft">
              <div className={'asset remark ' + rs}>
                <Icon type={rs === 'pending' ? 'sync' : rs === 'fail' ? 'exclamation-circle' : 'file-done'} spin={rs === 'pending'} /> {r.processedRemark}
              </div>
            </Tooltip>
            : null}
        </div>
      </div>
    )
  }

  const renderCompare = (r) => {
    if (r.source === 'PV') {
      const rows = [
        ['Model', r.assetModel && r.assetModel.name],
        ['Sub type', r.equipmentSubType && r.equipmentSubType.name],
        ['Asset type', r.assetType && r.assetType.name],
        ['Location', locName(r.location, locations)],
        ['Sub-location', clientName(r.client, clients)]
      ]
      return rows.map(([lbl, val]) => (
        <React.Fragment key={lbl}>
          <div className="rl">{lbl}</div>
          <div className="cell"><span className="dot ok" /><span className="v">{val || '—'}</span></div>
          <div className="cell empty">no master record</div>
        </React.Fragment>
      ))
    }
    const m = r.matchedAsset
    const rows = [
      ['Location', locName(r.location, locations), m && locName(m.location, locations)],
      ['Sub-location', clientName(r.client, clients), m && clientName(m.client, clients)],
      ['Model', m && m.assetModel && m.assetModel.name, m && m.assetModel && m.assetModel.name],
      ['Owner', m && m.ownerCode, m && m.ownerCode]
    ]
    return rows.map(([lbl, sv, mv], i) => {
      const comparable = i < 2 // only location / sub-location are scan-vs-master comparisons
      const diff = comparable && m && sv !== mv
      return (
        <React.Fragment key={lbl}>
          <div className="rl">{lbl}</div>
          <div className={'cell scan' + (diff ? ' diff' : '')}>
            <span className={'dot ' + (diff ? 'no' : 'ok')} /><span className="v">{sv || '—'}</span>
          </div>
          {m
            ? <div className={'cell' + (diff ? ' diff' : '')}><span className="v">{mv || '—'}</span></div>
            : <div className="cell empty">not in register</div>}
        </React.Fragment>
      )
    })
  }

  // View-only detail for a master asset (Assets tab) — no action dock.
  const renderAssetFocus = (r) => {
    const meta = statusMeta(r)
    const rows = [
      ['Model', r.assetModel && r.assetModel.name],
      ['Sub type', r.equipmentSubType && r.equipmentSubType.name],
      ['Asset type', r.equipmentSubType && r.equipmentSubType.assetType && r.equipmentSubType.assetType.name],
      ['Location', locName(r.location, locations)],
      ['Sub-location', clientName(r.client, clients)],
      ['Owner', r.ownerCode]
    ]
    return (
      <div className="focus-inner">
        <div className="rec-head">
          <div>
            <h3>{r.assetCode}</h3>
            <div className="meta">
              <b>{(r.assetModel && r.assetModel.name) || '—'}</b> · Master asset ·{' '}
              <Link target="_blank" to={`/asset/history-report/${r._id}`}>history</Link>
            </div>
          </div>
          <div className="status-block">
            <span className={'pill ' + meta.cls}>{meta.label}</span>
            <div className="why">View only — assets have no reconciliation action.</div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-title"><Icon type="database" /> Asset details</div>
          <div className="cmp two">
            {rows.map(([lbl, val]) => (
              <React.Fragment key={lbl}>
                <div className="rl">{lbl}</div>
                <div className="cell"><span className="v">{val || '—'}</span></div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {(r.astRef || r.pvRef) && (
          <div className="panel">
            <div className="panel-title"><Icon type="pushpin" /> Reconciliation</div>
            <div className="cmp two">
              <div className="rl">Action</div>
              <div className="cell"><span className="pill done">{ACT[r.recStatus] ? ACT[r.recStatus].label : r.recStatus}</span></div>
              {r.astRef && <React.Fragment key="ast"><div className="rl">AST</div><div className="cell"><Link target="_blank" to={`/asset-stock-taking/view/${r.astRef.astId || ''}`}>{r.astRef.astNo || 'AST'}</Link></div></React.Fragment>}
              {r.pvRef && <React.Fragment key="pv"><div className="rl">PV</div><div className="cell"><Link target="_blank" to={`/pre-verification/view/${r.pvRef.pvId || ''}`}>{r.pvRef.pvNo || 'PV'}</Link></div></React.Fragment>}
              {r.recRemark && <React.Fragment key="rem"><div className="rl">Remark</div><div className="cell"><span className="v">{r.recRemark}</span></div></React.Fragment>}
            </div>
          </div>
        )}

        <div className="panel">
          <div className="panel-title"><Icon type="camera" /> Photo</div>
          <div className="photos"><LatestImage id={r._id} /></div>
        </div>
      </div>
    )
  }

  // ---------- AST table ----------
  // The AST tab is a table: every decision is taken inline, so an auditor never
  // has to open a record to see the discrepancy or act on it. PV keeps the
  // list + focus layout - its decisions need the link picker, which is a genuine
  // drill-in - and the Assets tab stays a read-only list.
  const isTable = sourceFilter === 'AST' || sourceFilter === 'PV' || sourceFilter === 'ASSETS'

  // Compact action labels. The full label stays in the tooltip so nothing is lost.
  const SHORT = {
    ATA_ARN: 'Transfer', SOLD: 'Sold', UNSOLD: 'Unsold', IGNORE: 'Ignore',
    RECONCILED: 'Reconcile', LINK_ASSET: 'Link', NEW_ASSET: 'New asset'
  }

  // A submitted record is no longer "saved" - it is running, or it came back with
  // an error. Both were previously indistinguishable from a saved decision here,
  // which is how 56 failed records could sit in the table looking fine.
  const rowStateOf = (r) =>
    r.isProcessed ? 'proc'
      : isInFlight(r) ? 'run'
        : didFail(r) ? 'fail'
          : hasPendingClear(r) ? 'pend'
            : isDecided(r) ? (isUnsaved(r) ? 'pend' : 'savd')
              : 'todo'

  const STATE_LABEL = {
    todo: 'To review', pend: 'Pending save', savd: 'Saved',
    run: 'Processing', fail: 'Failed', proc: 'Processed'
  }

  // The state cell is where a row explains itself, so a failed row shows the
  // server's own reason rather than just the word "Failed" - that reason is the
  // only thing that tells the user what to fix.
  const stateCell = (r) => {
    const st = rowStateOf(r)
    // The state and the server's remark stack: the remark is an ATA/ARN or invoice
    // number and sits on its own line under the state, so a long one wraps inside
    // the column instead of widening the whole table.
    return (
      <span className={'rt-state ' + st} title={r.processedRemark || undefined}>
        <span className="rt-state-line">
          {st === 'run'
            ? <Icon type="sync" spin className="dotm-ic" />
            : <i className={'dotm ' + st} />}
          {STATE_LABEL[st]}
        </span>
        {st === 'fail' && r.processedRemark && <em className="rt-why">{r.processedRemark}</em>}
        {st === 'proc' && r.processedRemark && <em className="rt-why ok">{r.processedRemark}</em>}
        {/* The asset this PV decision produced: the new asset's code, or the code it linked to. */}
        {st === 'proc' && r.createdAssetCode && <em className="rt-why ok">Created · {r.createdAssetCode}</em>}
        {st === 'proc' && !r.createdAssetCode && r.linkedAssetCode && <em className="rt-why ok">Linked · {r.linkedAssetCode}</em>}
      </span>
    )
  }

  const rowActions = (r) => {
    const rec = recommended(r)
    const done = isDecided(r)
    const names = r.source === 'PV' ? PV_ACTIONS : astActionsFor(r)
    return (
      <div className="acts">
        {names.map(name => {
          const chosen = done && r.actionTaken.name === name
          const cls = 'act'
            + (chosen ? ' chosen' : (name === rec && !done ? ' rec' : ''))
            + (r.isProcessed ? ' locked' : '')
          return (
            <Tooltip key={name} title={ACT[name].label}>
              <button className={cls} disabled={r.isProcessed}
                onClick={(e) => { e.stopPropagation(); pickAction(r, name) }}>
                {SHORT[name] || ACT[name].label}
              </button>
            </Tooltip>
          )
        })}
        {done && !r.isProcessed && (
          <Tooltip title="Remove this decision">
            <button className="act mute" onClick={(e) => { e.stopPropagation(); clearAction(r) }}>Clear</button>
          </Tooltip>
        )}
      </div>
    )
  }

  // The register cell prints a value only when it DIFFERS from the audit. When it
  // matches it says so - the audit value is always shown in full in the column to
  // its left, so nothing is hidden, and the 1,272 fully-matched rows stop
  // competing for attention with the ones that need a decision.
  const registerCell = (r) => {
    if (!r.matchedAsset) return <span className="rt-none">not in register</span>
    const aL = locName(r.location, locations), aS = clientName(r.client, clients)
    const mL = locName(r.matchedAsset.location, locations), mS = clientName(r.matchedAsset.client, clients)
    const dL = aL !== mL, dS = aS !== mS
    if (!dL && !dS) return <span className="rt-same"><Icon type="check" /> same as audit</span>
    return (
      <div className="rt-place">
        <div className={dL ? 'd' : ''}>{mL || <em>—</em>}</div>
        <div className={'sub' + (dS ? ' d' : '')}>{mS || '—'}</div>
      </div>
    )
  }

  // Register location AS OF the AST record's scan date (r.time) - this is what the
  // status is compared against. Differences from the audit scan are highlighted.
  const registerOnDateCell = (r) => {
    if (!r.matchedAsset) return <span className="rt-none">not in register</span>
    const aL = locName(r.location, locations), aS = clientName(r.client, clients)
    const mL = locName(r.registerLocationOnDate, locations), mS = clientName(r.registerClientOnDate, clients)
    const dL = aL !== mL, dS = aS !== mS
    const dateTip = r.time ? `As of ${moment(r.time).format('DD MMM YYYY')}` : undefined
    if (!dL && !dS) return <Tooltip title={dateTip}><span className="rt-same"><Icon type="check" /> same as audit</span></Tooltip>
    return (
      <Tooltip title={dateTip}>
        <div className="rt-place">
          <div className={dL ? 'd' : ''}>{mL || <em>—</em>}</div>
          <div className={'sub' + (dS ? ' d' : '')}>{mS || '—'}</div>
        </div>
      </Tooltip>
    )
  }

  const renderTableRow = (r) => {
    const k = rkey(r)
    const meta = statusMeta(r)
    const st = rowStateOf(r)
    const cls = 'rt-row st-' + st
      + (k === currentKey ? ' active' : '')
      + (selected.has(k) ? ' sel' : '')
    return (
      <tr key={k} className={cls} onClick={() => setCurrentKey(k)}>
        <td className="c-sel" onClick={(e) => { e.stopPropagation(); toggleSel(r) }}>
          <Checkbox checked={selected.has(k)} disabled={r.isProcessed} />
        </td>
        <td className="c-ph">
          <PhotoThumb photos={r.photos} onClick={() => setCmpRecord(r)} />
        </td>
        <td className="c-asset">
          <div className="rt-code">{codeOf(r)}</div>
          <div className="rt-own">{identLine(r) || (r.astNo || '')}</div>
        </td>
        <td className="c-eq">
          <div className="rt-model">{nameOf(r)}</div>
          <div className="rt-sub">{typeLine(r) || '—'}</div>
        </td>
        <td className="c-loc">
          <div className="rt-place">
            <div>{locName(r.location, locations) || <em>—</em>}</div>
            <div className="sub">{clientName(r.client, clients) || '—'}</div>
          </div>
        </td>
        <td className="c-buy">{r.time
          ? <Tooltip title={moment(r.time).format('DD MMM YYYY, HH:mm')}><span className="rt-date">{moment(r.time).format('DD MMM YYYY')}</span></Tooltip>
          : <span className="rt-never">—</span>}</td>
        <td className="c-loc">{registerOnDateCell(r)}</td>
        <td className="c-loc c-reg">{registerCell(r)}</td>
        <td className="c-st">
          <span className={'pill ' + meta.cls}>{meta.label}</span>
          {hasPendingClear(r) && <span className="pill clr rt-clr">Clear pending save</span>}
        </td>
        <td className="c-act">{rowActions(r)}</td>
        <td className="c-state">
          {stateCell(r)}
        </td>
      </tr>
    )
  }

  // ---------- PV table ----------
  // Same shape as the AST table so the tab switch does not change how the screen
  // is read. The PV-specific column is "Best match": the most specific rule that
  // still yields a candidate AFTER the exclusions, which is what decides whether
  // this item is worth linking or should become a new asset.
  const bestMatchCell = (r) => {
    if (isDecided(r) && r.actionTaken.name === 'LINK_ASSET') {
      return <span className="bm linked">linked to <b>{r.actionTaken.refRecordSubId || 'asset'}</b></span>
    }
    // Marked New asset: the match data no longer applies.
    if (isDecided(r) && r.actionTaken.name === 'NEW_ASSET') return <span className="bm newasset">New asset</span>
    const bm = bestMatchFor(r)
    if (!bm) return <span className="bm none">no candidate</span>
    const strong = ['mlc', 'ml', 'slc', 'sl'].includes(bm.key)
    return (
      <Tooltip title={<><b>{bm.label}</b><br />{strong
        ? 'A candidate agrees on location, so it is worth comparing photos.'
        : 'Only the model or sub-type agrees — the location does not, so treat this as weak.'}</>}>
        <span className={'bm ' + (strong ? 'strong' : 'weak')}>
          {bm.short || bm.label} <b className="tnum">{bm.count}</b>
        </span>
      </Tooltip>
    )
  }

  const renderPvRow = (r) => {
    const k = rkey(r)
    const st = rowStateOf(r)
    const cls = 'rt-row st-' + st + (k === currentKey ? ' active' : '') + (selected.has(k) ? ' sel' : '')
    return (
      <tr key={k} className={cls} onClick={() => setCurrentKey(k)}>
        <td className="c-sel" onClick={(e) => { e.stopPropagation(); toggleSel(r) }}>
          <Checkbox checked={selected.has(k)} disabled={r.isProcessed} />
        </td>
        <td className="c-ph"><PhotoThumb photos={r.photos} onClick={() => setCmpRecord(r)} /></td>
        <td className="c-asset">
          <div className="rt-code">{codeOf(r)}</div>
          <div className="rt-own">{r.pvNo || ''}</div>
        </td>
        <td className="c-eq">
          <div className="rt-model">{(r.assetModel && r.assetModel.name) || '—'}</div>
          <div className="rt-sub">{typeLine(r) || '—'}</div>
        </td>
        <td className="c-loc">
          <div className="rt-place">
            <div>{locName(r.location, locations) || <em>—</em>}</div>
            <div className="sub">{clientName(r.client, clients) || '—'}</div>
          </div>
        </td>
        <td className="c-buy">{r.createdAt
          ? <Tooltip title={moment(r.createdAt).format('DD MMM YYYY, HH:mm')}><span className="rt-date">{moment(r.createdAt).format('DD MMM YYYY')}</span></Tooltip>
          : <span className="rt-never">—</span>}</td>
        <td className="c-bm">{bestMatchCell(r)}</td>
        <td className="c-det">{detailsCell(r)}</td>
        <td className="c-act">{rowActions(r)}</td>
        <td className="c-state">
          {stateCell(r)}
          <AiTag ai={aiTagOf(r)} />
        </td>
      </tr>
    )
  }

  // Details cell: only a NEW_ASSET row has anything to say here. It states
  // readiness and where the values came from, and clicking opens that one asset.
  const detailsCell = (r) => {
    if (!needsDetails(r)) return <span className="rt-none">—</span>
    const missing = detailsMissing(r)
    const held = assetDetails[r._uid]
    const provs = Object.values((held && held.prov) || {})
    const fromGroup = provs.find(p => p && p.startsWith('group:'))
    const anyIndividual = provs.includes('individual')
    const LBL = { ownedBy: 'owner', rate: 'value' }
    return (
      <button type="button" className="dcell" onClick={(e) => { e.stopPropagation(); setDetailsFor([r]) }}>
        {missing.length === 0
          ? <span className="pill ok"><Icon type="check-circle" theme="filled" /> Ready</span>
          : <span className={'pill ' + (provs.length ? 'warn' : 'err')}>
            Needs {missing.map(f => LBL[f] || f).join(' + ')}
          </span>}
        <span className="dsub">
          {fromGroup ? fromGroup.slice(6) : provs.length ? 'set on this asset' : 'nothing set'}
          {fromGroup && anyIndividual ? ' + edits' : ''}
        </span>
      </button>
    )
  }

  const renderPvTable = () => (
    <div className="rtwrap">
      <table className="rt pv">
        <thead>
          <tr>
            <th className="c-sel" />
            <th className="c-ph">Photo</th>
            <th className="c-asset">Temp code</th>
            <th className="c-eq">Equipment</th>
            <th className="c-loc scan">Found in audit</th>
            <th className="c-buy">PV date</th>
            <th className="c-bm">Best match</th>
            <th className="c-det">Details</th>
            <th className="c-act">Action</th>
            <th className="c-state">State</th>
          </tr>
        </thead>
        <tbody>{filtered.slice(0, LIST_CAP).map(renderPvRow)}</tbody>
      </table>
      {filtered.length > LIST_CAP &&
        <div className="rt-note">Showing first {LIST_CAP} of {filtered.length}. Refine filters to narrow down.</div>}
      {!filtered.length && <div className="rt-note">Nothing matches these filters.</div>}
    </div>
  )

  // ---------- Assets table ----------
  // Every register asset with its status in THIS audit. Only "Missing" takes a
  // decision here; everything else is already owned by an AST or PV record and is
  // shown read-only with a link to it, so the same decision never has two homes.
  const [assetStatusFilter, setAssetStatusFilter] = useState(null)

  // Purchase date and invoice value are NOT on the reconcile payload today - the
  // asset LIST endpoint flattens them off the purchase order (invoiceDate is what
  // the asset history report labels "Date of Purchase"), the reconcile endpoint
  // does not. The columns read them straight off the record, so they light up the
  // moment the backend includes them; until then they say so rather than
  // rendering a blank that reads as "no invoice".
  const hasCommercials = useMemo(
    () => (allAssets || []).some(a => a.invoiceValue != null || a.invoiceDate),
    [allAssets])

  const money = (v) => (v == null || v === '')
    ? null
    : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })

  const dateCell = (v, neverLabel) => {
    if (!v) return <span className="rt-never">{neverLabel}</span>
    return (
      <Tooltip title={moment(v).format('DD MMM YYYY, HH:mm')}>
        <span className="rt-date">{moment(v).format('DD MMM YYYY')}
          <em>{moment(v).fromNow()}</em></span>
      </Tooltip>
    )
  }

  // What the missing assets are worth - the reason these columns were asked for.
  const missingValue = useMemo(() => {
    const rows = assetQueue.filter(r => r.assetStatus === 'missing')
    const withValue = rows.filter(r => r.invoiceValue != null)
    return {
      count: rows.length,
      priced: withValue.length,
      total: withValue.reduce((n, r) => n + Number(r.invoiceValue || 0), 0)
    }
  }, [assetQueue])


  // ---- decisions on Missing assets: where each one stands ----
  // undecided -> unsaved -> saved (awaiting Process) -> processed | failed.
  // A saved decision that was cleared here is 'clearing' until the clear is saved.
  const ASSET_DECISION = {
    undecided: { label: 'Undecided' },
    unsaved: { label: 'Not saved' },
    clearing: { label: 'Clear not saved' },
    saved: { label: 'Saved — awaiting Process' },
    processing: { label: 'Processing…' },
    failed: { label: 'Process failed' },
    processed: { label: 'Processed' }
  }
  const assetDecisionOf = (r) => {
    const name = r.actionTaken && r.actionTaken.name
    if (r.isProcessed) return 'processed'
    if (!name) return (r.saveId && r.dirty) ? 'clearing' : 'undecided'
    if (r.dirty || !r.saveId) return 'unsaved'
    if (r.processedRemark === PROCESSING_REMARK) return 'processing'
    if (r.processedRemark) return 'failed'
    return 'saved'
  }
  // Status once the decision has been carried out on the register.
  const ASSET_OUTCOME = {
    MISSING: { label: 'Marked missing', cls: 'mmiss' },
    SOLD: { label: 'Marked sold', cls: 'msold' },
    IGNORE: { label: 'Ignored', cls: 'mign' }
  }
  const outcomeOf = (r) => (r.assetStatus === 'missing' && r.isProcessed && r.actionTaken && ASSET_OUTCOME[r.actionTaken.name]) || null
  // The voucher the decision was processed against: the ACV for Mark missing, the
  // sale invoice (SI-) for Mark sold - read from what the server recorded, never
  // composed here. Ignore raises no voucher.
  const voucherOf = (r) => {
    if (!r.isProcessed || !r.actionTaken) return null
    if (r.actionTaken.name === 'MISSING') return r.actionTaken.refRecordSubId || (r.processedRemark || '').split(' ')[0] || null
    if (r.actionTaken.name === 'SOLD') return (r.processedRemark || '').split(' - ')[0] || null
    return null
  }
  const voucherLink = (r) => {
    const no = voucherOf(r)
    if (!no) return null
    if (r.actionTaken.name === 'MISSING' && r.actionTaken.refRecordId) {
      return <Link to={`/asset-condition-voucher/view/${r.actionTaken.refRecordId}`} onClick={e => e.stopPropagation()}>{no}</Link>
    }
    return no
  }

  const assetRef = (r) => {
    if (r.assetStatus === 'missing' && (r.isProcessed || (r.actionTaken && r.actionTaken.name) || assetDecisionOf(r) === 'clearing')) {
      const st = assetDecisionOf(r)
      const act = r.actionTaken && r.actionTaken.name && ACT[r.actionTaken.name] ? ACT[r.actionTaken.name].label : null
      if (st === 'processed') {
        const v = voucherLink(r)
        return <>{v ? <><b>{v}</b> · </> : null}processed{r.processedOn ? ` ${moment(r.processedOn).format('DD MMM YYYY')}` : ''}
          {r.processedRemark && r.processedRemark.includes(' - ') ? <> · {r.processedRemark.split(' - ').slice(1).join(' - ')}</> : null}</>
      }
      if (st === 'clearing') return <>decision cleared · <b className="dec-warn">not saved</b></>
      if (st === 'failed') return <><b>{act}</b> · <span className="dec-fail">{r.processedRemark}</span></>
      if (st === 'processing') return <><b>{act}</b> · processing…</>
      return <><b>{act}</b> · {st === 'unsaved' ? <b className="dec-warn">not saved</b> : 'saved, awaiting Process'}</>
    }
    if (r.assetStatus === 'found' && r.astRef) {
      const meta = statusMeta({ ...r.astRef, source: 'AST' })
      return <>{meta.label} · <b>{r.astRef.astNo || 'AST'}</b>
        {r.recStatus && ACT[r.recStatus] ? <> · {ACT[r.recStatus].label}</> : null}</>
    }
    if (r.assetStatus === 'pvlink' && r.pvRef) return <><b>{r.pvRef.tempAssetCode}</b> · {r.pvRef.pvNo || 'PV'}</>
    if (r.assetStatus === 'newasset' && r.pvRef) return <><b>{r.pvRef.tempAssetCode}</b> · created on process</>
    if (r.assetStatus === 'newasset' && r.astCreatedRef) return <>repeat scan · <b>{r.astCreatedRef.astNo || 'AST'}</b> · created on process</>
    if (r.assetStatus === 'missing') return <>in scope · never scanned</>
    if (r.assetStatus === 'oos') return <>location not covered by this audit</>
    if (r.assetStatus === 'boughtafter') return <>bought <b>{moment(r.invoiceDate).format('DD MMM YYYY')}</b> · place last audited <b>{moment(r.placeAuditedOn).format('DD MMM YYYY')}</b></>
    return null
  }

  const assetActions = (r) => {
    if (r.assetStatus !== 'missing') {
      return <span className="rt-noact">no action needed</span>
    }
    if (r.isProcessed) {
      const out = ASSET_OUTCOME[r.actionTaken && r.actionTaken.name]
      const v = voucherLink(r)
      return (
        <span className="dec-done">
          <Icon type="check-circle" theme="filled" /> {out ? out.label : 'Processed'}
          {v && <span className="dec-vno">{v}</span>}
        </span>
      )
    }
    if (assetDecisionOf(r) === 'processing') {
      return <span className="dec-done"><Icon type="loading" /> Processing…</span>
    }
    if (assetsGated) {
      const why = `${pvOutstanding} PV record${pvOutstanding === 1 ? '' : 's'} still unprocessed — any could still be linked to this asset`
      return (
        <Tooltip title={why}>
          <div className="acts">
            <button className={'act' + (r.actionTaken && r.actionTaken.name === 'SOLD' ? ' primary' : '')} disabled>Mark sold</button>
            <button className={'act' + (r.actionTaken && r.actionTaken.name === 'MISSING' ? ' primary' : '')} disabled>Mark missing</button>
            <button className={'act danger' + (r.actionTaken && r.actionTaken.name === 'IGNORE' ? ' on' : '')} disabled>Ignore</button>
          </div>
        </Tooltip>
      )
    }
    const active = r.actionTaken && r.actionTaken.name
    return (
      <div className="acts" onClick={e => e.stopPropagation()}>
        <button className={'act' + (active === 'SOLD' ? ' primary' : '')} aria-pressed={active === 'SOLD'} onClick={() => setAssetAction(r, 'SOLD')}>Mark sold</button>
        <Tooltip title="Process raises an Asset Condition Voucher for this asset with condition Missing">
          <button className={'act' + (active === 'MISSING' ? ' primary' : '')} aria-pressed={active === 'MISSING'} onClick={() => setAssetAction(r, 'MISSING')}>Mark missing</button>
        </Tooltip>
        <button className={'act danger' + (active === 'IGNORE' ? ' on' : '')} aria-pressed={active === 'IGNORE'} onClick={() => setAssetAction(r, 'IGNORE')}>Ignore</button>
        {active && <button className="act" onClick={() => clearAssetAction(r)}>Clear</button>}
      </div>
    )
  }

  const renderAssetRow = (r) => {
    const k = rkey(r)
    const meta = outcomeOf(r) || ASSET_STATUS[r.assetStatus] || ASSET_STATUS.missing
    return (
      <tr key={k} className={'rt-row as-' + r.assetStatus + (k === currentKey ? ' active' : '')}
        onClick={() => setCurrentKey(k)}>
        <td className="c-sel" />
        <td className="c-ph">
          {/* Register assets carry no inline photos; AssetThumb reuses the AST/PV
              record's when there is one and otherwise fetches lazily on scroll. */}
          <AssetThumb assetId={r.assetStatus === 'newasset' ? null : r._id}
            inlinePhotos={(r.astRef && r.astRef.photos) || (r.pvRef && r.pvRef.photos)}
            onClick={() => setCmpRecord(r)} />
        </td>
        <td className="c-asset">
          <div className="rt-code">{r.assetCode || <em className="rt-new">not created yet</em>}</div>
          <div className="rt-own">{r.ownerCode || ''}</div>
        </td>
        <td className="c-eq">
          <div className="rt-model">{(r.assetModel && r.assetModel.name) || '—'}</div>
          <div className="rt-sub">{typeLine(r) || '—'}</div>
        </td>
        <td className="c-loc">
          <div className="rt-place">
            <div>{locName(r.location, locations) || <em>—</em>}</div>
            <div className="sub">{clientName(r.client, clients) || '—'}</div>
          </div>
        </td>
        <td className="c-loc">
          {/* Where the asset was on the audit's as-of date (X): its current place
              unless a transfer moved it after X, in which case that move's origin. */}
          <Tooltip title={r.asOfDate
            ? `As of ${moment(r.asOfDate).format('DD MMM YYYY')}${r.asOfSource ? ` · from ${r.asOfSource === 'PV' ? 'PV record' : 'AST scan'}${r.asOfRef ? ' ' + r.asOfRef : ''}` : ''}`
            : undefined}>
            <div className="rt-place">
              <div>{locName(r.locationOnDate, locations) || <em>—</em>}</div>
              <div className="sub">{clientName(r.clientOnDate, clients) || '—'}</div>
            </div>
          </Tooltip>
        </td>
        <td className="c-buy">{dateCell(r.invoiceDate, hasCommercials ? 'no purchase record' : 'not in payload')}</td>
        <td className="c-val">
          {money(r.invoiceValue) != null
            ? <span className="rt-val">{money(r.invoiceValue)}</span>
            : <span className="rt-never">{hasCommercials ? 'no value' : 'not in payload'}</span>}
        </td>
        <td className="c-seen">{dateCell(r.lastLocated, 'never located')}</td>
        <td className="c-as-st">
          <span className={'pill ' + meta.cls}>{meta.label}</span>
          <div className="rt-ref">{assetRef(r)}</div>
        </td>
        <td className="c-act">{assetActions(r)}</td>
      </tr>
    )
  }

  // Decision filter (Missing rows only): where each decision stands.
  const [assetDecisionFilter, setAssetDecisionFilter] = useState(null)
  const DECISION_CHIPS = [
    { key: 'undecided', label: 'Undecided' },
    { key: 'unsaved', label: 'Not saved' },
    { key: 'saved', label: 'Awaiting Process' },
    { key: 'processing', label: 'Processing' },
    { key: 'failed', label: 'Process failed' },
    { key: 'out:MISSING', label: 'Marked missing' },
    { key: 'out:SOLD', label: 'Marked sold' },
    { key: 'out:IGNORE', label: 'Ignored' }
  ]
  const decisionKeyOf = (r) => {
    const st = assetDecisionOf(r)
    if (st === 'processed') return `out:${(r.actionTaken && r.actionTaken.name) || ''}`
    return st === 'clearing' ? 'unsaved' : st
  }
  const decisionCounts = useMemo(() => {
    const c = {}
    assetQueue.forEach(r => { if (r.assetStatus === 'missing') { const k = decisionKeyOf(r); c[k] = (c[k] || 0) + 1 } })
    return c
  }, [assetQueue]) // eslint-disable-line react-hooks/exhaustive-deps

  const assetBase = useMemo(() => {
    const q = query.trim().toLowerCase()
    return assetQueue.filter(r => {
      if (assetStatusFilter && r.assetStatus !== assetStatusFilter) return false
      if (assetDecisionFilter && (r.assetStatus !== 'missing' || decisionKeyOf(r) !== assetDecisionFilter)) return false
      if (!q) return true
      const hay = `${r.assetCode || ''} ${r.ownerCode || ''} ${(r.assetModel && r.assetModel.name) || ''} ${locName(r.location, locations) || ''}`.toLowerCase()
      return hay.includes(q)
    })
  }, [assetQueue, assetStatusFilter, assetDecisionFilter, query, locations]) // eslint-disable-line react-hooks/exhaustive-deps

  // Invoice value / purchase date filters, to pick which missing assets to Ignore,
  // mark sold or mark missing. A row with no value (or no purchase date) cannot
  // satisfy a range, so it drops out of a filtered list - how many did is shown
  // beside the filters, never hidden.
  const [valueMin, setValueMin] = useState(null)
  const [valueMax, setValueMax] = useState(null)
  const [buyRange, setBuyRange] = useState(null) // [moment, moment]
  const numOrNull = (v) => (v === '' || v == null || !Number.isFinite(Number(v))) ? null : Number(v)
  const valueFilterOn = valueMin != null || valueMax != null
  const dateFilterOn = !!(buyRange && buyRange[0] && buyRange[1])
  const assetFiltered = useMemo(() => {
    if (!valueFilterOn && !dateFilterOn) return assetBase
    return assetBase.filter(r => {
      if (valueFilterOn) {
        const v = (r.invoiceValue == null || r.invoiceValue === '') ? NaN : Number(r.invoiceValue)
        if (!Number.isFinite(v)) return false
        if (valueMin != null && v < valueMin) return false
        if (valueMax != null && v > valueMax) return false
      }
      if (dateFilterOn) {
        if (!r.invoiceDate) return false
        const d = moment(r.invoiceDate)
        if (d.isBefore(buyRange[0], 'day') || d.isAfter(buyRange[1], 'day')) return false
      }
      return true
    })
  }, [assetBase, valueMin, valueMax, buyRange]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sort by purchase date or invoice value, ascending or descending. Sorting runs
  // before the LIST_CAP slice, so the rows drawn are the true top of the order.
  // Rows with no value / no purchase date always go LAST in either direction: a
  // blank is not the smallest or the largest value, and is never treated as 0.
  // Ties keep the list's existing order (stable sort). Bulk actions and counts
  // use assetFiltered and are unaffected by the order.
  const [assetSort, setAssetSort] = useState(null) // { key: 'invoiceDate' | 'invoiceValue', dir: 'asc' | 'desc' }
  const sortValueOf = (r, key) => {
    if (key === 'invoiceValue') {
      const v = (r.invoiceValue == null || r.invoiceValue === '') ? NaN : Number(r.invoiceValue)
      return Number.isFinite(v) ? v : null
    }
    const t = r.invoiceDate ? new Date(r.invoiceDate).getTime() : NaN
    return Number.isFinite(t) ? t : null
  }
  const assetSorted = useMemo(() => {
    if (!assetSort) return assetFiltered
    const { key, dir } = assetSort
    const sign = dir === 'desc' ? -1 : 1
    return assetFiltered
      .map((r, i) => ({ r, i, v: sortValueOf(r, key) }))
      .sort((a, b) => {
        if (a.v === null && b.v === null) return a.i - b.i
        if (a.v === null) return 1
        if (b.v === null) return -1
        return (a.v - b.v) * sign || a.i - b.i
      })
      .map(x => x.r)
  }, [assetFiltered, assetSort]) // eslint-disable-line react-hooks/exhaustive-deps
  // Click cycles: ascending -> descending -> off.
  const toggleAssetSort = (key) => setAssetSort(cur =>
    !cur || cur.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null)
  const sortHeader = (key, label, cls) => {
    const on = assetSort && assetSort.key === key
    const dir = on ? assetSort.dir : null
    return (
      <th className={cls} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
        <button type="button" onClick={() => toggleAssetSort(key)}
          title={`Sort by ${label.toLowerCase()} — ${!on ? 'ascending' : dir === 'asc' ? 'descending' : 'clear sort'}. Blank values stay last.`}
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: on ? 'var(--accent)' : 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'inherit', letterSpacing: 'inherit' }}>
          {label}
          <Icon type={dir === 'asc' ? 'caret-up' : dir === 'desc' ? 'caret-down' : 'swap'} rotate={!dir ? 90 : undefined}
            style={{ fontSize: 10, opacity: on ? 1 : 0.5 }} />
        </button>
      </th>
    )
  }

  const filterGaps = useMemo(() => ({
    noValue: valueFilterOn ? assetBase.filter(r => r.invoiceValue == null || r.invoiceValue === '').length : 0,
    noDate: dateFilterOn ? assetBase.filter(r => !r.invoiceDate).length : 0
  }), [assetBase, valueFilterOn, dateFilterOn])
  const resetAssetFilters = () => { setValueMin(null); setValueMax(null); setBuyRange(null) }

  // Bulk decisions on every MISSING row the filters leave - all of them, not just
  // the first LIST_CAP drawn. Confirmed first, with the count and the value.
  const bulkTargets = useMemo(
    () => assetFiltered.filter(r => r.assetStatus === 'missing' && r._id && !r.isProcessed),
    [assetFiltered])
  const bulkValue = useMemo(() => {
    const priced = bulkTargets.filter(r => r.invoiceValue != null && r.invoiceValue !== '' && Number.isFinite(Number(r.invoiceValue)))
    return { priced: priced.length, total: priced.reduce((n, r) => n + Number(r.invoiceValue), 0) }
  }, [bulkTargets])
  const bulkAssetPick = (name) => {
    const n = bulkTargets.length
    if (!n || assetsGated) return
    const changing = bulkTargets.filter(r => r.actionTaken && r.actionTaken.name && r.actionTaken.name !== name).length
    Modal.confirm({
      title: `${ACT[name].label}: ${n.toLocaleString('en-IN')} missing asset${n === 1 ? '' : 's'}?`,
      content: (
        <div>
          <div>Every missing asset in the current filter{n > LIST_CAP ? ` — including the ${(n - LIST_CAP).toLocaleString('en-IN')} beyond the first ${LIST_CAP} drawn` : ''}.</div>
          <div style={{ marginTop: 6 }}>
            Invoice value ₹{bulkValue.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })} across {bulkValue.priced.toLocaleString('en-IN')} with a value on record
            {bulkValue.priced < n ? `; ${(n - bulkValue.priced).toLocaleString('en-IN')} have none` : ''}.
          </div>
          {changing > 0 && <div style={{ marginTop: 6 }}>{changing.toLocaleString('en-IN')} already have a different decision, which this replaces.</div>}
          {name === 'MISSING' && <div style={{ marginTop: 6 }}>On Process, each gets its own Asset Condition Voucher with condition Missing.</div>}
          <div style={{ marginTop: 6 }}>Nothing is sent until you Save; nothing changes in the register until you Process.</div>
        </div>
      ),
      okText: ACT[name].label,
      onOk: () => {
        const done = setAssetActionBulk(bulkTargets, name)
        message.success(`${ACT[name].label} set on ${done.toLocaleString('en-IN')} asset${done === 1 ? '' : 's'} — Save, then Process.`)
      }
    })
  }
  const bulkAssetClear = () => {
    const done = clearAssetActionBulk(bulkTargets)
    if (!done) return message.info('No decisions to clear in this filter.')
    message.success(`Cleared ${done.toLocaleString('en-IN')} decision${done === 1 ? '' : 's'} — Save to apply any that were already saved.`)
  }
  const bulkDecided = bulkTargets.filter(r => r.actionTaken && r.actionTaken.name).length

  const renderAssetsTable = () => (
    <>
      {assetsGated && (
        <div className="gate" role="status">
          <Icon type="lock" />
          <span>
            <b>Decisions are locked until PV linking is processed.</b>{' '}
            <b className="tnum">{pvOutstanding}</b> PV record{pvOutstanding === 1 ? '' : 's'} still
            unprocessed — any of them could still be linked to an asset shown here as Missing or Out of
            scope, so disposing of one now could double-claim it.
          </span>
          <Button size="small" onClick={() => { setSourceFilter('PV'); setCurrentKey(null) }}>Go to PV</Button>
        </div>
      )}
      {missingValue.count > 0 && (
        <div className="missval">
          <Icon type="calculator" />
          <span>
            <b className="tnum">{missingValue.count.toLocaleString('en-IN')}</b> missing
            {hasCommercials
              ? <> · <b className="tnum">₹{missingValue.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</b> invoice value
                {missingValue.priced < missingValue.count &&
                  <> across the <b className="tnum">{missingValue.priced.toLocaleString('en-IN')}</b> with
                    an invoice on record — <b className="tnum">{(missingValue.count - missingValue.priced).toLocaleString('en-IN')}</b> have none,
                    so the true figure is higher</>}</>
              : <> · invoice value not available — the reconcile endpoint does not return
                <span className="mono"> invoiceValue</span>, so these cannot be valued yet</>}
          </span>
        </div>
      )}
      {/* One audit-wide date - the last asset captured (latest AST scan / PV) -
          drives both the Location-on-date column and the purchase-date cut-off. */}
      {auditCutoff ? (
        <div className="missval">
          <Icon type="clock-circle" />
          <span>
            Audit date <b>{moment(auditCutoff.date).format('DD MMM YYYY')}</b> — the last asset captured in this audit
            {auditCutoff.source
              ? <> ({auditCutoff.source === 'PV' ? 'PV record' : 'AST scan'}{auditCutoff.ref ? <> <b>{auditCutoff.ref}</b></> : ''})</>
              : ''}. <b>Location on date</b> is as of this day.
            {' '}<b className="tnum">{postAuditAssets.length.toLocaleString('en-IN')}</b> asset{postAuditAssets.length === 1 ? '' : 's'} purchased
            after it {postAuditAssets.length === 1 ? 'is' : 'are'} left out — beyond this audit's purview (listed in Export).
            {(() => {
              const undated = assetQueue.filter(r => r.assetStatus === 'missing' && !r.invoiceDate).length
              return undated > 0
                ? <> <b className="tnum">{undated.toLocaleString('en-IN')}</b> missing asset{undated === 1 ? ' has' : 's have'} no purchase date and
                  stay{undated === 1 ? 's' : ''} listed — {undated === 1 ? 'it' : 'they'} cannot be tested against this date.</>
                : null
            })()}
          </span>
        </div>
      ) : (
        <div className="missval">
          <Icon type="exclamation-circle" />
          <span>No capture date on this voucher, so the purchase-date cut-off is not applied — every register asset is listed.</span>
        </div>
      )}

      {/* Where the decisions on Missing assets stand. Click to filter; again to clear. */}
      {(decisionCounts && Object.keys(decisionCounts).length > 0) && (
        <div className="chips chips-status dec-chips" role="group" aria-label="Decision on missing assets">
          <span className="dec-lbl">Missing — decision:</span>
          {DECISION_CHIPS.filter(c => decisionCounts[c.key]).map(c => (
            <button key={c.key} type="button" className="chip" aria-pressed={assetDecisionFilter === c.key}
              onClick={() => setAssetDecisionFilter(assetDecisionFilter === c.key ? null : c.key)}>
              <i className={`sdot dec-${c.key.replace(':', '-')}`} />
              {c.label} <span className="b tnum">{decisionCounts[c.key].toLocaleString('en-IN')}</span>
            </button>
          ))}
        </div>
      )}

      {/* Pick missing assets by value and age, then act on all of them at once. */}
      <div className="afilters" role="group" aria-label="Filter by invoice value and purchase date">
        <span className="af-field">
          <span className="af-lbl" id="af-val">Invoice value ₹</span>
          <InputNumber size="small" min={0} placeholder="from" aria-labelledby="af-val" value={valueMin}
            onChange={(v) => setValueMin(numOrNull(v))} />
          <span className="af-dash" aria-hidden="true">–</span>
          <InputNumber size="small" min={0} placeholder="to" aria-label="Invoice value to" value={valueMax}
            onChange={(v) => setValueMax(numOrNull(v))} />
        </span>
        <span className="af-field">
          <span className="af-lbl">Purchase date</span>
          <DatePicker.RangePicker size="small" format="DD MMM YYYY" value={buyRange || []}
            onChange={(v) => setBuyRange(v && v[0] && v[1] ? v : null)} />
        </span>
        {(valueFilterOn || dateFilterOn) && <button type="button" className="af-reset" onClick={resetAssetFilters}>Reset</button>}
        {(filterGaps.noValue > 0 || filterGaps.noDate > 0) && (
          <span className="af-gap">
            Not in this filter:{' '}
            {filterGaps.noValue > 0 && <><b className="tnum">{filterGaps.noValue.toLocaleString('en-IN')}</b> with no invoice value</>}
            {filterGaps.noValue > 0 && filterGaps.noDate > 0 && ' · '}
            {filterGaps.noDate > 0 && <><b className="tnum">{filterGaps.noDate.toLocaleString('en-IN')}</b> with no purchase date</>}
          </span>
        )}
      </div>

      {bulkTargets.length > 0 && (
        <div className="dock bulk cockpit-bulk abulk">
          <span className="seln">{bulkTargets.length.toLocaleString('en-IN')} missing</span>
          <span className="hint">
            ₹{bulkValue.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}{bulkValue.priced < bulkTargets.length ? ` (${(bulkTargets.length - bulkValue.priced).toLocaleString('en-IN')} unpriced)` : ''}
            {' '}· apply to all in this filter:
          </span>
          <Tooltip title={assetsGated ? `${pvOutstanding} PV record${pvOutstanding === 1 ? '' : 's'} still unprocessed — any could still be linked to one of these assets` : undefined}>
            <div className="acts">
              <button className="act" disabled={assetsGated} onClick={() => bulkAssetPick('SOLD')}>Mark sold</button>
              <button className="act" disabled={assetsGated} onClick={() => bulkAssetPick('MISSING')}>Mark missing</button>
              <button className="act danger" disabled={assetsGated} onClick={() => bulkAssetPick('IGNORE')}>Ignore</button>
              <button className="act" disabled={assetsGated || !bulkDecided} onClick={bulkAssetClear}
                title="Remove the decision from every missing asset in this filter">
                Clear decisions{bulkDecided ? ` (${bulkDecided.toLocaleString('en-IN')})` : ''}
              </button>
            </div>
          </Tooltip>
        </div>
      )}
      <div className="rtwrap">
        <table className="rt assets">
          <thead>
            <tr>
              <th className="c-sel" />
              <th className="c-ph">Photo</th>
              <th className="c-asset">Asset</th>
              <th className="c-eq">Equipment</th>
              <th className="c-loc">Current location</th>
              <th className="c-loc">Location on date</th>
              {sortHeader('invoiceDate', 'Purchase date', 'c-buy')}
              {sortHeader('invoiceValue', 'Invoice value', 'c-val')}
              <th className="c-seen">Last located</th>
              <th className="c-as-st">Status in this audit</th>
              <th className="c-act">Action</th>
            </tr>
          </thead>
          <tbody>{assetSorted.slice(0, LIST_CAP).map(renderAssetRow)}</tbody>
        </table>
        {assetFiltered.length > LIST_CAP &&
          <div className="rt-note">Showing first {LIST_CAP} of {assetFiltered.length}. Refine filters to narrow down.</div>}
        {!assetFiltered.length && <div className="rt-note">Nothing matches these filters.</div>}
      </div>
    </>
  )

  // cmpRecord is only an identity handle: re-read the live row so an action taken
  // from inside the modal is reflected there immediately.
  const cmpLive = cmpRecord ? (queue.find(x => x._uid === cmpRecord._uid) || cmpRecord) : null

  const renderTable = () => (
    <div className="rtwrap">
      <table className="rt">
        <thead>
          <tr>
            <th className="c-sel" />
            <th className="c-ph">Photo</th>
            <th className="c-asset">Asset</th>
            <th className="c-eq">Equipment</th>
            <th className="c-loc scan">Location found in audit</th>
            <th className="c-buy">AST date</th>
            <th className="c-loc">Register (on date)</th>
            <th className="c-loc c-reg">Register (current)</th>
            <th className="c-st">Status</th>
            <th className="c-act">Action</th>
            <th className="c-state">State</th>
          </tr>
        </thead>
        <tbody>{filtered.slice(0, LIST_CAP).map(renderTableRow)}</tbody>
      </table>
      {filtered.length > LIST_CAP &&
        <div className="rt-note">Showing first {LIST_CAP} of {filtered.length}. Refine filters to narrow down.</div>}
      {!filtered.length && <div className="rt-note">Nothing matches these filters.</div>}
    </div>
  )

  const renderFocus = () => {
    if (!current) {
      const remaining = queue.filter(r => !isDecided(r)).length
      const msg = isAssets ? 'Pick an asset to view' : (remaining ? 'Pick a record to review' : 'Every record has a decision')
      return (
        <div className="empty">
          <Icon type={isAssets ? 'database' : (remaining ? 'select' : 'check-circle')} style={{ fontSize: 38, color: isAssets || remaining ? 'var(--faint)' : 'var(--match)' }} />
          <div className="big">{msg}</div>
          <div>{isAssets ? 'Assets are view-only — choose one from the list to see its details.' : (remaining ? 'Choose an item from the queue, or press J to jump to the first.' : 'Save, then Process to commit them.')}</div>
        </div>
      )
    }
    if (current.source === 'ASSET') return renderAssetFocus(current)
    const r = current
    const meta = statusMeta(r)
    const done = isDecided(r)
    const rec = recommended(r)
    const failed = !!(r.processedRemark && r.processedRemark !== 'Processing...' && !r.isProcessed)
    const actions = r.source === 'PV' ? PV_ACTIONS : astActionsFor(r)

    return (
      <>
        <div className="focus-inner">
          <div className="rec-head">
            <div>
              <h3>{codeOf(r)}</h3>
              <div className="meta">
                <b>{nameOf(r)}</b> · {r.source === 'PV' ? 'Pre-verification ' + (r.pvNo || '') : 'Stock-taking ' + (r.astNo || '')}
                {r.source === 'AST' && <> · <a onClick={() => setAstView(r)}>view detail</a></>}
              </div>
            </div>
            <div className="status-block">
              <span className={'pill ' + (done ? 'done' : meta.cls)}>{done ? 'Decided · ' + ACT[r.actionTaken.name].label : meta.label}</span>
              {r.isProcessed && <span className="pill proc" style={{ marginLeft: 6 }}><Icon type="check-circle" theme="filled" /> Processed</span>}
              {failed && <span className="pill err" style={{ marginLeft: 6 }}><Icon type="exclamation-circle" theme="filled" /> Failed</span>}
              <div className="why">{meta.why}</div>
            </div>
          </div>

          <div className={'insight' + (failed ? ' insight-err' : '')}>
            <Icon type={failed ? 'exclamation-circle' : 'bulb'} style={{ color: failed ? 'var(--crit)' : 'var(--accent)', marginTop: 2 }} />
            <div>
              {!failed && meta.why + ' '}
              {/* Only suggest an action that is actually on offer for this row -
                  a register-sold asset, for instance, has no SOLD button to press. */}
              {!done && rec && actions.includes(rec) && <>Suggested: <span className="rec-act" style={{ color: 'var(--accent)' }}>{ACT[rec].label}</span>.</>}
              {done && !r.isProcessed && !failed && <>Currently set to <b>{ACT[r.actionTaken.name].label}</b>{r.actionTaken.refRecordSubId ? ` → ${r.actionTaken.refRecordSubId}` : ''}. Pick another to change it, then Process.</>}
              {failed && (
                <><b style={{ color: 'var(--crit)' }}>Processing failed:</b> <span style={{ color: 'var(--crit)' }}>{r.processedRemark}</span>. Fix the cause and Process again.</>
              )}
              {r.isProcessed && (
                <>
                  <b>Processed{r.processedOn ? ' on ' + r.processedOn : ''}</b>{r.actionTaken ? <> as <b>{ACT[r.actionTaken.name] ? ACT[r.actionTaken.name].label : r.actionTaken.name}</b></> : ''} — result: <span className="rec-act" style={{ color: 'var(--accent)' }}>{r.processedRemark || '—'}</span>.
                  {r.processedRemark === 'Processing...' && <> Running in the background; hit <b>Generate</b> again to see the final result.</>}
                </>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-title"><Icon type="swap" /> Scanned vs master register</div>
            <div className="cmp">
              <div className="ch">Field</div>
              <div className="ch scan">Scanned in audit</div>
              <div className="ch">Master register</div>
              {renderCompare(r)}
            </div>
          </div>

          {r.source === 'PV' && (() => {
            const { groups, total } = pvCandidates(r)
            return (
              <div className="panel">
                <div className="panel-title"><Icon type="database" /> Master register · assets you can link ({total})</div>
                {groups.length
                  ? <div className="link-groups">
                    {groups.map(g => (
                      <div className="lg-row clickable" key={g.name}
                        onClick={() => { if (!r.isProcessed) { setLinkRecord(r); setLinkTab(g.key); setShowLink(true) } }}>
                        <span className="lg-name">{g.name}</span>
                        <span className="lg-count">{g.count}</span>
                      </div>
                    ))}
                  </div>
                  : <div className="pc-none" style={{ padding: '12px 14px' }}>No matching assets in the register to link.</div>}
                {total > 0 && <div className="lg-hint">Choose <b>Link to asset</b> below to pick one — the picker groups them the same way.</div>}
              </div>
            )
          })()}

          <div className="panel">
            <div className="panel-title"><Icon type="camera" /> Photos</div>
            <div className="photo-cmp">
              <div className="pc-col">
                <div className="pc-head scan">Scanned in audit</div>
                <div className="photos">
                  {r.photos && r.photos.length > 0
                    ? <PhotoThumb photos={r.photos} onClick={() => { setGalleryRecord(r); setShowGallery(true) }} />
                    : <span className="pc-none">no photos</span>}
                </div>
              </div>
              <div className="pc-col">
                <div className="pc-head">Master register</div>
                <div className="photos">
                  {r.matchedAsset && r.matchedAsset._id
                    ? <LatestImage id={r.matchedAsset._id} />
                    : <span className="pc-none">{r.source === 'PV' ? 'no master record' : 'not in register'}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Per-record action dock — lives inside the record detail, acts on the
            focused record only. The bulk bar is separate (cockpit footer). */}
        <div className="dock">
          <span className="hint">{r.isProcessed ? 'Already processed — action is final:' : done ? 'Change action for this record:' : 'Set action for this record:'}</span>
          <div className="acts">
            {actions.map((name, i) => (
              <button
                key={name}
                className={'act ' + (ACT[name].kind || '') + (name === rec && !done ? ' recommended' : '') + (r.isProcessed ? ' disabled' : '')}
                disabled={r.isProcessed}
                onClick={() => pickAction(r, name)}
              >
                {ACT[name].label} <span className="key">{i + 1}</span>
              </button>
            ))}
            {done && !r.isProcessed && <button className="act" onClick={() => clearAction(r)}>Clear</button>}
          </div>
          <div className="spacer" />
          {!r.isProcessed && <div className="legend"><span><kbd>J</kbd><kbd>K</kbd> move</span><span><kbd>1</kbd>–<kbd>5</kbd> act</span></div>}
        </div>
      </>
    )
  }

  return (
    <div className="rcx">
      <Spin spinning={!!props.loading}>
        <div className="cockpit">
          {/* ---- top bar: title + grouped filter form ---- */}
          <div className="topbar">
            <div className="tb-title">
              <span className="glyph"><Icon type="audit" /></span>
              <div>
                <h3>Reconciliation Cockpit</h3>
                <div className="sub">Physical audit → master register</div>
              </div>
            </div>

            <div className="filterform" role="search" aria-label="Load an audit voucher">
              {/* the label wraps the box, so it names the input for screen readers */}
              <label className="ff-voucher">
                <span className="ff-label">Audit voucher</span>
                <AuditVoucherSearch styleName="gx-lt-icon-search-bar-lg" placeholder="Type 3+ characters…"
                  allowClear onClear={handleVoucherClear}
                  onSelect={selectVoucher} authUser={props.authUser} />
              </label>
              <Button className="ff-go" type="primary" icon="thunderbolt" loading={props.loading} disabled={!voucher} onClick={fetchAssets}>Generate</Button>
              {voucher && (
                <div className="ff-scope" aria-live="polite">
                  {scopeChips.length
                    ? <span>Audit scope from the voucher: {scopeChips.map(c => <span key={c.key} className="ff-scope-chip">{c.label}: <b>{c.name}</b></span>)}</span>
                    : <span>Loads the whole voucher. Narrow the rows with the table filters.</span>}
                </div>
              )}
            </div>
          </div>

          {/* ---- results toolbar (progress + actions), separate from the filter form ---- */}
          {hasData && (
            <div className="resultbar">
              <div className="ring" title={`${pct}% decided`}>
                <svg width="44" height="44" viewBox="0 0 46 46">
                  <circle className="track" cx="23" cy="23" r="19" fill="none" strokeWidth="5" />
                  <circle className="bar" cx="23" cy="23" r="19" fill="none" strokeWidth="5" strokeDasharray={ringC} strokeDashoffset={ringC * (1 - (total ? decided / total : 0))} />
                </svg>
                <span className="pct">{pct}%</span>
              </div>
              <div className="prog-meta"><b className="tnum">{decided}/{total}</b><span>decided</span></div>
              <div className="kpis" role="group" aria-label="Filter the queue by stage">
                <button type="button" className="kpi rest" aria-pressed={statusFilter === 'review'}
                  title="Show only records with no decision yet" onClick={() => toggleStage('review')}>
                  <div className="n tnum">{toReview}</div>
                  <div className="k">To review</div>
                  <div className="ksub">
                    <span title="AST records (scanned against the master register) with no decision yet.">
                      <b className="tnum">{toReviewAst}</b> AST
                    </span>
                    <span className="sep">·</span>
                    <span title="PV records (physical items found with no asset code) with no decision yet.">
                      <b className="tnum">{toReviewPv}</b> PV
                    </span>
                  </div>
                </button>
                <button type="button" className="kpi rev" aria-pressed={statusFilter === 'reviewed'}
                  title="Show only records with a decision that has not been processed yet"
                  onClick={() => toggleStage('reviewed')}>
                  <div className="n tnum">{reviewed}</div>
                  <div className="k">Reviewed</div>
                  <div className="ksub">
                    <span title="A decision is set on the record but has not been saved to the server yet.">
                      <b className="tnum">{reviewedPending}</b> pending save
                    </span>
                    <span className="sep">·</span>
                    <span title="Saved to the server, but not processed yet.">
                      <b className="tnum">{reviewedSaved}</b> saved
                    </span>
                  </div>
                </button>
                {(processing > 0 || statusFilter === 'processing') && (
                  <button type="button" className="kpi run" aria-pressed={statusFilter === 'processing'}
                    title="Submitted to the server and still running. These are out of your hands until they land."
                    onClick={() => toggleStage('processing')}>
                    <div className="n tnum"><Icon type="sync" spin /> {processing}</div>
                    <div className="k">Processing</div>
                    <div className="ksub">
                      <span title="AST records the server is still working through."><b className="tnum">{processingAst}</b> AST</span>
                      <span className="sep">·</span>
                      <span title="PV records the server is still working through."><b className="tnum">{processingPv}</b> PV</span>
                    </div>
                  </button>
                )}
                {(failedN > 0 || statusFilter === 'failed') && (
                  <button type="button" className="kpi fail" aria-pressed={statusFilter === 'failed'}
                    title="Processing came back with an error. Each record's Remark carries the server's reason."
                    onClick={() => toggleStage('failed')}>
                    <div className="n tnum">{failedN}</div>
                    <div className="k">Failed</div>
                    <div className="ksub">
                      <span title="AST records whose processing failed."><b className="tnum">{failedAst}</b> AST</span>
                      <span className="sep">·</span>
                      <span title="PV records whose processing failed."><b className="tnum">{failedPv}</b> PV</span>
                    </div>
                  </button>
                )}
                <button type="button" className="kpi done" aria-pressed={statusFilter === 'processed'}
                  title="Show only records whose action has already been executed"
                  onClick={() => toggleStage('processed')}>
                  <div className="n tnum">{processed}</div>
                  <div className="k">Processed</div>
                  <div className="ksub">
                    <span title="AST records whose action has been executed against the master register.">
                      <b className="tnum">{processedAst}</b> AST
                    </span>
                    <span className="sep">·</span>
                    <span title="PV records whose action has been executed - linked to an asset, or a new asset created.">
                      <b className="tnum">{processedPv}</b> PV
                    </span>
                  </div>
                </button>
              </div>
              <div className="rb-actions">
                <Tooltip title="Set RECONCILE on every fully-matched AST record">
                  <Button icon="thunderbolt" onClick={autoReconcile} disabled={!autoN}>Auto-match ({autoN})</Button>
                </Tooltip>
                <Tooltip title="Duplicate scans: the scan closest to the register place keeps the code (Ignore or Transfer); the others become new assets. Shows a preview first.">
                  <Button icon="branches" onClick={openDupAuto}
                    disabled={!dupAuto.groups.length && !dupAuto.skipped.length}>
                    Auto-resolve duplicates ({dupAuto.groups.length})
                  </Button>
                </Tooltip>
                <Tooltip title="Exports AST, PV, register assets and the audit scope — all tabs, whichever one is open">
                  <Button icon="download" onClick={exportData}>Export</Button>
                </Tooltip>
                <Tooltip title="Management PDF: what the audit covered, how long it took, and what was found, recovered and missing">
                  <Button icon="file-pdf" onClick={downloadReport}>Report</Button>
                </Tooltip>
                <Button type="primary" icon="save" loading={props.loading} onClick={save}>Save</Button>
                <Button type="danger" icon="play-circle" loading={props.loading || runActive} onClick={process}>Process</Button>
              </div>
            </div>
          )}

          {/* Standing, non-blocking reminder while decisions are unsaved. Deliberately
              a banner rather than a navigation block: it stays visible the whole
              time without ever interrupting an action. */}
          {hasData && hasUnsaved && (
            <div className="unsaved" role="status">
              <Icon type="exclamation-circle" theme="filled" />
              <span>
                <b className="tnum">{unsavedTotal}</b> decision{unsavedTotal === 1 ? '' : 's'} not saved yet
                {assetUnsaved > 0 && <> ({assetUnsaved} on the Assets tab)</>}
                {' '}— {unsavedTotal === 1 ? 'it' : 'they'} will be lost if you leave this page or re-run Generate.
              </span>
              <Button size="small" type="primary" icon="save" loading={props.loading} onClick={save}>Save now</Button>
            </div>
          )}

          {/* Background processing: the server works asynchronously, so this is the
              only thing that tells the user it finished. Rechecking re-runs the
              reconcile call - the API offers nothing cheaper - so it is paused
              whenever there are unsaved decisions, which that call would discard. */}
          {hasData && batchStats && (
            <div className={'procbar' + (batchStats.running ? '' : ' done')} role="status">
              <Icon type={batchStats.running ? 'sync' : (batchStats.failed ? 'exclamation-circle' : 'check-circle')}
                spin={!!batchStats.running} theme={batchStats.running ? undefined : 'filled'} />
              <span>
                {batchStats.running > 0 ? (
                  <>
                    <b className="tnum">{batchStats.running}</b> of <b className="tnum">{batchStats.total}</b> still
                    processing on the server
                    {batchStats.done > 0 && <> · <b className="tnum">{batchStats.done}</b> done</>}
                    {batchStats.failed > 0 && <> · <b className="tnum">{batchStats.failed}</b> failed</>}
                  </>
                ) : (
                  <>
                    Processing finished — <b className="tnum">{batchStats.done}</b> processed
                    {batchStats.failed > 0 && <>, <b className="tnum">{batchStats.failed}</b> failed</>}
                    {batchStats.unaccounted > 0 && <>, <b className="tnum">{batchStats.unaccounted}</b> not visible in the current filter</>}
                  </>
                )}
                {runActive && (
                  <span className="pb-when">
                    sending part <b className="tnum">{Math.min(run.sent + 1, run.parts)}</b> of <b className="tnum">{run.parts}</b>
                    {run.current && <> ({run.current})</>}
                  </span>
                )}
                {lastChecked && <span className="pb-when">checked {moment(lastChecked).fromNow()}</span>}
              </span>
              {batchStats.running > 0 && hasUnsaved && (
                <span className="pb-warn">auto-check paused — saving first would avoid losing your unsaved decisions</span>
              )}
              {batchStats.running > 0 && (
                <Button size="small" icon="reload" loading={checking}
                  onClick={() => { setChecking(true); fetchAssets() }}>Check now</Button>
              )}
              <Button size="small" type="link" disabled={runActive} onClick={() => { setBatch(null); setRun(null) }}>Dismiss</Button>
            </div>
          )}
          {hasData && run && run.failures.length > 0 && (
            <div className="procfail" role="alert">
              <Icon type="exclamation-circle" theme="filled" />
              <div>
                <b className="tnum">{run.failures.length}</b> of <b className="tnum">{run.parts}</b> part{run.parts === 1 ? '' : 's'} could
                not be confirmed. Their records stay Processing until the re-check shows what the server did — do not process them again before that.
                <ul>
                  {run.failures.map((f, i) => <li key={i}><b>{f.label}</b> — {f.error}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* ---- workspace ---- */}
          {hasData ? (
            <>
              {/* Header chrome sits ABOVE the view so the tabs, search, status chips
                  and selection bar read the same whether the AST table or the
                  PV/Assets list is below them. */}
              <div className="qbar">
                <div className="queue-head">
                  <div className="row1">
                    <h4>Decision queue</h4>
                    <span className="count">{filtered.length} shown</span>
                  </div>
                  <div className="seg" role="group" aria-label="Source">
                    {[['AST', 'AST'], ['PV', 'PV'], ['ASSETS', 'Assets']].map(([s, label]) => (
                      <button key={s} aria-pressed={sourceFilter === s} onClick={() => { setSourceFilter(s); setCurrentKey(null); setRecStatusFilter(null); setBestMatchFilter(null) }}>{label}</button>
                    ))}
                  </div>
                  <Input allowClear size="small" style={{ marginTop: 9 }} prefix={<Icon type="search" style={{ color: 'var(--faint)' }} />}
                    placeholder="Search code / model / location…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                {/* Assets tab: status breakdown + the audit-scope control. */}
                {isAssets && (
                  <div className="chips chips-status" role="group" aria-label="Asset status">
                    <button className="chip" aria-pressed={!assetStatusFilter}
                      onClick={() => setAssetStatusFilter(null)}>
                      All <span className="b tnum">{assetQueue.length}</span>
                    </button>
                    {assetBreakdown.map(({ key, count }) => (
                      <button key={key} className="chip" aria-pressed={assetStatusFilter === key}
                        onClick={() => setAssetStatusFilter(assetStatusFilter === key ? null : key)}>
                        <i className={`sdot as-${key}`} />
                        {ASSET_STATUS[key].label} <span className="b tnum">{count}</span>
                      </button>
                    ))}
                    <span className="scopechip">
                      <Icon type="environment" />
                      {scopeSet
                        ? <>Scope:{' '}
                          {excludedLocs.size > 0 && <><b className="tnum">{excludedLocs.size}</b> location{excludedLocs.size === 1 ? '' : 's'}</>}
                          {excludedLocs.size > 0 && excludedSubs.size > 0 && ' + '}
                          {excludedSubs.size > 0 && <><b className="tnum">{excludedSubs.size}</b> sub-location{excludedSubs.size === 1 ? '' : 's'}</>}
                          {' '}excluded</>
                        : <>Scope: whole organisation</>}
                      <a onClick={() => setScopeOpen(true)}>edit</a>
                    </span>
                  </div>
                )}
                {/* AST "current status" breakdown. Second-level filter: combines with
                      the stage selected in the top counters (AND), so the counts always
                      match what the list shows for that stage. */}
                {astBreakdown.length > 0 && (
                  <div className="chips chips-status" role="group" aria-label="Current status breakdown">
                    <button className="chip" aria-pressed={!recStatusFilter}
                      onClick={() => setRecStatusFilter(null)}>
                      All statuses <span className="b tnum">{astBreakdown.reduce((n, x) => n + x.count, 0)}</span>
                    </button>
                    {astBreakdown.map(({ key, count }) => {
                      const meta = metaForGroup(key)
                      const label = meta ? meta.label : (key === NO_STATUS ? 'No status' : key)
                      const why = meta ? meta.why
                        : (key === NO_STATUS ? 'These AST records came back with no reconcile status.' : key)
                      return (
                        <Tooltip key={key} title={why}>
                          <button className="chip" aria-pressed={recStatusFilter === key}
                            onClick={() => setRecStatusFilter(recStatusFilter === key ? null : key)}>
                            <i className={`sdot ${meta ? meta.cls : 'none'}`} />
                            {label} <span className="b tnum">{count}</span>
                          </button>
                        </Tooltip>
                      )
                    })}
                  </div>
                )}
                {/* PV "Best match" breakdown. Same second-level filter as the AST
                      breakdown above: combines with the selected stage (AND). */}
                {pvBreakdown.length > 0 && (
                  <div className="chips chips-status" role="group" aria-label="Best match breakdown">
                    <button className="chip" aria-pressed={!bestMatchFilter}
                      onClick={() => setBestMatchFilter(null)}>
                      All best matches <span className="b tnum">{pvBreakdown.reduce((n, x) => n + x.count, 0)}</span>
                    </button>
                    {pvBreakdown.map(({ key, count }) => {
                      const meta = bestMatchMeta(key)
                      return (
                        <Tooltip key={key} title={meta.why}>
                          <button className="chip" aria-pressed={bestMatchFilter === key}
                            onClick={() => setBestMatchFilter(bestMatchFilter === key ? null : key)}>
                            {meta.label} <span className="b tnum">{count}</span>
                          </button>
                        </Tooltip>
                      )
                    })}
                  </div>
                )}
                {sourceFilter === 'PV' && (
                  <AiLinkPanel run={aiRun}
                    eligible={filtered.filter(r => r.source === 'PV' && !r.isProcessed && !isDecided(r)).length}
                    shown={filtered.filter(r => r.source === 'PV').length}
                    threshold={JEV_MATCH_THRESHOLD} cap={JEV_CALL_CAP}
                    ownerOptions={ownerOptions(props.filters)} lastOwner={lastNewAssetOwner()}
                    onStart={(defaults) => runAiLink(filtered.filter(r => r.source === 'PV' && !r.isProcessed && !isDecided(r)).map(pdKeyOf), defaults)}
                    onStop={stopAiLink} onClose={() => setAiRun(null)}
                    backlog={backlog} onBacklog={runBacklog} onBacklogStop={stopBacklog} onBacklogClose={() => setBacklog(null)} />
                )}
                {!isAssets && (
                  <div className="selbar">
                    <a onClick={selectAllFiltered}>Select all shown</a>
                    {selected.size > 0 && <><span>·</span><a onClick={clearSel}>Clear ({selected.size})</a></>}
                  </div>
                )}
              </div>

              {/* Bulk bar — driven purely by checkbox selection. It sits ABOVE the
                  view, next to the "Select all shown" link that fills the
                  selection: below a 400-row table it was off-screen the moment it
                  appeared. Sticky so it survives scrolling the page. */}
              {selected.size > 0 && (
                <div className="dock bulk cockpit-bulk">
                  <span className="seln">{selectedRecords.length} selected</span>
                  <span className="hint">apply to all selected:</span>
                  <div className="acts">
                    {bulkOptions.length
                      ? bulkOptions.map(name => (
                        <button key={name} className={'act ' + (ACT[name].kind || '')} onClick={() => bulkPick(name)}>{ACT[name].label}</button>
                      ))
                      : <span className="hint">No common bulk action for this mix (Link to asset is per-record).</span>}
                    <button className="act" onClick={bulkClear} disabled={!clearableN}
                      title="Remove the decision from the selected records, sending them back to To review">
                      Clear decision{clearableN === 1 ? '' : 's'}{clearableN ? ` (${clearableN})` : ''}
                    </button>
                  </div>
                  {/* No "Clear selection" here - the queue's selbar already has
                      "Clear (N)" next to "Select all shown". */}
                </div>
              )}

              {isTable ? (sourceFilter === 'ASSETS' ? renderAssetsTable() : sourceFilter === 'PV' ? renderPvTable() : renderTable()) : (
                <div className="workspace">
                  <aside className="queue">
                    <div className="list">
                      {filtered.slice(0, LIST_CAP).map(renderQueueItem)}
                      {filtered.length > LIST_CAP && <div className="empty" style={{ padding: 16 }}>Showing first {LIST_CAP} of {filtered.length}. Refine filters to narrow down.</div>}
                      {!filtered.length && <div className="empty" style={{ padding: 24 }}>Nothing matches these filters.</div>}
                    </div>
                  </aside>

                  <section className="focus">{renderFocus()}</section>
                </div>
              )}

            </>
          ) : (
            <div className="empty">
              <Icon type="inbox" style={{ fontSize: 40, color: 'var(--faint)' }} />
              <div className="big">Load an audit voucher</div>
              <div>Pick a voucher above and hit Generate. The whole voucher is loaded.</div>
            </div>
          )}
        </div>
      </Spin>

      {/* PV link picker: the PV's own photo stays on screen beside the candidates,
          which is the comparison the decision actually rests on. */}
      {showLink && linkRecord && (
        <PvLinkPicker
          visible={showLink}
          record={linkRecord}
          candidates={linkCands}
          jevPanel={linkSearch && linkRecord && linkRecord.source === 'PV' ? (
            <JevCheck search={linkSearch} threshold={JEV_MATCH_THRESHOLD}
              checkOf={(a) => jevChecks[jevKeyOf(linkRecord, a)]}
              onStop={() => jevSearchStop(linkRecord)}
              available={new Set(linkOrder.map(o => String(o.asset._id)))}
              onContinue={() => jevSearchRun(linkRecord, linkOrder, 0)}
              onRestart={() => jevSearchRestart(linkRecord, linkOrder)} />
          ) : null}
          jevDocs={(() => {
            const out = {}
            if (linkRecord) linkOrder.forEach(o => {
              const c = jevChecks[jevKeyOf(linkRecord, o.asset)]
              if (c && c.doc && c.doc.status === 'OK' && typeof c.doc.sameProbability === 'number') out[String(o.asset._id)] = c.doc
            })
            return out
          })()}
          jevMatch={linkSearch && linkSearch.status === 'found' && linkSearch.order[linkSearch.pos]
            ? { assetId: linkSearch.matchId, groupKey: linkSearch.order[linkSearch.pos].groupKey } : null}
          locName={(id) => locName(id, locations)}
          clientName={(id) => clientName(id, clients)}
          loading={props.loading}
          onLink={(asset, willOverwrite) => { handleRecordAction(asset, willOverwrite, 'LINK_ASSET'); hideLink() }}
          onNewAsset={() => {
            const r = linkRecord
            hideLink()
            setPvAction(r._uid, { name: 'NEW_ASSET' })
            setDetailsFor([r])
          }}
          onIgnore={() => { const r = linkRecord; hideLink(); setPvAction(r._uid, { name: 'IGNORE' }) }}
          onClose={hideLink}
        />
      )}

      {detailsFor && (
        <NewAssetDetails
          visible
          records={detailsFor}
          details={assetDetails}
          ownerOptions={ownerOptions(props.filters)}
          cacheKey={mastersCacheKey}
          onApply={applyDetails}
          onClose={() => setDetailsFor(null)}
        />
      )}

      {dupPlan && (
        <DuplicateAutoModal plan={dupPlan} onApply={applyDupPlan} onCancel={() => setDupPlan(null)}
          place={placeLine}
          regPlace={(r) => [locName(r.registerLocationOnDate, locations), clientName(r.registerClientOnDate, clients)].filter(Boolean).join(' / ')} />
      )}

      {/* Audit scope. Declared, never inferred - coverage is evidence, not a rule. */}
      <Modal visible={scopeOpen} onCancel={() => setScopeOpen(false)} width={980} centered
        title="Which locations did this audit cover?" className="rcx-scope-modal"
        footer={[
          <Button key="all" onClick={() => { setExcludedLocs(new Set()); setExcludedSubs(new Set()) }}>
            Include everything
          </Button>,
          <Button key="ok" type="primary" onClick={() => setScopeOpen(false)}>Done</Button>
        ]}>
        <div className="rcx">
          <div className="scope-note">
            An asset outside the scope is <b>not missing</b> — the audit never looked for it. Coverage is
            shown as <b>evidence, not a rule</b>: a scan proves someone was there, not that the location was
            worked through, so the choice is yours.
            <div className="scope-warn">
              <Icon type="exclamation-circle" /> Scope is not saved with the voucher yet — it resets when this
              page reloads. Persisting it needs a backend change.
            </div>
          </div>
          <div className="scope-list">
            <div className="scope-row head">
              <span /><span>Location</span><span>Accounted</span><span>Coverage</span>
            </div>
            {locationCoverage.map(l => {
              const off = excludedLocs.has(l.loc)
              const open = scopeExpanded.has(l.loc)
              // A location's own excluded sub-locations. Excluding the whole
              // location covers all of them, so the sub rows go quiet.
              const offSubs = l.subs.filter(sv => excludedSubs.has(`${l.loc}|${sv.client}`)).length
              return (
                <React.Fragment key={l.loc}>
                  <div className={'scope-row' + (off ? ' off' : '')}>
                    <Checkbox checked={!off} onChange={() => setExcludedLocs(prev => {
                      const n = new Set(prev); n.has(l.loc) ? n.delete(l.loc) : n.add(l.loc); return n
                    })} />
                    <span className="scope-name">
                      <button className="scope-exp" onClick={() => setScopeExpanded(prev => {
                        const n = new Set(prev); n.has(l.loc) ? n.delete(l.loc) : n.add(l.loc); return n
                      })}>
                        <Icon type={open ? 'down' : 'right'} />
                      </button>
                      {l.name}
                      <span className="scope-subn">
                        {l.subs.length} sub-location{l.subs.length === 1 ? '' : 's'}
                        {!off && offSubs > 0 && <b> · {offSubs} excluded</b>}
                      </span>
                    </span>
                    <span className="scope-num mono">{l.accounted} / {l.total}</span>
                    <span className="scope-bar">
                      <i className={l.pct === 0 ? 'zero' : l.pct < 60 ? 'low' : ''}
                        style={{ width: `${Math.max(l.pct, 2)}%` }} />
                      <em className="mono">{l.pct}%</em>
                    </span>
                  </div>
                  {open && l.subs.map(sv => {
                    const key = `${l.loc}|${sv.client}`
                    const subOff = off || excludedSubs.has(key)
                    return (
                      <div key={key} className={'scope-row sub' + (subOff ? ' off' : '')}>
                        <Checkbox checked={!subOff} disabled={off}
                          onChange={() => setExcludedSubs(prev => {
                            const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n
                          })} />
                        <span className="scope-name">{sv.name}</span>
                        <span className="scope-num mono">{sv.accounted} / {sv.total}</span>
                        <span className="scope-bar">
                          <i className={sv.pct === 0 ? 'zero' : sv.pct < 60 ? 'low' : ''}
                            style={{ width: `${Math.max(sv.pct, 2)}%` }} />
                          <em className="mono">{sv.pct}%</em>
                        </span>
                      </div>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </Modal>

      {astView && <AstRecord visible={!!astView} onCancel={() => setAstView(null)} data={astView} locations={locations} clients={clients} />}

      {/* Photo history comparison: this audit's photo against the asset's earlier
          ones. The record's decision stays available in the footer so opening the
          evidence is not a detour back into a drill-in. */}
      <PhotoCompare
        visible={!!cmpLive}
        record={cmpLive}
        onClose={() => setCmpRecord(null)}
        locName={(id) => locName(id, locations)}
        clientName={(id) => clientName(id, clients)}
        actions={cmpLive && cmpLive.source === 'AST' && !cmpLive.isProcessed
          ? <><span className="pc-lbl">Set action for this record</span>{rowActions(cmpLive)}</>
          : null}
      />

      <PhotoGalleryModal visible={showGallery} onCancel={() => setShowGallery(false)}
        photos={galleryRecord && galleryRecord.photos}
        title={galleryRecord && (codeOf(galleryRecord) || galleryRecord.astNo || galleryRecord.pvNo)} />
    </div>
  )
}

const mapStateToProps = ({ reconciliation, auth, commonData, asset, preVerification }) => ({
  reconciliationAssets: reconciliation.reconciliationAssets,
  reconciliationSavedAstAssets: reconciliation.reconciliationSavedAstAssets,
  reconciliationSavedPvAssets: reconciliation.reconciliationSavedPvAssets,
  reconciliationProcessedAstAssets: reconciliation.reconciliationProcessedAstAssets,
  reconciliationProcessedPvAssets: reconciliation.reconciliationProcessedPvAssets,
  reconciliationSavedAssetAssets: reconciliation.reconciliationSavedAssetAssets,
  reconciliationProcessedAssetAssets: reconciliation.reconciliationProcessedAssetAssets,
  authUser: auth.authUser,
  filters: commonData.filters,
  loading: commonData.loading,
  // The save/process thunks report failures only here.
  commonError: commonData.error,
  pvRecordByVoucher: preVerification.pvRecordByVoucher,
  assetsForReconciliation: asset.assetsForReconciliation
})

const mapDispatchToProps = {
  getAssetsByAuditVoucher, fetchFilter, getAssetsForReconciliation, getPvByVoucher,
  saveReconciliationData, processReconciliationData
}

export default connect(mapStateToProps, mapDispatchToProps)(ReconciliationCockpit)
